import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    forwardRef,
} from "react";
import {
    ReactFlow,
    Background,
    Controls,
    Handle,
    Position,
    Panel,
    useReactFlow,
    useStore,
    applyNodeChanges,
    applyEdgeChanges,
    BaseEdge,
    getSmoothStepPath,
    type Node,
    type Edge,
    type Connection,
    type NodeChange,
    type EdgeChange,
    type NodeProps,
    type EdgeProps,
    type ReactFlowInstance,
    MarkerType,
} from "@xyflow/react";
import Dagre from "@dagrejs/dagre";
import "@xyflow/react/dist/style.css";
import "./IVRSchemeBuilder.scss";
import "./RouterMenu/router-menu.css";
import { IconBadge } from "./RouterMenu/IconBadge";
import type { RouterMenuItem } from "./RouterMenu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import type { AppDispatch } from "../../../../redux/store";
import { showError } from "../../../../redux/actions/actions";
import { checkPermissions } from "../../../../redux/permissions/permissionsUtils";
import { setIvrSchemeGraphApiSlice } from "../../../../redux/actions/actionsIVRConfiguration";
import {
    IVR_MENU_DTMF_KEY_ORDER,
    IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT,
    IVR_TREE_PARENT_TO_CHILD_GAP,
    IVR_SCHEDULER_SIBLING_GAP_PX,
    ivrSchedulerSlotRowY,
    ivrSchedulerDaySlotRowLayout,
    ivrSchedulerAdvancedColumnWidth,
    IVR_START_ABOVE_ENTRY_GAP,
    IVR_MENU_DTMF_GAP_PX,
    IVR_PLAY_CHAIN_VERTICAL_GAP,
    IVR_MENU_DTMF_VERTICAL_GAP,
    IVR_TIDY_SUBTREE_GAP_PX,
    ivrAgentHunterQueueChildY,
    ivrMenuRetriesBranchLayout,
} from "./ivrSchemeConstants";
import {
    deleteIvrConfigElement,
    deleteIvrConfigRoute,
    deleteIvrConfigSchedule,
    getIVRConfigurationFullById,
    ivrConfigMutationRequest,
    postIvrConfigRoute,
    postIvrConfigSchedule,
    putIvrConfigConfiguration,
    putIvrConfigElement,
    putIvrConfigSchedule,
} from "../../../../redux/api/apiIVRConfiguration";
import {
    findInactiveSchedulerSlotMatchingPostCandidate,
    nextIvrRoutePriority,
    nextIvrSchedulePostPriorityFromSources,
    serializeIvrGraphToApiParts,
    slotRowsToSchedules,
} from "./ivrSchemeSerialization";
import {
    filterIvrSchedulerSlotRowsForScheduleApi,
    normalizeIvrSchedulerScheduleKind,
} from "./ivrElementDraftData";
import { blockConfigToCreateElementPayload } from "./ivrConfigElementPayload";
import {
    augmentSchedulerApiElementWithClientBlockConfig,
    buildHydratedGraphFromApiPayload,
    buildSchedulerSlotRowsFromBlockConfig,
    collectIvrSchedulerOutboundLinkSourceIds,
    expandSchedulerDaysweekAdvancedChain,
    expandSchedulerDaysweekSimpleChain,
    ivrBuildSchedulerAdvancedColumns,
    schedulerPreferFlatSlotRowChain,
    elementToBlockConfig,
    formatSchedulerSlotNodeLabel,
    ivrSchedulerSlotNodeIdSuffix,
    ivrSchedulerSlotRowIndexFromSlotSuffix,
    ivrOperatorQueueChainNodeId,
    IVR_OPERATOR_QUEUE_CHAIN_SUFFIX,
    apiElementDraftDataRaw,
    mergeRemovedSchedulerApiSchedulesIntoDraftJson,
    resolveEffectiveIvrEntryElementId,
    resolveIvrSchedulerPresentationKind,
    resolveSchedulerOnceExtraWindow,
    schedulerOnceDailyIsFullDay,
    trimTimeToHHMM,
    schedulerBlockConfigPatchFromSchedules,
    schedulerDayOfWeekToDaysMask,
    ivrSchedulerWeekdayChainDayOfWeek,
    schedulesToDeleteForSchedulerJumpEdge,
    ivrRawScheduleActiveToId,
    ivrRawScheduleElementId,
    ivrRawScheduleRowId,
    resolveIvrSchedulesServerTruth,
    schedulerSlotRowIsPersistedInApi,
    schedulerSlotRowsNeedOthers,
    type IvrApiElement,
    type IvrApiRoute,
    type IvrApiSchedule,
    type IvrSchedulerJumpEdgeDeletionMode,
    type IvrSchedulerSlotRow,
} from "./ivrSchemeHydration";
import {
    ivrBuildIvrSchemeEdgePath,
    ivrEdgeHighlightOpacity,
    ivrEdgeVisualStyle,
    ivrResolveEdgeVisualKind,
    ivrResolveLightSchemeMarkerEnd,
} from "./ivrSchemeEdgeRouting";
import {
    buildIvrGraphPlaybackPlans,
    IVR_SCHEME_PLAYBACK_PLAN_GAP_MS,
    IVR_SCHEME_PLAYBACK_STEP_MS,
} from "./ivrSchemePlayback";

export type IVRBlockShape = "circle" | "diamond" | "triangle" | "square";

export type IVRBlockConfig = {
    dataType?: string;
    selectedDay?: string;
    comparisonParamId?: string;
    lineIds?: string[];
    [key: string]: unknown;
};

export type IVRBlockData = {
    label: string;
    shape: IVRBlockShape;
    icon?: string;
    muted?: boolean;
    accentColor?: string;
    outlineDashed?: boolean;
    blockConfig?: IVRBlockConfig;
    isGenerated?: boolean;
    configParentId?: string;
    configSourceNodeId?: string;
    playbackActive?: boolean;
    onAddNode?: (sourceNodeId: string, position: Position) => void;
    onDeleteNode?: (nodeId: string) => void;
};

const nodeTypes = { ivrBlock: IVRBlockNode };

const DAY_ABBREVS: Record<string, string> = {
    monday: "ПН",
    tuesday: "ВТ",
    wednesday: "СР",
    thursday: "ЧТ",
    friday: "ПТ",
    saturday: "СБ",
    sunday: "НД",
};

const DATA_TYPE_LABELS: Record<string, string> = {
    number: "Номер",
    string: "Рядок",
    time: "Час",
    date: "Дата",
    date_time: "Дата, час",
    day_of_month: "День місяця",
    day_of_week: "День тижня",
    qualification_group: "Кваліфікаційна група",
    queue: "Черга",
};

const NODE_GAP_VERTICAL = 68;
const NODE_GAP_HORIZONTAL = 72;

const IVR_LAYOUT_ALIGN_COLLIDE_ROUNDS = 4;
const IVR_LAYOUT_GRID_PX = 8;
const STACK_STEP_VERTICAL = 60;
const STACK_STEP_HORIZONTAL = 90;
const BASE_NODE_ID = "ivr-base";
const START_BLOCK_LABEL = "Start";
const LEGACY_INCOMING_LABEL = "Вхідна сесія";
const LEGACY_PLAY_LABEL = "Play";
const START_CONFIG_PREFIX = "start-config-";
const CHAIN_PREFIX = "start-chain-";

function isIvrMenuChainAudioLaneEdgeSource(
    chainPrefix: string,
    sourceId: string
): boolean {
    if (!sourceId.startsWith(chainPrefix)) return false;
    const suffix = sourceId.slice(chainPrefix.length);
    if (suffix === "silens-pre" || suffix === "silens-post") return true;
    return /^audio-\d+$/.test(suffix);
}

function isIvrPlayChainAudioLaneEdgeSource(
    chainPrefix: string,
    sourceId: string
): boolean {
    if (!sourceId.startsWith(chainPrefix)) return false;
    const suffix = sourceId.slice(chainPrefix.length);
    if (suffix === "silens-pre" || suffix === "silens-post") return true;
    if (/^audio-\d+$/.test(suffix)) return true;
    if (suffix === "timeout") return true;
    return false;
}

function isIvrMenuDtmfSubtreeOutboundSource(
    chainPrefix: string,
    sourceId: string
): boolean {
    if (!sourceId.startsWith(chainPrefix)) return false;
    const s = sourceId.slice(chainPrefix.length);
    if (s.startsWith("dtmf")) return true;
    return s.includes("retries");
}


function isIvrAgentHunterChainLeafSource(
    chainPrefix: string,
    sourceId: string
): boolean {
    if (!sourceId.startsWith(chainPrefix)) return false;
    const s = sourceId.slice(chainPrefix.length);
    return (
        s === IVR_OPERATOR_QUEUE_CHAIN_SUFFIX ||
        s === "end"
    );
}

function resolveSchedulerJumpTargetElementIdFromGraph(
    configNodeId: string,
    nodes: Node<IVRBlockData>[],
    edges: Edge[]
): number | null {
    const prefix = `${CHAIN_PREFIX}${configNodeId}-`;
    const byId = new Map(nodes.map((n) => [n.id, n]));
    for (const e of edges) {
        if (!e.source.startsWith(prefix)) continue;
        if (!e.target.startsWith(START_CONFIG_PREFIX)) continue;
        const tgt = byId.get(e.target);
        const raw = tgt?.data?.blockConfig?.elementId;
        const num = typeof raw === "number" ? raw : Number(raw);
        if (Number.isFinite(num) && num >= 0) return num;
    }
    return null;
}
function resolveFromElementIdForPlusSource(
    nds: Node<IVRBlockData>[],
    sourceNodeId: string,
    entryElementId: number | null | undefined
): number | undefined {
    if (sourceNodeId === BASE_NODE_ID) {
        const n =
            entryElementId != null ? Number(entryElementId) : NaN;
        return Number.isFinite(n) && n > 0 ? n : undefined;
    }
    const byId = new Map(nds.map((n) => [n.id, n]));
    let current: Node<IVRBlockData> | undefined = byId.get(sourceNodeId);
    const seen = new Set<string>();
    while (current && !seen.has(current.id)) {
        seen.add(current.id);
        const raw = current.data?.blockConfig?.elementId;
        const elId = typeof raw === "number" ? raw : Number(raw);
        if (Number.isFinite(elId) && elId > 0) return elId;
        const parentId = current.data?.configParentId;
        if (!parentId || parentId === current.id) break;
        current = byId.get(parentId);
    }
    return undefined;
}

function resolveEntryElementIdFromPayload(
    entryElementId: number | null | undefined,
    elements: IvrApiElement[] | null | undefined
): number {
    return resolveEffectiveIvrEntryElementId(
        entryElementId,
        elements ?? []
    );
}

function resolveRouteSourceElementId(
    sourceNodeId: string,
    nodes: Node<IVRBlockData>[],
    entryElementId: number | null | undefined,
    elements?: IvrApiElement[] | null
): number | null {
    if (sourceNodeId === BASE_NODE_ID) {
        return resolveEntryElementIdFromPayload(entryElementId, elements ?? null);
    }
    const byId = new Map(nodes.map((n) => [n.id, n]));
    let cur: Node<IVRBlockData> | undefined = byId.get(sourceNodeId);
    const seen = new Set<string>();
    while (cur && !seen.has(cur.id)) {
        seen.add(cur.id);
        if (cur.id.startsWith(START_CONFIG_PREFIX)) {
            const raw = cur.data?.blockConfig?.elementId;
            const num = typeof raw === "number" ? raw : Number(raw);
            if (Number.isFinite(num)) return num;
            return null;
        }
        const parentId = cur.data?.configParentId;
        if (!parentId) return null;
        cur = byId.get(parentId);
    }
    return null;
}

function resolveRouteTargetElementId(
    targetNodeId: string,
    nodes: Node<IVRBlockData>[]
): number | null {
    const n = nodes.find((x) => x.id === targetNodeId);
    if (!n?.id.startsWith(START_CONFIG_PREFIX)) return null;
    const raw = n.data?.blockConfig?.elementId;
    const num = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(num) || num <= 0) return null;
    return num;
}

function resolveSchedulerJumpEdgeDeletionPlan(
    edge: Edge,
    nodes: Node<IVRBlockData>[],
    ivrData: Record<string, unknown> | undefined
): null | {
    fromEl: number;
    toEl: number;
    configNodeId: string;
    mode: IvrSchedulerJumpEdgeDeletionMode;
} {
    const srcNode = nodes.find((n) => n.id === edge.source);
    const parentId = srcNode?.data?.configParentId;
    if (!parentId) return null;
    const parentCfg = nodes.find((n) => n.id === parentId);
    const bc = parentCfg?.data?.blockConfig as Record<string, unknown> | undefined;
    if (!bc) return null;
    const isScheduler =
        bc.startConfiguration === "Scheduler" ||
        String(bc.elementType ?? "").toUpperCase() === "SCHEDULER" ||
        String(bc.elementType ?? "").toUpperCase() === "SCHEDULE";
    if (!isScheduler) return null;

    const chainPrefix = `${CHAIN_PREFIX}${parentId}-`;
    if (!edge.source.startsWith(chainPrefix)) return null;
    const rel = edge.source.slice(chainPrefix.length);
    const isBranch =
        rel.startsWith("sched-slot-") ||
        rel.startsWith("day-") ||
        rel === "sched-others" ||
        rel === "daily-primary" ||
        rel === "daily-rest";
    if (!isBranch) return null;

    const cfg = ivrData?.configuration as
        | { entryElementId?: number | null }
        | undefined;
    const elements = (ivrData?.elements ?? []) as IvrApiElement[];
    const schedules = resolveIvrSchedulesServerTruth(ivrData);
    const fromEl = resolveRouteSourceElementId(
        edge.source,
        nodes,
        cfg?.entryElementId ?? null,
        elements
    );
    if (fromEl == null || fromEl <= 0) return null;
    const toEl = resolveRouteTargetElementId(edge.target, nodes);
    if (toEl == null || !Number.isFinite(toEl)) return null;

    const allRows = [...buildSchedulerSlotRowsFromBlockConfig(bc, toEl)].sort(
        (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );

    let mode: IvrSchedulerJumpEdgeDeletionMode = { kind: "none" };
    if (rel.startsWith("sched-slot-")) {
        const tail = rel.slice("sched-slot-".length);
        let si = ivrSchedulerSlotRowIndexFromSlotSuffix(tail, allRows);
        if (
            (si < 0 || si >= allRows.length) &&
            /^\d+$/.test(tail) &&
            allRows.length > 0
        ) {
            const j = parseInt(tail, 10);
            if (Number.isFinite(j) && j >= 0 && j < allRows.length) {
                si = j;
            }
        }
        if (si >= 0 && si < allRows.length) {
            mode = { kind: "slotRows", rows: [allRows[si]!] };
        }
    } else if (rel.startsWith("day-")) {
        const dow = parseInt(rel.slice("day-".length), 10);
        if (Number.isFinite(dow)) {
            const m = schedulerDayOfWeekToDaysMask(dow);
            const rowsForDay = allRows.filter((r) => (Number(r.daysMask) || 0) === m);
            if (rowsForDay.length > 0) {
                mode = { kind: "slotRows", rows: rowsForDay };
            }
        }
    } else if (rel === "daily-primary" && allRows[0]) {
        mode = { kind: "slotRows", rows: [allRows[0]] };
    } else if (rel === "daily-rest" && allRows[1]) {
        mode = { kind: "slotRows", rows: [allRows[1]] };
    } else if (rel === "sched-others") {
        mode = { kind: "allWithActiveTarget", toElementId: toEl };
    }

    if (mode.kind === "none" && isBranch) {
        const persisted = schedules.filter((s) => {
            const id = ivrRawScheduleRowId(s);
            if (!(Number.isFinite(id) && id > 0)) return false;
            return ivrRawScheduleElementId(s) === fromEl;
        });
        const toTarget = persisted.filter(
            (s) => ivrRawScheduleActiveToId(s) === toEl
        );
        if (toTarget.length === 1) {
            mode = { kind: "allWithActiveTarget", toElementId: toEl };
        } else if (toTarget.length > 1 && allRows.length === 0) {
            mode = { kind: "allWithActiveTarget", toElementId: toEl };
        }
    }

    return { fromEl, toEl, configNodeId: parentId, mode };
}

const ALL_DTMF_KEYS: string[] = [...IVR_MENU_DTMF_KEY_ORDER];
export function resolveIvrPostRouteTypeValue(sourceNodeId: string): {
    routeType: string;
    routeValue: string;
} {
    if (!sourceNodeId.startsWith(CHAIN_PREFIX)) {
        return { routeType: "DEFAULT", routeValue: "" };
    }
    if (sourceNodeId.endsWith("-sched-others")) {
        return { routeType: "DEFAULT", routeValue: "" };
    }
    if (sourceNodeId.endsWith("-dtmf-others")) {
        return { routeType: "EVENT", routeValue: "invalid" };
    }
    if (
        sourceNodeId.endsWith("-dtmf-timeout") ||
        sourceNodeId.endsWith("-daily-rest")
    ) {
        return { routeType: "EVENT", routeValue: "timeout" };
    }
    if (sourceNodeId.endsWith("-others-retries-false")) {
        return { routeType: "EVENT_EXCEEDED", routeValue: "invalid" };
    }
    if (sourceNodeId.endsWith("-timeout-retries-false")) {
        return { routeType: "EVENT_EXCEEDED", routeValue: "timeout" };
    }
    if (sourceNodeId.endsWith("-others-retries-true")) {
        return { routeType: "EVENT", routeValue: "invalid" };
    }
    if (sourceNodeId.endsWith("-timeout-retries-true")) {
        return { routeType: "EVENT", routeValue: "timeout" };
    }
    const retriesKeyFalse = sourceNodeId.match(
        /-retries-([0-9*#]+)-false$/
    );
    if (retriesKeyFalse?.[1] && ALL_DTMF_KEYS.includes(retriesKeyFalse[1])) {
        return {
            routeType: "DTMF_EXCEEDED",
            routeValue: retriesKeyFalse[1],
        };
    }
    const retriesKeyTrue = sourceNodeId.match(/-retries-([0-9*#]+)-true$/);
    if (retriesKeyTrue?.[1] && ALL_DTMF_KEYS.includes(retriesKeyTrue[1])) {
        return { routeType: "DTMF", routeValue: retriesKeyTrue[1] };
    }
    const dtmfKey = sourceNodeId.match(/-dtmf-([0-9*#]+)$/);
    if (dtmfKey?.[1] && ALL_DTMF_KEYS.includes(dtmfKey[1])) {
        return { routeType: "DTMF", routeValue: dtmfKey[1] };
    }
    if (
        sourceNodeId.endsWith("-timeout") &&
        !sourceNodeId.includes("dtmf-timeout") &&
        !sourceNodeId.includes("retries")
    ) {
        return { routeType: "DEFAULT", routeValue: "" };
    }
    return { routeType: "DEFAULT", routeValue: "" };
}
const TOTAL_WEEKDAYS = 7;

type IVRConfigurableLinkEdgeData = {
    ivrConfigurableLink?: boolean;
    ivrRoute?: IvrApiRoute;
};

function isPlusZoneToConfigRootEdge(
    edge: Pick<Edge, "source" | "target" | "data">,
    nodeList: Node<IVRBlockData>[]
): boolean {
    if (!edge.target.startsWith(START_CONFIG_PREFIX)) return false;
    const marked = (edge.data as IVRConfigurableLinkEdgeData | undefined)
        ?.ivrConfigurableLink;
    if (marked === true) return true;

    const srcN = nodeList.find((n) => n.id === edge.source);
    if (!srcN) return false;
    const srcIsEnd = isIvrEndBlockData(srcN.data);
    return (
        edge.source === BASE_NODE_ID ||
        (!!srcN.data.onAddNode && !srcIsEnd)
    );
}

function normalizeIvrRouteTypeKey(t: string | null | undefined): string {
    return String(t ?? "").trim().toUpperCase().replace(/-/g, "_");
}

function ivrRawRouteIdFromEdgePack(
    pack: IVRConfigurableLinkEdgeData | undefined
): number {
    const rt = pack?.ivrRoute as Record<string, unknown> | undefined;
    if (!rt) return NaN;
    const v = rt.id ?? rt.Id;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : NaN;
}

function ivrApiRouteAsRecord(r: unknown): Record<string, unknown> {
    return r != null && typeof r === "object"
        ? (r as Record<string, unknown>)
        : {};
}

function ivrRouteEndpointElementId(
    r: unknown,
    which: "from" | "to"
): number | null {
    const o = ivrApiRouteAsRecord(r);
    const camel = which === "from" ? "fromElementId" : "toElementId";
    const pascal = which === "from" ? "FromElementId" : "ToElementId";
    const v = o[camel] ?? o[pascal];
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function ivrRouteRowApiId(r: unknown): number | null {
    const o = ivrApiRouteAsRecord(r);
    const v = o.id ?? o.Id;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function ivrRouteTypeNormFromRecord(r: unknown): string {
    const o = ivrApiRouteAsRecord(r);
    const v = o.routeType ?? o.RouteType;
    return normalizeIvrRouteTypeKey(String(v ?? ""));
}

function ivrRouteValueNormFromRecord(r: unknown): string {
    const o = ivrApiRouteAsRecord(r);
    const v = o.routeValue ?? o.RouteValue;
    return String(v ?? "").trim();
}

function ivrRouteIsDeletableByActiveFlag(r: unknown): boolean {
    const o = ivrApiRouteAsRecord(r);
    const ia = o.isActive ?? o.IsActive;
    return ia !== false;
}

function isSchedulerScheduleBackedJumpSource(sourceNodeId: string): boolean {
    if (!sourceNodeId.startsWith(CHAIN_PREFIX)) return false;
    if (
        sourceNodeId.endsWith("-sched-others") ||
        sourceNodeId.includes("-sched-others")
    ) {
        return false;
    }
    if (sourceNodeId.includes("-sched-slot-")) return true;
    if (/-day-\d+$/.test(sourceNodeId)) return true;
    if (
        sourceNodeId.endsWith("-daily-primary") ||
        sourceNodeId.endsWith("-daily-rest")
    ) {
        return true;
    }
    return false;
}

function resolveRouteIdForConfigurableEdgeDelete(
    edge: Edge,
    nodes: Node<IVRBlockData>[],
    routes: readonly IvrApiRoute[],
    entryElementId: number | null | undefined,
    elements: readonly IvrApiElement[]
): number | null {
    const pack = edge.data as IVRConfigurableLinkEdgeData | undefined;
    const fromPack = ivrRawRouteIdFromEdgePack(pack);
    if (Number.isFinite(fromPack) && fromPack > 0) return fromPack;

    if (isSchedulerScheduleBackedJumpSource(edge.source)) {
        return null;
    }

    const fromEl = resolveRouteSourceElementId(
        edge.source,
        nodes,
        entryElementId,
        elements as IvrApiElement[]
    );
    const toEl = resolveRouteTargetElementId(edge.target, nodes);
    if (fromEl == null || fromEl <= 0 || toEl == null || !Number.isFinite(toEl)) {
        return null;
    }
    const { routeType, routeValue } = resolveIvrPostRouteTypeValue(edge.source);
    const nt = normalizeIvrRouteTypeKey(routeType);
    const rv = String(routeValue ?? "").trim();

    const sameEndpointIds: number[] = [];
    const exactMatchIds: number[] = [];

    for (const r of routes) {
        if (!ivrRouteIsDeletableByActiveFlag(r)) continue;
        const rFrom = ivrRouteEndpointElementId(r, "from");
        const rTo = ivrRouteEndpointElementId(r, "to");
        if (rFrom !== fromEl || rTo !== toEl) continue;
        const rid = ivrRouteRowApiId(r);
        if (rid == null) continue;
        if (
            ivrRouteTypeNormFromRecord(r) === nt &&
            ivrRouteValueNormFromRecord(r) === rv
        ) {
            exactMatchIds.push(rid);
            continue;
        }
        sameEndpointIds.push(rid);
    }

    if (exactMatchIds.length === 1) {
        return exactMatchIds[0]!;
    }
    if (exactMatchIds.length > 1) {
        return null;
    }

    if (sameEndpointIds.length === 1) {
        return sameEndpointIds[0]!;
    }

    return null;
}

function ivrRouteContinuationKeyForEdge(
    edge: Edge,
    nodes: Node<IVRBlockData>[],
    entryElementId: number | null | undefined,
    elements: readonly IvrApiElement[]
): string | null {
    if (!edge.target.startsWith(START_CONFIG_PREFIX)) return null;
    const fromEl = resolveRouteSourceElementId(
        edge.source,
        nodes,
        entryElementId,
        elements as IvrApiElement[]
    );
    const toEl = resolveRouteTargetElementId(edge.target, nodes);
    if (
        fromEl == null ||
        fromEl <= 0 ||
        toEl == null ||
        !Number.isFinite(toEl)
    ) {
        return null;
    }
    const { routeType, routeValue } = resolveIvrPostRouteTypeValue(
        edge.source
    );
    return `${fromEl}|${toEl}|${normalizeIvrRouteTypeKey(routeType)}|${String(routeValue ?? "").trim()}`;
}

function IvrSchemeEdge(props: EdgeProps) {
    const themeColorScheme = useAppSelector((state) => state.ThemeReducer.colorScheme);
    const isEasterEggPink =
        typeof window !== "undefined" &&
        localStorage.getItem("easterEggPink") === "true";
    const isDiagramLightScheme = themeColorScheme === "light" || isEasterEggPink;

    const nodes = useStore((s) => s.nodes as Node<IVRBlockData>[]);
    const selectedNodeIds = useStore(
        (s) =>
            new Set(
                (s.nodes as Node<IVRBlockData>[])
                    .filter((n) => n.selected)
                    .map((n) => n.id)
            )
    );
    const selectedEdgeIds = useStore(
        (s) => new Set(s.edges.filter((e) => e.selected).map((e) => e.id))
    );
    const { deleteElements } = useReactFlow();
    const rolesVision = useAppSelector(
        (state) => state.RolesReducer.rolesVision
    ) as unknown as Record<string, string[]>;
    const hasIVRConfigurationDeletePermission = !!checkPermissions(rolesVision, "IVRConfig").delete;
    const deletable =
        hasIVRConfigurationDeletePermission &&
        isPlusZoneToConfigRootEdge(
        { source: props.source, target: props.target, data: props.data },
        nodes
    );
    const {
        id,
        source,
        target,
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        selected,
        pathOptions,
        interactionWidth,
        label,
        labelStyle,
        labelShowBg,
        labelBgStyle,
        labelBgPadding,
        labelBgBorderRadius,
        markerEnd,
        markerStart,
        style,
    } = props;

    const isTopDownOrthogonal =
        sourcePosition === Position.Bottom && targetPosition === Position.Top;

    const visualKind = ivrResolveEdgeVisualKind(
        { source, target, data: props.data },
        nodes,
        (e) => isPlusZoneToConfigRootEdge(e, nodes)
    );
    const highlightOpacity = ivrEdgeHighlightOpacity(
        id,
        source,
        target,
        selectedNodeIds,
        selectedEdgeIds
    );

    let path: string;
    let labelX: number;
    let labelY: number;

    if (isTopDownOrthogonal && sourceY < targetY) {
        const built = ivrBuildIvrSchemeEdgePath(
            sourceX,
            sourceY,
            targetX,
            targetY,
            { preferLocalBus: visualKind === "chain" }
        );
        path = built.path;
        labelX = built.labelX;
        labelY = built.labelY;
    } else {
        const [smoothPath, smoothLabelX, smoothLabelY] = getSmoothStepPath({
            sourceX,
            sourceY,
            sourcePosition,
            targetX,
            targetY,
            targetPosition,
            borderRadius: pathOptions?.borderRadius ?? 0,
            offset: pathOptions?.offset ?? 28,
            stepPosition:
                pathOptions?.stepPosition ??
                (isTopDownOrthogonal ? 0 : 0.5),
            ...(pathOptions?.centerX != null ? { centerX: pathOptions.centerX } : {}),
            ...(pathOptions?.centerY != null ? { centerY: pathOptions.centerY } : {}),
        });
        path = smoothPath;
        labelX = smoothLabelX;
        labelY = smoothLabelY;
    }

    const onRemoveEdge = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (deletable) {
            deleteElements({ edges: [{ id }] });
        }
    };

    const edgeStyle = ivrEdgeVisualStyle(
        visualKind,
        highlightOpacity,
        !!selected,
        isDiagramLightScheme
    );
    const showMarker = visualKind !== "chain";
    let finalMarkerEnd: EdgeProps["markerEnd"] | { type: MarkerType; color?: string; width?: number; height?: number };
    if (!showMarker) {
        finalMarkerEnd = undefined;
    } else if (typeof markerEnd === "string") {
        finalMarkerEnd = markerEnd;
    } else if (markerEnd && typeof markerEnd === "object" && "type" in markerEnd) {
        finalMarkerEnd = isDiagramLightScheme
            ? (ivrResolveLightSchemeMarkerEnd(
                  markerEnd,
                  visualKind,
                  !!selected
              ) as typeof markerEnd)
            : markerEnd;
    } else {
        finalMarkerEnd = {
            type: MarkerType.ArrowClosed,
            color: "#b1b1b7",
            width: 18,
            height: 18,
        };
    }

    const deleteBtnSize = 22;
    const deleteBtnHalf = deleteBtnSize / 2;

    return (
        <>
            <BaseEdge
                id={id}
                path={path}
                labelX={labelX}
                labelY={labelY}
                label={label}
                labelStyle={labelStyle}
                labelShowBg={labelShowBg}
                labelBgStyle={labelBgStyle}
                labelBgPadding={labelBgPadding}
                labelBgBorderRadius={labelBgBorderRadius}
                style={{ ...style, ...edgeStyle }}
                markerEnd={finalMarkerEnd as string | undefined}
                markerStart={markerStart}
                interactionWidth={interactionWidth}
            />
            {deletable && selected && (
                <foreignObject
                    x={labelX - deleteBtnHalf}
                    y={labelY - deleteBtnHalf}
                    width={deleteBtnSize}
                    height={deleteBtnSize}
                    className="nopan nodrag"
                    style={{ overflow: "visible", pointerEvents: "all" }}
                >
                    <div
                        {...({
                            xmlns: "http://www.w3.org/1999/xhtml",
                        } as React.HTMLAttributes<HTMLDivElement>)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: `${deleteBtnSize}px`,
                            height: `${deleteBtnSize}px`,
                        }}
                    >
                        <button
                            type="button"
                            className="nopan nodrag ivr-edge-delete-btn"
                            onMouseDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={onRemoveEdge}
                            title="Видалити зв'язок"
                        >
                            ×
                        </button>
                    </div>
                </foreignObject>
            )}
        </>
    );
}

const edgeTypes = { default: IvrSchemeEdge };

export function isIvrEndBlockData(
    data: Pick<IVRBlockData, "blockConfig" | "label" | "icon">
): boolean {
    const sc = data.blockConfig?.startConfiguration;
    if (sc === "End") return true;
    const raw = (data.label ?? "").trim();
    if (raw.toLowerCase() === "end") return true;
    const first = raw.split("\n")[0]?.trim().toLowerCase();
    if (first === "end") return true;
    if (data.icon === "pi pi-stop") return true;
    return false;
}

function getBlockDisplayLabel(data: IVRBlockData): string {
    const cfg = data.blockConfig;
    if (cfg?.startConfiguration === "End") {
        const name =
            (cfg.name as string | undefined)?.trim() ||
            (cfg.startConfigTitle as string | undefined)?.trim() ||
            "END";
        return name;
    }
    if (!cfg) return data.label || "";

    if (
        cfg.dataType === "day_of_week" &&
        cfg.selectedDay &&
        DAY_ABBREVS[cfg.selectedDay]
    ) {
        return DAY_ABBREVS[cfg.selectedDay];
    }
    if (
        cfg.dataType === "number" &&
        cfg.comparisonOperator &&
        cfg.conditionValue != null
    ) {
        return `${cfg.comparisonOperator} ${cfg.conditionValue}`;
    }
    if (cfg.dataType === "time" && cfg.timeRange) {
        return cfg.timeRange as string;
    }
    if (cfg.dataType && DATA_TYPE_LABELS[cfg.dataType]) {
        return DATA_TYPE_LABELS[cfg.dataType];
    }
    return data.label?.trim() ? data.label : "";
}

function toRgba(color: string, alpha: number): string {
    const c = (color ?? "").trim();
    if (!c) return `rgba(255,255,255,${alpha})`;

    if (c.startsWith("rgb(") || c.startsWith("rgba(")) {
        const inside = c.substring(c.indexOf("(") + 1, c.lastIndexOf(")"));
        const parts = inside
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean)
            .map((x) => Number(x.replace(/[^0-9.]/g, "")));
        const [r, g, b] = parts.length >= 3 ? parts : [0, 0, 0];
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    if (c.startsWith("#")) {
        const hex = c.slice(1);
        const full =
            hex.length === 3
                ? hex.split("").map((ch) => `${ch}${ch}`).join("")
                : hex;
        if (full.length !== 6) return `rgba(255,255,255,${alpha})`;
        const r = parseInt(full.slice(0, 2), 16);
        const g = parseInt(full.slice(2, 4), 16);
        const b = parseInt(full.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    return c;
}

type IvrStartConfigSurfaceKind =
    | "play"
    | "menu"
    | "agenthunter"
    | "scheduler"
    | "default";

function getIvrStartConfigSurfaceKind(
    blockConfig: IVRBlockData["blockConfig"]
): IvrStartConfigSurfaceKind {
    const raw = String(blockConfig?.startConfiguration ?? "Play").trim();
    const key = raw.toLowerCase().replace(/\s+/g, "");
    if (key === "play") return "play";
    if (key === "menu") return "menu";
    if (key === "agenthunter") return "agenthunter";
    if (key === "scheduler") return "scheduler";
    return "default";
}

function IVRBlockNode({ id, data, selected }: NodeProps<Node<IVRBlockData>>) {
    const themeColorScheme = useAppSelector((state) => state.ThemeReducer.colorScheme);
    const isEasterEggPink =
        typeof window !== "undefined" &&
        localStorage.getItem("easterEggPink") === "true";
    const isDiagramLightScheme = themeColorScheme === "light" || isEasterEggPink;

    const {
        shape,
        icon,
        muted,
        accentColor,
        onAddNode,
        onDeleteNode,
        isGenerated,
        outlineDashed,
    } = data;
    const displayLabel = getBlockDisplayLabel(data);
    const isStartBase = id === "ivr-base";
    const isEndBlock = isIvrEndBlockData(data);
    const isStartConfig =
        !!isGenerated && shape === "circle" && id !== "ivr-base" && !isEndBlock;
    const startConfigSurfaceKind = isStartConfig
        ? getIvrStartConfigSurfaceKind(data.blockConfig)
        : null;
    const isPlayMainConfig =
        isStartConfig && startConfigSurfaceKind === "play";

    const startBaseBorderColor = "rgb(209 205 61)";
    const startBaseBackgroundColor = isDiagramLightScheme
        ? "rgb(219 215 52 / 98%)"
        : "rgba(209, 205, 61, 0.24)";
    const endBorderColor = "rgb(255 0 0)";
    const endBackgroundColor = isDiagramLightScheme
        ? "rgb(255 0 0 / 76%)"
        : "rgba(255, 0, 0, 0.24)";

    const handleAddNode = (position: Position, e: React.MouseEvent) => {
        e.stopPropagation();
        if (onAddNode) {
            onAddNode(id, position);
        }
    };

    const handleDeleteNode = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDeleteNode) {
            onDeleteNode(id);
        }
    };

    const canShowPlusUI = !!selected && !!onAddNode && !isEndBlock;
    const needsIncomingTarget = !isStartBase;
    const needsBottomSource = isStartBase || !isEndBlock;
    const isStartConfigRoot = id.startsWith(START_CONFIG_PREFIX);
    const hunterQids =
        (data.blockConfig?.hunterQueueIds as number[] | undefined) ?? [];
    const isAgentHunterWithQueueAnchor =
        isStartConfig &&
        data.blockConfig?.startConfiguration === "AgentHunter" &&
        hunterQids.some((q) => Number.isFinite(q) && q > 0);
    const canDragEdgeFromBottom =
        isStartBase ||
        (!!onAddNode && !isEndBlock && !isAgentHunterWithQueueAnchor);

    const structuralHandleClass = "ivr-flow-handle--structural";
    const visibleHandleClass = "ivr-flow-handle--visible";
    const structuralSilentConnectableClass =
        "ivr-flow-handle--structural ivr-flow-handle--silent-connectable";

    const bottomHandleClass = canShowPlusUI
        ? visibleHandleClass
        : canDragEdgeFromBottom
          ? structuralSilentConnectableClass
          : structuralHandleClass;

    const borderColor =
        isEndBlock
            ? endBorderColor
            : isStartBase
              ? startBaseBorderColor
              : accentColor ||
                (shape === "circle" ? "rgb(76, 175, 80)" :
                  shape === "diamond" ? "#5c5cff" :
                  shape === "square" ? "rgb(255, 152, 0)" :
                  "rgb(15, 139, 253)");

    const generatedTintAlpha = isDiagramLightScheme ? 0.9 : 0.24;
    const tintBackground = isGenerated ? toRgba(borderColor, generatedTintAlpha) : undefined;
    const iconAccentColor = isEndBlock
        ? endBorderColor
        : isStartBase
          ? startBaseBorderColor
          : accentColor;

    const solidGeneratedSquareOutline =
        isGenerated &&
        shape === "square" &&
        outlineDashed !== true;

    return (
        <div
            className={`ivr-block-node shape-${shape === "diamond" ? "diamond" : shape === "triangle" ? "triangle" : shape === "square" ? "square" : "circle"} ${selected ? "selected" : ""} ${data.playbackActive ? "playback-active" : ""} ${isGenerated ? "generated" : ""} ${solidGeneratedSquareOutline ? "ivr-gen-outline-solid" : ""} ${isStartBase ? "start-base" : ""} ${isStartConfig ? "configurationMainBlock" : ""} ${startConfigSurfaceKind ? `ivr-config-main ivr-config-main--${startConfigSurfaceKind}` : ""} ${isEndBlock ? "end-block" : ""}`}
            style={
                isEndBlock
                    ? {
                          borderTop: `3px solid ${borderColor}`,
                          borderRight: `3px solid ${borderColor}`,
                          borderBottom: `3px solid ${borderColor}`,
                          borderLeft: `3px solid ${borderColor}`,
                          borderRadius: "9999px",
                          width: 120,
                          height: 54,
                          minWidth: 120,
                          maxWidth: 120,
                          padding: 0,
                          justifyContent: "center",
                          borderImage: "initial",
                          backgroundColor: endBackgroundColor,
                      }
                    : isStartBase
                      ? {
                            borderTop: `3px solid ${borderColor}`,
                            borderRight: `3px solid ${borderColor}`,
                            borderBottom: `3px solid ${borderColor}`,
                            borderLeft: `3px solid ${borderColor}`,
                            borderRadius: "9999px",
                            borderImage: "initial",
                            padding: "10px 10px 10px 6px",
                            backgroundColor: startBaseBackgroundColor,
                        }
                      : isStartConfig
                        ? undefined
                        : {
                              borderLeft: `3px ${
                                  outlineDashed ? "dashed" : "solid"
                              } ${borderColor}`,
                              backgroundColor: tintBackground,
                          }
            }
        >
            {needsIncomingTarget && (
                <Handle
                    type="target"
                    position={Position.Top}
                    id="top"
                    className={
                        isStartConfigRoot
                            ? visibleHandleClass
                            : structuralHandleClass
                    }
                    isConnectable={isStartConfigRoot}
                />
            )}
            {isStartConfigRoot && needsIncomingTarget && (
                <>
                    <Handle
                        type="target"
                        position={Position.Left}
                        id="left"
                        className={visibleHandleClass}
                        isConnectable
                    />
                    <Handle
                        type="target"
                        position={Position.Right}
                        id="right"
                        className={visibleHandleClass}
                        isConnectable
                    />
                </>
            )}
            {needsBottomSource && (
                <Handle
                    type="source"
                    position={Position.Bottom}
                    id="bottom-source"
                    className={bottomHandleClass}
                    isConnectable={canDragEdgeFromBottom}
                />
            )}
            {isStartBase && canShowPlusUI && (
                <>
                    <Handle
                        type="source"
                        position={Position.Top}
                        id="top-source"
                        className={visibleHandleClass}
                        isConnectable
                    />
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="right-source"
                        className={visibleHandleClass}
                        isConnectable
                    />
                    <Handle
                        type="source"
                        position={Position.Left}
                        id="left-source"
                        className={visibleHandleClass}
                        isConnectable
                    />
                </>
            )}
            {icon && (
                <IconBadge shape={shape} muted={muted} accentColor={iconAccentColor}>
                    <i className={icon} />
                </IconBadge>
            )}
            <span className="ivr-block-node__label">{displayLabel}</span>
            {selected && onAddNode && !isEndBlock && (
                <>
                    {isStartBase ? (
                        <>
                            <button
                                type="button"
                                className="ivr-add-node-btn ivr-add-node-top"
                                onClick={(e) => handleAddNode(Position.Top, e)}
                                title="Додати блок зверху"
                            >
                                +
                            </button>
                            <button
                                type="button"
                                className="ivr-add-node-btn ivr-add-node-right"
                                onClick={(e) => handleAddNode(Position.Right, e)}
                                title="Додати блок справа"
                            >
                                +
                            </button>
                            <button
                                type="button"
                                className="ivr-add-node-btn ivr-add-node-bottom"
                                onClick={(e) => handleAddNode(Position.Bottom, e)}
                                title="Додати блок знизу"
                            >
                                +
                            </button>
                            <button
                                type="button"
                                className="ivr-add-node-btn ivr-add-node-left"
                                onClick={(e) => handleAddNode(Position.Left, e)}
                                title="Додати блок зліва"
                            >
                                +
                            </button>
                        </>
                    ) : isAgentHunterWithQueueAnchor ? null : (
                        <button
                            type="button"
                            className="ivr-add-node-btn ivr-add-node-bottom"
                            onClick={(e) => handleAddNode(Position.Bottom, e)}
                            title="Додати блок знизу"
                        >
                            +
                        </button>
                    )}
                </>
            )}
            {selected && onDeleteNode && (
                <button
                    type="button"
                    className="ivr-delete-node-btn"
                    onClick={handleDeleteNode}
                    title="Удалить"
                >
                    ×
                </button>
            )}
        </div>
    );
}

export type IVRSchemeBuilderRef = {
    addNode: (item: RouterMenuItem) => void;
    updateNodeData: (nodeId: string, blockConfig: IVRBlockConfig) => void;
    generateStartConfigChain: (configNodeId: string, config: IVRBlockConfig) => void;
    getNodeById: (nodeId: string) => Node<IVRBlockData> | undefined;
    collectPositiveElementIds: () => number[];
    collectEndElementPositiveIds: () => number[];
    getSchedulerJumpTargetElementId: (configNodeId: string) => number | null;
    playScheme: () => Promise<boolean>;
    playSchemeFromNode: (nodeId: string) => Promise<boolean>;
    pauseSchemePlayback: () => void;
    resumeSchemePlayback: () => void;
    stopSchemePlayback: () => void;
};

export type IvrSchemePlaybackState = {
    active: boolean;
    paused: boolean;
};

export type IVRSchemeBuilderProps = {
    onSelectedNodeChange?: (node: Node<IVRBlockData> | null) => void;
    onSchemePlaybackStateChange?: (state: IvrSchemePlaybackState) => void;
};

function schedulerNeedsOthers(
    enabledDays: { start: string; end: string }[]
): boolean {
    if (enabledDays.length < TOTAL_WEEKDAYS) return true;
    return enabledDays.some((d) => {
        const s = (d.start ?? "").trim();
        const e = (d.end ?? "").trim();
        const full24 =
            s === "00:00" &&
            (e === "23:59" || e === "00:00" || e === "24:00");
        return !full24;
    });
}

function isDailySchedulerFullDay(start: string, end: string): boolean {
    const parse = (t: string) => {
        const p = (t ?? "").trim().split(":");
        const h = parseInt(p[0] || "0", 10) || 0;
        const m = parseInt(p[1] || "0", 10) || 0;
        return { h, m };
    };
    const a = parse(start);
    const b = parse(end);
    const fullStart = a.h === 0 && a.m === 0;
    const fullEnd =
        (b.h === 23 && b.m === 59) ||
        (b.h === 0 && b.m === 0) ||
        b.h >= 24;
    return fullStart && fullEnd;
}

function dailyRestRangeLabel(
    start: string,
    end: string,
    start2?: string,
    end2?: string
): string {
    const s2 = trimTimeToHHMM(start2 ?? "");
    const e2 = trimTimeToHHMM(end2 ?? "");
    if (s2 && e2) return `${s2} - ${e2}`;
    const parse = (t: string) => {
        const p = trimTimeToHHMM(t).split(":");
        const h = parseInt(p[0] || "0", 10) || 0;
        const m = parseInt(p[1] || "0", 10) || 0;
        return { h, m };
    };
    const a = parse(start);
    const b = parse(end);
    const sh = String(b.h).padStart(2, "0");
    const sm = String(b.m).padStart(2, "0");
    const eh = String(a.h).padStart(2, "0");
    const em = String(a.m).padStart(2, "0");
    return `${sh}:${sm} - ${eh}:${em}`;
}

function wrapLabelToLines(input: string, maxCharsPerLine: number, maxLines: number): string {
    const text = (input ?? "").trim();
    if (!text) return "";

    const words = text.split(/\s+/g);
    const lines: string[] = [];

    const pushLine = (line: string) => {
        lines.push(line);
    };

    let current = "";
    for (const word of words) {
        const maybeSplitWord =
            word.length > maxCharsPerLine
                ? word.match(new RegExp(`.{1,${maxCharsPerLine}}`, "g")) ?? [word]
                : [word];

        for (const part of maybeSplitWord) {
            const next = current ? `${current} ${part}` : part;
            if (next.length <= maxCharsPerLine) {
                current = next;
                continue;
            }

            if (current) pushLine(current);
            current = part;

            if (lines.length >= maxLines) break;
        }

        if (lines.length >= maxLines) break;
    }

    if (current && lines.length < maxLines) {
        pushLine(current);
    }

    if (lines.length <= maxLines) return lines.join("\n");

    const kept = lines.slice(0, maxLines);
    const last = kept[kept.length - 1] ?? "";
    kept[kept.length - 1] = last.length > Math.max(0, maxCharsPerLine - 3) ? `${last.slice(0, maxCharsPerLine - 3)}...` : last;
    return kept.join("\n");
}

const WEEKDAY_ABBREVS: Record<number, string> = {
    1: "ПН",
    2: "ВТ",
    3: "СР",
    4: "ЧТ",
    5: "ПТ",
    6: "СБ",
    0: "НД",
};

function directionKey(sourceNodeId: string, position: Position): string {
    return `${sourceNodeId}-${position}`;
}

const IVR_NODE_WIDTH = 120;
const IVR_NODE_HEIGHT = 50;

const IVR_LAYOUT_ALIGN_START_CONFIG_UNDER_CREATION_SOURCE = true;

function estimateNodeHeight(node: Node<IVRBlockData>): number {
    const label = String(node.data?.label ?? "").trim();
    const lineCount = label ? label.split("\n").length : 1;
    const clampedLines = Math.min(lineCount, 4);
    const lineHeightPx = Math.ceil(11 * 1.15);
    const labelHeight = clampedLines * lineHeightPx;
    const iconHeight = node.data?.icon ? 22 : 0;
    const paddingV = 12;
    return Math.max(IVR_NODE_HEIGHT, paddingV + Math.max(labelHeight, iconHeight));
}

function resolveStoredConfigCreationSource(
    node: Node<IVRBlockData>,
    anchorId?: string
): string | undefined {
    const selfId = anchorId ?? node.id;
    const d = node.data;
    const top = d.configSourceNodeId;
    const bc = d.blockConfig?.configSourceNodeId as string | undefined;
    if (
        typeof top === "string" &&
        top !== BASE_NODE_ID &&
        top.trim() !== "" &&
        top !== selfId
    ) {
        return top;
    }
    if (
        typeof bc === "string" &&
        bc !== BASE_NODE_ID &&
        bc.trim() !== "" &&
        bc !== selfId
    ) {
        return bc;
    }
    return undefined;
}

function pickIncomingAlignmentSource(
    targetId: string,
    edges: Edge[],
    layoutedById: Map<string, Node<IVRBlockData>>,
    storedSource: string | undefined
): string | undefined {
    const incoming = edges.filter((e) => e.target === targetId);
    if (incoming.length === 0) return undefined;

    const allCand = Array.from(new Set(incoming.map((e) => e.source)));
    if (allCand.length === 1) {
        const only = allCand[0]!;
        if (
            isIvrEndConfigAnchorId(targetId, layoutedById) &&
            only === BASE_NODE_ID
        ) {
            return undefined;
        }
        return only;
    }

    const cand = allCand.filter((c) => c !== BASE_NODE_ID);
    const isEndTarget = isIvrEndConfigAnchorId(targetId, layoutedById);

    if (isEndTarget) {
        if (storedSource && cand.includes(storedSource)) return storedSource;
        if (cand.length === 1) return cand[0];
        if (cand.length === 0) return undefined;
        let best = cand[0]!;
        let bestY = layoutedById.get(best)?.position.y ?? 0;
        for (let i = 1; i < cand.length; i++) {
            const c = cand[i]!;
            const y = layoutedById.get(c)?.position.y ?? 0;
            if (y >= bestY) {
                best = c;
                bestY = y;
            }
        }
        return best;
    }

    if (cand.length === 1) return cand[0];
    if (cand.length === 0) return BASE_NODE_ID;

    const schedOthers = cand.find(
        (c) => c.endsWith("-sched-others") || c.includes("-sched-others")
    );
    if (schedOthers) return schedOthers;

    const dtmfOthers = cand.find(
        (c) => c.endsWith("-dtmf-others") || c.includes("-dtmf-others")
    );
    if (dtmfOthers) return dtmfOthers;

    const dailyRest = cand.find((c) => c.endsWith("-daily-rest"));
    if (dailyRest) return dailyRest;

    if (storedSource && allCand.includes(storedSource)) {
        return storedSource;
    }

    let best = cand[0]!;
    let bestScore =
        (layoutedById.get(best)?.position.x ?? 0) * 1000 +
        (layoutedById.get(best)?.position.y ?? 0);
    for (let i = 1; i < cand.length; i++) {
        const c = cand[i]!;
        const score =
            (layoutedById.get(c)?.position.x ?? 0) * 1000 +
            (layoutedById.get(c)?.position.y ?? 0);
        if (score >= bestScore) {
            best = c;
            bestScore = score;
        }
    }
    return best;
}

function alignStartConfigRootsUnderCreationSources(
    nodes: Node<IVRBlockData>[],
    edges: Edge[],
    anchorOf: Map<string, string>,
    direction: "TB" | "LR"
): Node<IVRBlockData>[] {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const shifts = new Map<string, { dx: number; dy: number }>();

    for (const n of nodes) {
        if (!n.id.startsWith(START_CONFIG_PREFIX)) continue;
        if (anchorOf.get(n.id) !== n.id) continue;

        const alignSrcId = pickIncomingAlignmentSource(
            n.id,
            edges,
            byId,
            resolveStoredConfigCreationSource(n)
        );
        if (!alignSrcId) continue;
        const srcNode = byId.get(alignSrcId);
        if (!srcNode) continue;

        let dx = 0;
        let dy = 0;
        if (direction === "TB") {
            const srcCx = srcNode.position.x + IVR_NODE_WIDTH / 2;
            const rootCx = n.position.x + IVR_NODE_WIDTH / 2;
            dx = srcCx - rootCx;
        } else {
            const srcH = estimateNodeHeight(srcNode);
            const rootH = estimateNodeHeight(n);
            const srcCy = srcNode.position.y + srcH / 2;
            const rootCy = n.position.y + rootH / 2;
            dy = srcCy - rootCy;
        }
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
        shifts.set(n.id, { dx, dy });
    }

    if (shifts.size === 0) return nodes;

    return nodes.map((node) => {
        const aid = anchorOf.get(node.id);
        if (!aid) return node;
        const sh = shifts.get(aid);
        if (!sh) return node;
        return {
            ...node,
            position: {
                x: node.position.x + sh.dx,
                y: node.position.y + sh.dy,
            },
        };
    });
}

const ANCHOR_GROUP_LAYOUT_GAP = 76;
const IVR_OUTER_RANKSEP = IVR_TREE_PARENT_TO_CHILD_GAP;

function anchorGroupBoundingBoxesFromNodes(
    nodes: Node<IVRBlockData>[],
    anchorOf: Map<string, string>
): Map<string, { left: number; right: number; top: number; bottom: number }> {
    const byAnchor = new Map<string, Node<IVRBlockData>[]>();
    for (const n of nodes) {
        const aid = anchorOf.get(n.id);
        if (!aid) continue;
        const arr = byAnchor.get(aid) ?? [];
        arr.push(n);
        byAnchor.set(aid, arr);
    }
    const boxes = new Map<
        string,
        { left: number; right: number; top: number; bottom: number }
    >();
    byAnchor.forEach((arr, aid) => {
        let left = Infinity;
        let right = -Infinity;
        let top = Infinity;
        let bottom = -Infinity;
        for (const n of arr) {
            const h = estimateNodeHeight(n);
            left = Math.min(left, n.position.x);
            right = Math.max(right, n.position.x + IVR_NODE_WIDTH);
            top = Math.min(top, n.position.y);
            bottom = Math.max(bottom, n.position.y + h);
        }
        if (Number.isFinite(left)) {
            boxes.set(aid, { left, right, top, bottom });
        }
    });
    return boxes;
}

function verticalBandsOverlapTb(
    a: { top: number; bottom: number },
    b: { top: number; bottom: number },
    minOverlapPx = 28
): boolean {
    const yov = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
    return yov > minOverlapPx;
}

function horizontalBandsOverlapLr(
    a: { left: number; right: number },
    b: { left: number; right: number },
    minOverlapPx = 28
): boolean {
    const xov = Math.min(a.right, b.right) - Math.max(a.left, b.left);
    return xov > minOverlapPx;
}

/**
 * After Dagre + optional horizontal alignment, anchor-group bboxes can intersect.
 * Push overlapping groups apart along the non-flow axis (X for TB, Y for LR).
 */
function resolveAnchorGroupCollisions(
    nodes: Node<IVRBlockData>[],
    anchorOf: Map<string, string>,
    direction: "TB" | "LR"
): Node<IVRBlockData>[] {
    const baseBoxes = anchorGroupBoundingBoxesFromNodes(nodes, anchorOf);
    const aids = Array.from(baseBoxes.keys());
    if (aids.length <= 1) return nodes;

    if (direction === "TB") {
        const shift = new Map<string, number>();
        for (const aid of aids) shift.set(aid, 0);

        const maxPasses = aids.length + 10;
        for (let pass = 0; pass < maxPasses; pass++) {
            const meta = aids.map((aid) => {
                const b = baseBoxes.get(aid)!;
                const d = shift.get(aid) ?? 0;
                return {
                    aid,
                    left: b.left + d,
                    right: b.right + d,
                    top: b.top,
                    bottom: b.bottom,
                };
            });
            meta.sort((x, y) => x.left - y.left);

            let changed = false;
            for (let i = 0; i < meta.length; i++) {
                const cur = meta[i]!;
                let minLeft = cur.left;
                for (let j = 0; j < i; j++) {
                    const prev = meta[j]!;
                    if (!verticalBandsOverlapTb(prev, cur)) continue;
                    minLeft = Math.max(minLeft, prev.right + ANCHOR_GROUP_LAYOUT_GAP);
                }
                if (minLeft > cur.left + 0.5) {
                    shift.set(cur.aid, (shift.get(cur.aid) ?? 0) + (minLeft - cur.left));
                    changed = true;
                }
            }
            if (!changed) break;
        }

        if (
            Array.from(shift.values()).every((v) => Math.abs(v) < 0.5)
        )
            return nodes;

        return nodes.map((n) => {
            const aid = anchorOf.get(n.id);
            if (!aid) return n;
            const dx = shift.get(aid) ?? 0;
            if (Math.abs(dx) < 0.5) return n;
            return {
                ...n,
                position: { x: n.position.x + dx, y: n.position.y },
            };
        });
    }

    const shiftY = new Map<string, number>();
    for (const aid of aids) shiftY.set(aid, 0);

    const maxPassesLr = aids.length + 10;
    for (let pass = 0; pass < maxPassesLr; pass++) {
        const meta = aids.map((aid) => {
            const b = baseBoxes.get(aid)!;
            const d = shiftY.get(aid) ?? 0;
            return {
                aid,
                left: b.left,
                right: b.right,
                top: b.top + d,
                bottom: b.bottom + d,
            };
        });
        meta.sort((x, y) => x.top - y.top);

        let changed = false;
        for (let i = 0; i < meta.length; i++) {
            const cur = meta[i]!;
            let minTop = cur.top;
            for (let j = 0; j < i; j++) {
                const prev = meta[j]!;
                if (!horizontalBandsOverlapLr(prev, cur)) continue;
                minTop = Math.max(minTop, prev.bottom + ANCHOR_GROUP_LAYOUT_GAP);
            }
            if (minTop > cur.top + 0.5) {
                shiftY.set(cur.aid, (shiftY.get(cur.aid) ?? 0) + (minTop - cur.top));
                changed = true;
            }
        }
        if (!changed) break;
    }

    if (
        Array.from(shiftY.values()).every((v) => Math.abs(v) < 0.5)
    )
        return nodes;

    return nodes.map((n) => {
        const aid = anchorOf.get(n.id);
        if (!aid) return n;
        const dy = shiftY.get(aid) ?? 0;
        if (Math.abs(dy) < 0.5) return n;
        return {
            ...n,
            position: { x: n.position.x, y: n.position.y + dy },
        };
    });
}

function snapIvrLayoutNodesToGrid(
    nodes: Node<IVRBlockData>[]
): Node<IVRBlockData>[] {
    return nodes.map((n) => ({
        ...n,
        position: {
            x: Math.round(n.position.x / IVR_LAYOUT_GRID_PX) * IVR_LAYOUT_GRID_PX,
            y: Math.round(n.position.y / IVR_LAYOUT_GRID_PX) * IVR_LAYOUT_GRID_PX,
        },
    }));
}

function refineLayoutAlignAndSeparate(
    laidOut: Node<IVRBlockData>[],
    edges: Edge[],
    anchorOf: Map<string, string>,
    direction: "TB" | "LR"
): Node<IVRBlockData>[] {
    let nodes = laidOut;
    if (!IVR_LAYOUT_ALIGN_START_CONFIG_UNDER_CREATION_SOURCE) {
        return resolveAnchorGroupCollisions(nodes, anchorOf, direction);
    }
    for (let r = 0; r < IVR_LAYOUT_ALIGN_COLLIDE_ROUNDS; r++) {
        nodes = alignStartConfigRootsUnderCreationSources(
            nodes,
            edges,
            anchorOf,
            direction
        );
        nodes = resolveAnchorGroupCollisions(nodes, anchorOf, direction);
    }
    return nodes;
}

function buildIvrAnchorMaps(nodes: Node<IVRBlockData>[]) {
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const anchorOf = new Map<string, string>();
    const childrenOf = new Map<string, string[]>();

    for (const node of nodes) {
        const pid = node.data?.configParentId;
        if (
            pid &&
            pid !== node.id &&
            node.data?.isGenerated &&
            nodeById.has(pid)
        ) {
            anchorOf.set(node.id, pid);
            const arr = childrenOf.get(pid) ?? [];
            arr.push(node.id);
            childrenOf.set(pid, arr);
        } else {
            anchorOf.set(node.id, node.id);
            if (!childrenOf.has(node.id)) childrenOf.set(node.id, []);
        }
    }

    return { nodeById, anchorOf, childrenOf };
}

function computeAnchorGroupGeometryFromPositions(
    nodes: Node<IVRBlockData>[],
    anchorOf: Map<string, string>,
    childrenOf: Map<string, string[]>
): {
    nodeRelTL: Map<string, { x: number; y: number }>;
    groupBBox: Map<string, { w: number; h: number }>;
} {
    const nodeRelTL = new Map<string, { x: number; y: number }>();
    const groupBBox = new Map<string, { w: number; h: number }>();
    const byId = new Map(nodes.map((n) => [n.id, n]));

    childrenOf.forEach((_cids, aid) => {
        const members = [aid, ...(childrenOf.get(aid) ?? [])];
        let minL = Infinity;
        let maxR = -Infinity;
        let minT = Infinity;
        let maxB = -Infinity;

        for (let i = 0; i < members.length; i++) {
            const n = byId.get(members[i]!);
            if (!n) continue;
            const h = estimateNodeHeight(n);
            minL = Math.min(minL, n.position.x);
            maxR = Math.max(maxR, n.position.x + IVR_NODE_WIDTH);
            minT = Math.min(minT, n.position.y);
            maxB = Math.max(maxB, n.position.y + h);
        }

        if (!Number.isFinite(minL)) return;

        groupBBox.set(aid, { w: maxR - minL, h: maxB - minT });
        for (let i = 0; i < members.length; i++) {
            const n = byId.get(members[i]!);
            if (!n) continue;
            nodeRelTL.set(members[i]!, {
                x: n.position.x - minL,
                y: n.position.y - minT,
            });
        }
    });

    return { nodeRelTL, groupBBox };
}

/** Express each anchor group relative to its config root (root → 0,0). */
function resolvePlacementSourceNodeId(
    anchorId: string,
    anchorNode: Node<IVRBlockData>,
    edges: Edge[],
    layoutedById: Map<string, Node<IVRBlockData>>
): string | undefined {
    const stored = resolveStoredConfigCreationSource(anchorNode, anchorId);
    let sourceNodeId = pickIncomingAlignmentSource(
        anchorId,
        edges,
        layoutedById,
        stored
    );
    if (
        !sourceNodeId &&
        stored &&
        stored !== BASE_NODE_ID &&
        stored !== anchorId
    ) {
        sourceNodeId = stored;
    }
    if (isIvrEndConfigAnchorId(anchorId, layoutedById)) {
        if (
            !sourceNodeId ||
            sourceNodeId === BASE_NODE_ID ||
            sourceNodeId === anchorId
        ) {
            return undefined;
        }
        return sourceNodeId;
    }
    if (!sourceNodeId) {
        sourceNodeId = BASE_NODE_ID;
    }
    return sourceNodeId;
}

function finalizeEndConfigPlacement(
    nodes: Node<IVRBlockData>[],
    edges: Edge[],
    anchorOf: Map<string, string>,
    positions: Map<string, { x: number; y: number }>
): void {
    const layoutedById = nodesByCurrentPositions(nodes, positions);

    for (const n of nodes) {
        if (!isIvrEndConfigAnchorId(n.id, layoutedById)) continue;

        const sourceNodeId = resolvePlacementSourceNodeId(
            n.id,
            n,
            edges,
            layoutedById
        );

        if (
            sourceNodeId &&
            sourceNodeId !== BASE_NODE_ID &&
            sourceNodeId !== n.id
        ) {
            const sourceNode = layoutedById.get(sourceNodeId);
            if (!sourceNode) continue;
            const sourcePos =
                positions.get(sourceNodeId) ?? sourceNode.position;
            const sourceAnchorId = resolveConfigAnchorPlacementParent(
                sourceNodeId,
                anchorOf
            );
            const bottomY = Math.max(
                ivrAnchorGroupBottomY(
                    sourceAnchorId,
                    nodes,
                    positions,
                    anchorOf
                ),
                ivrLayoutNodeBottomY(sourceNode, sourcePos)
            );
            positions.set(n.id, {
                x:
                    ivrLayoutNodeCenterX(sourcePos) -
                    IVR_NODE_WIDTH / 2,
                y: bottomY + IVR_OUTER_RANKSEP,
            });
            continue;
        }

        let maxBottom = 0;
        for (const other of nodes) {
            if (other.id === n.id) continue;
            const p = positions.get(other.id) ?? other.position;
            maxBottom = Math.max(
                maxBottom,
                p.y + estimateNodeHeight(other)
            );
        }
        const cur = positions.get(n.id) ?? n.position;
        positions.set(n.id, {
            x: cur.x,
            y: maxBottom + IVR_OUTER_RANKSEP,
        });
    }
}

function ivrLayoutNodeCenterX(pos: { x: number }): number {
    return pos.x + IVR_NODE_WIDTH / 2;
}

function ivrLayoutNodeBottomY(
    node: Node<IVRBlockData>,
    pos: { x: number; y: number }
): number {
    return pos.y + estimateNodeHeight(node);
}

function nodesByCurrentPositions(
    nodes: Node<IVRBlockData>[],
    positions: Map<string, { x: number; y: number }>
): Map<string, Node<IVRBlockData>> {
    const out = new Map<string, Node<IVRBlockData>>();
    for (const n of nodes) {
        const pos = positions.get(n.id) ?? n.position;
        out.set(n.id, { ...n, position: { ...pos } });
    }
    return out;
}

function resolveConfigAnchorPlacementParent(
    sourceNodeId: string,
    anchorOf: Map<string, string>
): string {
    if (sourceNodeId === BASE_NODE_ID) return BASE_NODE_ID;
    return anchorOf.get(sourceNodeId) ?? sourceNodeId;
}

function ivrAnchorGroupBottomY(
    anchorId: string,
    nodes: Node<IVRBlockData>[],
    positions: Map<string, { x: number; y: number }>,
    anchorOf: Map<string, string>
): number {
    let maxBottom = 0;
    for (const n of nodes) {
        if (anchorOf.get(n.id) !== anchorId) continue;
        const pos = positions.get(n.id) ?? n.position;
        maxBottom = Math.max(maxBottom, ivrLayoutNodeBottomY(n, pos));
    }
    return maxBottom;
}

function isIvrEndConfigAnchorId(
    nodeId: string,
    layoutedById: Map<string, Node<IVRBlockData>>
): boolean {
    if (!nodeId.startsWith(START_CONFIG_PREFIX)) return false;
    const n = layoutedById.get(nodeId);
    return n != null && isIvrEndBlockData(n.data);
}

function ivrShouldRecenterChainUnderRoot(
    anchorId: string,
    nodes: Node<IVRBlockData>[]
): boolean {
    const n = nodes.find((node) => node.id === anchorId);
    if (!n) return false;
    const sc = n.data?.blockConfig?.startConfiguration as string | undefined;
    if (sc === "Scheduler") return true;
    const label = String(n.data?.label ?? "").split("\n")[0]?.trim();
    return label === "Scheduler";
}

function ivrSchedulerFlatRowMemberIds(
    anchorId: string,
    nodes: Node<IVRBlockData>[],
    anchorOf: Map<string, string>,
    positions: Map<string, { x: number; y: number }>
): string[] {
    const chainPrefix = `${CHAIN_PREFIX}${anchorId}-`;
    const daySortOrder: Record<number, number> = {
        1: 0,
        2: 1,
        3: 2,
        4: 3,
        5: 4,
        6: 5,
        0: 6,
    };

    const slotIds: string[] = [];
    const dayIds: { dayOfWeek: number; id: string }[] = [];
    let othersId: string | undefined;

    for (const n of nodes) {
        if (anchorOf.get(n.id) !== anchorId || n.id === anchorId) continue;
        if (n.id.includes("-sched-others")) {
            othersId = n.id;
            continue;
        }
        if (n.id.includes("-sched-slot-")) {
            slotIds.push(n.id);
            continue;
        }
        if (
            n.id.includes("-daily-primary") ||
            n.id.includes("-daily-rest")
        ) {
            slotIds.push(n.id);
            continue;
        }
        if (!n.id.startsWith(chainPrefix)) continue;
        const dayMatch = /-day-(\d+)$/.exec(n.id);
        if (dayMatch) {
            const dow = parseInt(dayMatch[1]!, 10);
            if (Number.isFinite(dow)) {
                dayIds.push({ dayOfWeek: dow, id: n.id });
            }
        }
    }

    if (dayIds.length > 0) {
        dayIds.sort(
            (a, b) =>
                (daySortOrder[a.dayOfWeek] ?? 999) -
                (daySortOrder[b.dayOfWeek] ?? 999)
        );
        const row = dayIds.map((d) => d.id);
        if (othersId) row.push(othersId);
        return row;
    }

    if (slotIds.length === 0 && !othersId) return [];

    slotIds.sort((a, b) => {
        const ax = positions.get(a)?.x ?? 0;
        const bx = positions.get(b)?.x ?? 0;
        if (Math.abs(ax - bx) > 0.5) return ax - bx;
        return a.localeCompare(b);
    });

    if (othersId) slotIds.push(othersId);
    return slotIds;
}

function ivrIsDaysweekAdvancedSchedulerAnchor(
    anchorId: string,
    nodes: Node<IVRBlockData>[]
): boolean {
    const n = nodes.find((node) => node.id === anchorId);
    if (!n) return false;
    const kind = normalizeIvrSchedulerScheduleKind(
        n.data?.blockConfig?.schedulerScheduleKind
    );
    if (kind !== "daysweek_advanced") return false;
    const chainPrefix = `${CHAIN_PREFIX}${anchorId}-`;
    return nodes.some(
        (node) =>
            node.id.startsWith(chainPrefix) && node.id.includes("-day-")
    );
}

/** DaysWeek (advanced): day row under scheduler, time slots in a horizontal row under each day. */
function layoutSchedulerDaysweekAdvancedTree(
    anchorId: string,
    nodes: Node<IVRBlockData>[],
    positions: Map<string, { x: number; y: number }>
): void {
    const rootPos = positions.get(anchorId);
    const anchorNode = nodes.find((n) => n.id === anchorId);
    if (!rootPos || !anchorNode) return;

    const sorted = [
        ...((anchorNode.data?.blockConfig?.schedulerSlotRows as
            | IvrSchedulerSlotRow[]
            | undefined) ?? []),
    ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    if (!sorted.length) return;

    const needOthers = schedulerSlotRowsNeedOthers(sorted);
    const columns = ivrBuildSchedulerAdvancedColumns(sorted, needOthers);
    if (!columns.length) return;

    const rootH = estimateNodeHeight(anchorNode);
    const yDay = ivrSchedulerSlotRowY(rootPos.y, rootH);
    const colGap = 0;
    const rootCenterX = ivrLayoutNodeCenterX(rootPos);
    const colWidths = columns.map((c) =>
        c.kind === "day"
            ? ivrSchedulerAdvancedColumnWidth(c.slots.length)
            : IVR_SCHEDULER_SIBLING_GAP_PX
    );
    const totalWidth =
        colWidths.reduce((s, w) => s + w, 0) +
        Math.max(0, columns.length - 1) * colGap;
    let xCursor = rootCenterX - totalWidth / 2;
    const chainPrefix = `${CHAIN_PREFIX}${anchorId}-`;

    for (let ci = 0; ci < columns.length; ci++) {
        const col = columns[ci]!;
        const colW = colWidths[ci] ?? IVR_SCHEDULER_SIBLING_GAP_PX;
        const centerX = xCursor + colW / 2;

        if (col.kind === "day") {
            const dayId = `${chainPrefix}day-${col.dayOfWeek}`;
            positions.set(dayId, {
                x: centerX - IVR_NODE_WIDTH / 2,
                y: yDay,
            });
            const slotCount = col.slots.length;
            col.slots.forEach((slot, si) => {
                const slotId = `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(
                    sorted,
                    slot.idx
                )}`;
                positions.set(
                    slotId,
                    ivrSchedulerDaySlotRowLayout(
                        yDay,
                        centerX,
                        si,
                        slotCount
                    )
                );
            });
        } else {
            positions.set(`${chainPrefix}sched-others`, {
                x: centerX - IVR_NODE_WIDTH / 2,
                y: yDay,
            });
        }

        xCursor += colW + colGap;
    }
}

function layoutSchedulerAnchorChain(
    anchorId: string,
    nodes: Node<IVRBlockData>[],
    anchorOf: Map<string, string>,
    positions: Map<string, { x: number; y: number }>
): void {
    if (ivrIsDaysweekAdvancedSchedulerAnchor(anchorId, nodes)) {
        layoutSchedulerDaysweekAdvancedTree(anchorId, nodes, positions);
        return;
    }
    layoutSchedulerFlatSlotRow(anchorId, nodes, anchorOf, positions);
}

/** Force flat scheduler slot row spacing from layout constants (hydration geometry may be overwritten). */
function layoutSchedulerFlatSlotRow(
    anchorId: string,
    nodes: Node<IVRBlockData>[],
    anchorOf: Map<string, string>,
    positions: Map<string, { x: number; y: number }>
): void {
    const rootPos = positions.get(anchorId);
    if (!rootPos) return;

    const rowIds = ivrSchedulerFlatRowMemberIds(
        anchorId,
        nodes,
        anchorOf,
        positions
    );
    if (rowIds.length === 0) return;

    const rootNode = nodes.find((n) => n.id === anchorId);
    const rootH = rootNode ? estimateNodeHeight(rootNode) : IVR_NODE_HEIGHT;
    const slotY = ivrSchedulerSlotRowY(rootPos.y, rootH);
    const gap = IVR_SCHEDULER_SIBLING_GAP_PX;
    const totalSpan = Math.max(0, rowIds.length - 1) * gap;
    const rootCenterX = ivrLayoutNodeCenterX(rootPos);
    const startCenterX = rootCenterX - totalSpan / 2;

    for (let i = 0; i < rowIds.length; i++) {
        positions.set(rowIds[i]!, {
            x: startCenterX + i * gap - IVR_NODE_WIDTH / 2,
            y: slotY,
        });
    }
}

function alignStartNodeAboveEntryConfig(
    nodes: Node<IVRBlockData>[],
    positions: Map<string, { x: number; y: number }>,
    edges: Edge[]
): void {
    const entryEdge = edges.find(
        (e) =>
            e.source === BASE_NODE_ID &&
            e.target.startsWith(START_CONFIG_PREFIX)
    );
    if (!entryEdge) return;

    const startNode = nodes.find((n) => n.id === BASE_NODE_ID);
    const entryPos = positions.get(entryEdge.target);
    if (!startNode || !entryPos) return;

    const startH = estimateNodeHeight(startNode);
    const entryCenterX = ivrLayoutNodeCenterX(entryPos);
    positions.set(BASE_NODE_ID, {
        x: entryCenterX - IVR_NODE_WIDTH / 2,
        y: entryPos.y - IVR_START_ABOVE_ENTRY_GAP - startH,
    });
}

function layoutAgentHunterQueueChildren(
    nodes: Node<IVRBlockData>[],
    anchorOf: Map<string, string>,
    positions: Map<string, { x: number; y: number }>
): void {
    for (const n of nodes) {
        if (anchorOf.get(n.id) !== n.id) continue;
        if (n.data?.blockConfig?.startConfiguration !== "AgentHunter") continue;
        const queueId = ivrOperatorQueueChainNodeId(n.id);
        if (!nodes.some((node) => node.id === queueId)) continue;
        const pos = positions.get(n.id) ?? n.position;
        positions.set(queueId, {
            x: pos.x,
            y: ivrAgentHunterQueueChildY(pos.y, estimateNodeHeight(n)),
        });
    }
}

function getLayoutedElementsTreeTb(
    nodes: Node<IVRBlockData>[],
    edges: Edge[],
    anchorOf: Map<string, string>,
    childrenOf: Map<string, string[]>,
    options?: { preserveY?: boolean }
): Node<IVRBlockData>[] {
    const positions = new Map<string, { x: number; y: number }>();
    for (const n of nodes) {
        positions.set(n.id, { ...n.position });
    }

    const configAnchorIds = nodes
        .filter(
            (n) =>
                n.id.startsWith(START_CONFIG_PREFIX) &&
                anchorOf.get(n.id) === n.id
        )
        .map((n) => n.id);

    for (const aid of configAnchorIds) {
        if (ivrShouldRecenterChainUnderRoot(aid, nodes)) {
            layoutSchedulerAnchorChain(aid, nodes, anchorOf, positions);
        }
    }
    layoutAgentHunterQueueChildren(nodes, anchorOf, positions);

    const nodesWithCurrentPos = nodes.map((n) => ({
        ...n,
        position: positions.get(n.id) ?? n.position,
    }));
    const { nodeRelTL, groupBBox } = computeAnchorGroupGeometryFromPositions(
        nodesWithCurrentPos,
        anchorOf,
        childrenOf
    );

    const nodesById = nodesByCurrentPositions(nodes, positions);

    const endAnchorIds = new Set<string>();
    const treeAnchorIds: string[] = [];

    for (const aid of configAnchorIds) {
        if (isIvrEndConfigAnchorId(aid, nodesById)) {
            endAnchorIds.add(aid);
            continue;
        }
        if (!nodesById.has(aid)) continue;
        treeAnchorIds.push(aid);
    }

    const placementParentOf = new Map<string, string>();
    const placementChildren = new Map<string, string[]>();
    placementChildren.set(BASE_NODE_ID, []);

    for (const aid of treeAnchorIds) {
        const node = nodesById.get(aid)!;
        const sourceNodeId = resolvePlacementSourceNodeId(
            aid,
            node,
            edges,
            nodesById
        );
        const parent =
            sourceNodeId && sourceNodeId !== BASE_NODE_ID
                ? resolveConfigAnchorPlacementParent(sourceNodeId, anchorOf)
                : BASE_NODE_ID;
        placementParentOf.set(aid, parent);
        const arr = placementChildren.get(parent) ?? [];
        arr.push(aid);
        placementChildren.set(parent, arr);
    }

    const reachable = new Set<string>([BASE_NODE_ID]);
    let reachChanged = true;
    while (reachChanged) {
        reachChanged = false;
        for (const aid of treeAnchorIds) {
            if (reachable.has(aid)) continue;
            const parent = placementParentOf.get(aid);
            if (parent && reachable.has(parent)) {
                reachable.add(aid);
                reachChanged = true;
            }
        }
    }
    for (const aid of treeAnchorIds) {
        if (reachable.has(aid)) continue;
        const oldParent = placementParentOf.get(aid);
        if (oldParent) {
            const arr = placementChildren.get(oldParent) ?? [];
            placementChildren.set(
                oldParent,
                arr.filter((c) => c !== aid)
            );
        }
        placementParentOf.set(aid, BASE_NODE_ID);
        const baseArr = placementChildren.get(BASE_NODE_ID) ?? [];
        if (!baseArr.includes(aid)) {
            baseArr.push(aid);
            placementChildren.set(BASE_NODE_ID, baseArr);
        }
    }

    const siblingSortKey = (aid: string): { x: number; id: string } => {
        const node = nodesById.get(aid);
        if (!node) return { x: 0, id: aid };
        const stored = resolveStoredConfigCreationSource(node, aid);
        const sourceId =
            pickIncomingAlignmentSource(aid, edges, nodesById, stored) ??
            stored ??
            BASE_NODE_ID;
        const srcNode = nodesById.get(sourceId);
        return { x: srcNode?.position.x ?? 0, id: aid };
    };
    placementChildren.forEach((arr) => {
        if (arr.length <= 1) return;
        arr.sort((a, b) => {
            const ka = siblingSortKey(a);
            const kb = siblingSortKey(b);
            if (Math.abs(ka.x - kb.x) > 0.5) return ka.x - kb.x;
            return ka.id.localeCompare(kb.id);
        });
    });

    const sourceOffsetRel = (
        childAid: string,
        parentAid: string
    ): number => {
        const node = nodesById.get(childAid);
        const parentNode = nodesById.get(parentAid);
        if (!node || !parentNode) return 0;
        const parentCx = parentNode.position.x + IVR_NODE_WIDTH / 2;

        const xs: number[] = [];
        for (const e of edges) {
            if (e.target !== childAid) continue;
            if (e.source === BASE_NODE_ID || e.source === childAid) continue;
            const srcAnchor = anchorOf.get(e.source) ?? e.source;
            if (srcAnchor !== parentAid) continue;
            const sn = nodesById.get(e.source);
            if (!sn) continue;
            xs.push(sn.position.x + IVR_NODE_WIDTH / 2);
        }
        if (xs.length === 0) {
            const stored = resolveStoredConfigCreationSource(node, childAid);
            if (!stored || stored === BASE_NODE_ID || stored === childAid)
                return 0;
            const srcAnchor = anchorOf.get(stored) ?? stored;
            if (srcAnchor !== parentAid) return 0;
            const sn = nodesById.get(stored);
            if (!sn) return 0;
            return sn.position.x + IVR_NODE_WIDTH / 2 - parentCx;
        }
        const avgX = xs.reduce((s, x) => s + x, 0) / xs.length;
        return avgX - parentCx;
    };

    type SubtreeMetrics = {
        leftHalf: number;
        rightHalf: number;
        childRelX: number[];
    };
    const subtreeMetrics = new Map<string, SubtreeMetrics>();

    const computeSubtreeMetrics = (aid: string): SubtreeMetrics => {
        const cached = subtreeMetrics.get(aid);
        if (cached) return cached;
        const ownW = groupBBox.get(aid)?.w ?? IVR_NODE_WIDTH;
        const ownHalf = ownW / 2;
        const ch = placementChildren.get(aid) ?? [];

        if (ch.length === 0) {
            const r: SubtreeMetrics = {
                leftHalf: ownHalf,
                rightHalf: ownHalf,
                childRelX: [],
            };
            subtreeMetrics.set(aid, r);
            return r;
        }

        const childM = ch.map((c) => computeSubtreeMetrics(c));
        const preferred = ch.map((c) => sourceOffsetRel(c, aid));
        const gap = IVR_TIDY_SUBTREE_GAP_PX;
        const n = ch.length;
        const fwd = new Array<number>(n);
        fwd[0] = preferred[0]!;
        for (let i = 1; i < n; i++) {
            const minV =
                fwd[i - 1]! +
                childM[i - 1]!.rightHalf +
                childM[i]!.leftHalf +
                gap;
            fwd[i] = Math.max(preferred[i]!, minV);
        }

        const bwd = new Array<number>(n);
        bwd[n - 1] = preferred[n - 1]!;
        for (let i = n - 2; i >= 0; i--) {
            const maxV =
                bwd[i + 1]! -
                childM[i + 1]!.leftHalf -
                childM[i]!.rightHalf -
                gap;
            bwd[i] = Math.min(preferred[i]!, maxV);
        }

        const childRelX = new Array<number>(n);
        for (let i = 0; i < n; i++) {
            childRelX[i] = (fwd[i]! + bwd[i]!) / 2;
        }

        for (let i = 1; i < n; i++) {
            const minV =
                childRelX[i - 1]! +
                childM[i - 1]!.rightHalf +
                childM[i]!.leftHalf +
                gap;
            if (childRelX[i]! < minV) childRelX[i] = minV;
        }

        let minLeft = Infinity;
        let maxRight = -Infinity;
        for (let i = 0; i < n; i++) {
            const l = childRelX[i]! - childM[i]!.leftHalf;
            const r = childRelX[i]! + childM[i]!.rightHalf;
            if (l < minLeft) minLeft = l;
            if (r > maxRight) maxRight = r;
        }

        const r: SubtreeMetrics = {
            leftHalf: Math.max(ownHalf, -minLeft),
            rightHalf: Math.max(ownHalf, maxRight),
            childRelX,
        };
        subtreeMetrics.set(aid, r);
        return r;
    };

    const baseChildren = placementChildren.get(BASE_NODE_ID) ?? [];
    for (const c of baseChildren) computeSubtreeMetrics(c);

    const anchorTopY = new Map<string, number>();
    const anchorRootCenterX = new Map<string, number>();

    if (options?.preserveY) {
        for (const aid of treeAnchorIds) {
            const pos = positions.get(aid);
            if (pos) anchorTopY.set(aid, pos.y);
        }
    } else {
        const baseNode = nodesById.get(BASE_NODE_ID);
        const baseBottomY = baseNode
            ? baseNode.position.y + estimateNodeHeight(baseNode)
            : 0;
        const firstLevelTopY = baseBottomY + IVR_START_ABOVE_ENTRY_GAP;

        const assignY = (aid: string, topY: number): void => {
            anchorTopY.set(aid, topY);
            const groupH = groupBBox.get(aid)?.h ?? IVR_NODE_HEIGHT;
            const childTopY = topY + groupH + IVR_TREE_PARENT_TO_CHILD_GAP;
            for (const c of placementChildren.get(aid) ?? []) {
                assignY(c, childTopY);
            }
        };
        for (const c of baseChildren) assignY(c, firstLevelTopY);
    }

    const assignX = (aid: string, centerX: number): void => {
        anchorRootCenterX.set(aid, centerX);
        const ch = placementChildren.get(aid) ?? [];
        if (ch.length === 0) return;
        const metrics = subtreeMetrics.get(aid);
        if (!metrics || metrics.childRelX.length !== ch.length) {
            for (const c of ch) assignX(c, centerX);
            return;
        }
        for (let i = 0; i < ch.length; i++) {
            assignX(ch[i]!, centerX + metrics.childRelX[i]!);
        }
    };

    {
        const childM = baseChildren.map(
            (c) =>
                subtreeMetrics.get(c) ?? {
                    leftHalf: IVR_NODE_WIDTH / 2,
                    rightHalf: IVR_NODE_WIDTH / 2,
                    childRelX: [],
                }
        );
        const totalSpread =
            childM.reduce((s, m) => s + m.leftHalf + m.rightHalf, 0) +
            Math.max(0, baseChildren.length - 1) * IVR_TIDY_SUBTREE_GAP_PX;
        const baseNode = nodesById.get(BASE_NODE_ID);
        const baseCenterX = baseNode
            ? baseNode.position.x + IVR_NODE_WIDTH / 2
            : 80 + IVR_NODE_WIDTH / 2;
        let cursor = baseCenterX - totalSpread / 2;
        for (let i = 0; i < baseChildren.length; i++) {
            const m = childM[i]!;
            assignX(baseChildren[i]!, cursor + m.leftHalf);
            cursor += m.leftHalf + m.rightHalf + IVR_TIDY_SUBTREE_GAP_PX;
        }
    }

    for (const aid of treeAnchorIds) {
        const centerX = anchorRootCenterX.get(aid);
        const topY = anchorTopY.get(aid);
        if (centerX == null || topY == null) continue;
        const bbox = groupBBox.get(aid);
        const groupW = bbox?.w ?? IVR_NODE_WIDTH;
        const groupLeft = centerX - groupW / 2;
        const members = [aid, ...(childrenOf.get(aid) ?? [])];
        for (const memberId of members) {
            const rel = nodeRelTL.get(memberId);
            if (!rel) continue;
            positions.set(memberId, {
                x: groupLeft + rel.x,
                y: topY + rel.y,
            });
        }
    }

    for (const aid of configAnchorIds) {
        if (endAnchorIds.has(aid)) continue;
        if (ivrShouldRecenterChainUnderRoot(aid, nodes)) {
            layoutSchedulerAnchorChain(aid, nodes, anchorOf, positions);
        }
    }
    layoutAgentHunterQueueChildren(nodes, anchorOf, positions);

    finalizeEndConfigPlacement(nodes, edges, anchorOf, positions);

    alignStartNodeAboveEntryConfig(nodes, positions, edges);

    const laidOut = nodes.map((n) => ({
        ...n,
        position: positions.get(n.id) ?? n.position,
    }));

    return snapIvrLayoutNodesToGrid(laidOut);
}

/** Legacy Dagre layout (used for LR direction). */
function getLayoutedElementsDagre(
    nodes: Node<IVRBlockData>[],
    edges: Edge[],
    direction: "TB" | "LR",
    anchorOf: Map<string, string>,
    childrenOf: Map<string, string[]>
): Node<IVRBlockData>[] {
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const posById = new Map(nodes.map((n) => [n.id, n.position]));
    const nodeRelTL = new Map<string, { x: number; y: number }>();
    const groupBBox = new Map<string, { w: number; h: number }>();

    childrenOf.forEach((cids, aid) => {
        const anchor = nodeById.get(aid);
        if (!anchor) return;

        const members = [aid, ...cids];

        if (cids.length === 0) {
            nodeRelTL.set(aid, { x: 0, y: 0 });
            groupBBox.set(aid, {
                w: IVR_NODE_WIDTH,
                h: estimateNodeHeight(anchor),
            });
            return;
        }

        const memberSet = new Set(members);
        const ig = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
        ig.setGraph({
            rankdir: direction,
            ranksep: direction === "TB" ? IVR_OUTER_RANKSEP : 56,
            nodesep: IVR_MENU_DTMF_GAP_PX,
        });

        for (let i = 0; i < members.length; i++) {
            const n = nodeById.get(members[i]!)!;
            ig.setNode(members[i]!, {
                width: IVR_NODE_WIDTH,
                height: estimateNodeHeight(n),
            });
        }

        const intraEdges = edges
            .filter((e) => memberSet.has(e.source) && memberSet.has(e.target))
            .sort((a, b) => {
                if (a.source !== b.source) return 0;
                return (
                    (posById.get(a.target)?.x ?? 0) -
                    (posById.get(b.target)?.x ?? 0)
                );
            });
        for (let i = 0; i < intraEdges.length; i++) {
            ig.setEdge(intraEdges[i]!.source, intraEdges[i]!.target);
        }

        Dagre.layout(ig);

        let cMinL = Infinity;
        let cMaxR = -Infinity;
        for (const cid of cids) {
            const p = ig.node(cid);
            if (!p) continue;
            cMinL = Math.min(cMinL, p.x - IVR_NODE_WIDTH / 2);
            cMaxR = Math.max(cMaxR, p.x + IVR_NODE_WIDTH / 2);
        }

        const anchorP = ig.node(aid);
        const childrenCenterX =
            Number.isFinite(cMinL) && Number.isFinite(cMaxR)
                ? (cMinL + cMaxR) / 2
                : (anchorP?.x ?? 0);

        const anchorOriginalX = anchorP?.x ?? childrenCenterX;
        const anchorRecenteredX = childrenCenterX;

        let minL = Infinity;
        let maxR = -Infinity;
        let minT = Infinity;
        let maxB = -Infinity;
        const memberCenterX = (mid: string) => {
            if (mid === aid) return anchorRecenteredX;
            return ig.node(mid)?.x ?? anchorOriginalX;
        };
        for (let i = 0; i < members.length; i++) {
            const p = ig.node(members[i]!);
            if (!p) continue;
            const nh = p.height ?? IVR_NODE_HEIGHT;
            const cx = memberCenterX(members[i]!);
            minL = Math.min(minL, cx - IVR_NODE_WIDTH / 2);
            maxR = Math.max(maxR, cx + IVR_NODE_WIDTH / 2);
            minT = Math.min(minT, p.y - nh / 2);
            maxB = Math.max(maxB, p.y + nh / 2);
        }

        groupBBox.set(aid, { w: maxR - minL, h: maxB - minT });

        for (let i = 0; i < members.length; i++) {
            const p = ig.node(members[i]!);
            if (!p) continue;
            const nh = p.height ?? IVR_NODE_HEIGHT;
            const cx = memberCenterX(members[i]!);
            nodeRelTL.set(members[i]!, {
                x: cx - IVR_NODE_WIDTH / 2 - minL,
                y: p.y - nh / 2 - minT,
            });
        }
    });

    const anchors = new Set(childrenOf.keys());
    const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    g.setGraph({
        rankdir: direction,
        ranksep: direction === "TB" ? IVR_OUTER_RANKSEP : 84,
        nodesep: IVR_SCHEDULER_SIBLING_GAP_PX,
    });

    Array.from(anchors).forEach((aid) => {
        const b = groupBBox.get(aid) ?? {
            w: IVR_NODE_WIDTH,
            h: IVR_NODE_HEIGHT,
        };
        g.setNode(aid, { width: b.w, height: b.h });
    });

    const seenSkelEdges = new Set<string>();
    const sortedEdges = [...edges].sort((a, b) => {
        if (a.source !== b.source) return 0;
        return (
            (posById.get(a.target)?.x ?? 0) - (posById.get(b.target)?.x ?? 0)
        );
    });

    for (const e of sortedEdges) {
        const sa = anchorOf.get(e.source) ?? e.source;
        const ta = anchorOf.get(e.target) ?? e.target;
        if (sa === ta || !anchors.has(sa) || !anchors.has(ta)) continue;
        const key = `${sa}\0${ta}`;
        if (seenSkelEdges.has(key)) continue;
        seenSkelEdges.add(key);
        g.setEdge(sa, ta);
    }

    Dagre.layout(g);

    const bboxTL = new Map<string, { x: number; y: number }>();
    Array.from(anchors).forEach((aid) => {
        const p = g.node(aid);
        if (!p) return;
        bboxTL.set(aid, {
            x: p.x - (p.width ?? IVR_NODE_WIDTH) / 2,
            y: p.y - (p.height ?? IVR_NODE_HEIGHT) / 2,
        });
    });

    const laidOut = nodes.map((node) => {
        const aid = anchorOf.get(node.id);
        if (!aid) return node;
        const tl = bboxTL.get(aid);
        const rel = nodeRelTL.get(node.id);
        if (!tl || !rel) return node;
        return { ...node, position: { x: tl.x + rel.x, y: tl.y + rel.y } };
    });

    return snapIvrLayoutNodesToGrid(
        refineLayoutAlignAndSeparate(laidOut, edges, anchorOf, direction)
    );
}

function getLayoutedElements(
    nodes: Node<IVRBlockData>[],
    edges: Edge[],
    direction: "TB" | "LR",
    options?: { preserveY?: boolean }
) {
    const { anchorOf, childrenOf } = buildIvrAnchorMaps(nodes);

    const laidOutNodes =
        direction === "TB"
            ? getLayoutedElementsTreeTb(
                  nodes,
                  edges,
                  anchorOf,
                  childrenOf,
                  options
              )
            : getLayoutedElementsDagre(
                  nodes,
                  edges,
                  direction,
                  anchorOf,
                  childrenOf
              );

    return { nodes: laidOutNodes, edges };
}

const initialNodes: Node<IVRBlockData>[] = [
    {
        id: BASE_NODE_ID,
        type: "ivrBlock",
        position: { x: 80, y: 60 },
        data: {
            label: START_BLOCK_LABEL,
            shape: "circle",
            icon: "pi pi-circle-fill",
        },
    },
];

function makeEdge(
    source: string,
    target: string,
    sourceHandle = "bottom-source",
    targetHandle = "top",
    extraData?: Record<string, unknown> | null
): Edge {
    const base: Edge = {
        id: `e-${source}-${target}`,
        source,
        target,
        sourceHandle,
        targetHandle,
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: "#b1b1b7",
            width: 18,
            height: 18,
        },
    };
    const data: IVRConfigurableLinkEdgeData = { ...(extraData as IVRConfigurableLinkEdgeData) };
    if (target.startsWith(START_CONFIG_PREFIX)) {
        data.ivrConfigurableLink = true;
    }
    return Object.keys(data).length ? { ...base, data } : base;
}

async function persistSchedulerDraftAfterScheduleRemoval(
    dispatch: AppDispatch,
    jwt: string,
    flowId: number,
    parentEl: IvrApiElement,
    nextRoutes: IvrApiRoute[],
    nextSchedules: IvrApiSchedule[]
): Promise<boolean> {
    const parentType = String(parentEl.elementType ?? "").toUpperCase();

    if (parentType !== "SCHEDULER" && parentType !== "SCHEDULE") {
        return true;
    }

    const blockConfig = elementToBlockConfig(
        parentEl,
        nextRoutes,
        nextSchedules
    ) as Record<string, unknown>;

    const rawDraftData = apiElementDraftDataRaw(parentEl);
    const overrideDraftData =
        typeof rawDraftData === "string" && rawDraftData.trim() !== ""
            ? rawDraftData.trim()
            : undefined;

    const draftDataOptions =
        overrideDraftData != null
            ? { overrideDraftData }
            : { previousElementDraftData: rawDraftData };

    const putPayload = blockConfigToCreateElementPayload(flowId, blockConfig, {
        ivrSchedulesForDraft: nextSchedules,
        ...draftDataOptions,
    });

    try {
        const putResult = await ivrConfigMutationRequest(
            dispatch,
            putIvrConfigElement(putPayload, jwt)
        );

        return putResult !== false;
    } catch {
        return false;
    }
}

const IVRSchemeBuilder = forwardRef<IVRSchemeBuilderRef, IVRSchemeBuilderProps>(
    function IVRSchemeBuilder(
        { onSelectedNodeChange, onSchemePlaybackStateChange },
        ref
    ) {
        const nodeIdRef = useRef(1);
        const directionCountRef = useRef<Map<string, number>>(new Map());
        const [nodes, setNodes] = useState<Node<IVRBlockData>[]>(initialNodes);
        const [edges, setEdges] = useState<Edge[]>([]);
        const rfInstanceRef = useRef<ReactFlowInstance<Node<IVRBlockData>, Edge> | null>(null);
        const requestVerticalLayoutRef = useRef(false);
        const ivrLayoutPreserveYRef = useRef(false);
        const dispatch = useAppDispatch();
        const rolesVision = useAppSelector(
            (state) => state.RolesReducer.rolesVision
        ) as unknown as Record<string, string[]>;
        const ivrConfigurationPermissions = checkPermissions(rolesVision, "IVRConfig");
        const hasIVRConfigurationCreatePermission = !!ivrConfigurationPermissions.create;
        const hasIVRConfigurationUpdatePermission = !!ivrConfigurationPermissions.update;
        const hasIVRConfigurationDeletePermission = !!ivrConfigurationPermissions.delete;
        const canMutateIVRScheme =
            hasIVRConfigurationCreatePermission || hasIVRConfigurationUpdatePermission;
        const ivrConfigurationData = useAppSelector(
            (state) => state.IVRConfigurationReducer.ivrConfigurationData
        );
        const ivrFullRef = useRef(ivrConfigurationData);
        ivrFullRef.current = ivrConfigurationData;
        const themeColorScheme = useAppSelector((state) => state.ThemeReducer.colorScheme);
        const isEasterEggPink =
            typeof window !== "undefined" &&
            localStorage.getItem("easterEggPink") === "true";
        const ivrDiagramColorScheme: "light" | "dark" =
            themeColorScheme === "light" || isEasterEggPink ? "light" : "dark";
        const schemeReloadToken = (ivrConfigurationData as { _schemeReloadToken?: number })
            ?._schemeReloadToken;

        const serializedSchemeSigRef = useRef<string>("");
        const cfgFlowIdRef = useRef<number | null | undefined>(undefined);
        cfgFlowIdRef.current = (
            ivrConfigurationData?.configuration as { flowId?: number | null } | undefined
        )?.flowId;

        const tempElementIdRef = useRef(-1000);

        useEffect(() => {
            setNodes((nds) => {
                const normalized = nds
                    .map((n) => {
                        if (n.id === BASE_NODE_ID) {
                            return {
                                ...n,
                                data: {
                                    ...n.data,
                                    label: START_BLOCK_LABEL,
                                    icon: n.data.icon || "pi pi-circle-fill",
                                },
                            };
                        }
                        if (
                            n.data.label === LEGACY_INCOMING_LABEL ||
                            n.data.label === LEGACY_PLAY_LABEL
                        ) {
                            return {
                                ...n,
                                data: { ...n.data, label: START_BLOCK_LABEL },
                            };
                        }
                        return n;
                    })
                    .filter(
                        (n) =>
                            n.id === BASE_NODE_ID || n.data.label !== START_BLOCK_LABEL
                    );

                const hasBase = normalized.some((n) => n.id === BASE_NODE_ID);
                if (hasBase) return normalized;

                return [...initialNodes, ...normalized];
            });
        }, []);

        const startConfigIdRef = useRef(1);

        useEffect(() => {
            if (schemeReloadToken == null) return;

            const data = ivrFullRef.current;
            const cfg = data?.configuration as
                | { flowId?: number | null; entryElementId?: number | null }
                | undefined;
            const elements = data?.elements as IvrApiElement[] | undefined;
            if (!elements?.length) return;

            const routes = (data?.routes ?? []) as IvrApiRoute[];
            const schedules = resolveIvrSchedulesServerTruth(
                data
            ) as IvrApiSchedule[];

            const built = buildHydratedGraphFromApiPayload({
                entryElementId: cfg?.entryElementId,
                elements,
                routes,
                schedules,
            });
            if (!built) return;

            startConfigIdRef.current = built.nextStartConfigId;
            let nextTemp = -1000;
            for (const n of built.nodes as Node<IVRBlockData>[]) {
                const eid = n.data?.blockConfig?.elementId;
                if (typeof eid === "number" && eid < 0) {
                    nextTemp = Math.min(nextTemp, eid - 1);
                }
            }
            tempElementIdRef.current = nextTemp;

            setNodes(built.nodes as Node<IVRBlockData>[]);
            setEdges(built.edges);
            ivrLayoutPreserveYRef.current = false;
            requestVerticalLayoutRef.current = true;
        }, [schemeReloadToken]);

        useEffect(() => {
            const t = window.setTimeout(() => {
                const cfg = ivrFullRef.current?.configuration as
                    | { flowId?: number | null; entryElementId?: number | null }
                    | undefined;
                const slice = serializeIvrGraphToApiParts(nodes, edges, cfg ?? null);
                const sig = JSON.stringify(slice);
                if (sig === serializedSchemeSigRef.current) return;
                serializedSchemeSigRef.current = sig;
                dispatch(setIvrSchemeGraphApiSlice(slice));
            }, 450);
            return () => window.clearTimeout(t);
        }, [nodes, edges, dispatch]);

        const createNewStartConfigFrom = useCallback(
            (sourceNodeId: string) => {
                if (!canMutateIVRScheme) {
                    return;
                }
                const id = `${START_CONFIG_PREFIX}${startConfigIdRef.current}`;
                startConfigIdRef.current += 1;


                setEdges((eds) => {
                    let next = eds;
                    if (sourceNodeId === BASE_NODE_ID) {
                        next = eds.filter(
                            (e) =>
                                !(
                                    e.source === BASE_NODE_ID &&
                                    e.target.startsWith(START_CONFIG_PREFIX)
                                )
                        );
                    }
                    const edgeId = `e-${sourceNodeId}-${id}`;
                    if (next.some((e) => e.id === edgeId)) return next;
                    return [...next, makeEdge(sourceNodeId, id)];
                });

                setNodes((nds) => {
                    const sourceNode = nds.find((n) => n.id === sourceNodeId);
                    const baseX = sourceNode ? sourceNode.position.x : 80;
                    const baseY = sourceNode ? sourceNode.position.y : 60;

                    const existingConfigs = nds
                        .filter((n) => n.id.startsWith(START_CONFIG_PREFIX))
                        .sort((a, b) => a.position.x - b.position.x);
                    const idx = existingConfigs.length;

                    const isBaseSource = sourceNodeId === BASE_NODE_ID;
                    const posX = isBaseSource
                        ? baseX + idx * NODE_GAP_HORIZONTAL
                        : baseX + NODE_GAP_HORIZONTAL;
                    const posY = isBaseSource ? baseY + NODE_GAP_VERTICAL : baseY + NODE_GAP_VERTICAL;

                    const elId = tempElementIdRef.current;
                    tempElementIdRef.current -= 1;
                    const cfg = ivrFullRef.current?.configuration as
                        | { entryElementId?: number | null }
                        | undefined;
                    const fromElementId = resolveFromElementIdForPlusSource(
                        nds,
                        sourceNodeId,
                        cfg?.entryElementId
                    );
                    const newNode: Node<IVRBlockData> = {
                        id,
                        type: "ivrBlock",
                        position: {
                            x: posX,
                            y: posY,
                        },
                        data: {
                            label: "",
                            shape: "circle",
                            icon: "",
                            accentColor: "rgb(148, 148, 148)",
                            isGenerated: true,
                            configParentId: id,
                            configSourceNodeId: sourceNodeId,
                            blockConfig: {
                                startConfiguration: "Play",
                                startConfigTitle: "",
                                elementId: elId,
                                flowId: cfgFlowIdRef.current ?? null,
                                elementType: "PLAY",
                                name: null,
                                ...(fromElementId != null
                                    ? { fromElementId }
                                    : {}),
                            },
                        },
                    };

                    let result = [...nds, newNode];

                    const srcNode = nds.find((n) => n.id === sourceNodeId);
                    const parentConfigId = srcNode?.data?.configParentId;
                    if (
                        parentConfigId &&
                        parentConfigId !== sourceNodeId
                    ) {
                        const parentNode = nds.find(
                            (n) => n.id === parentConfigId
                        );
                        if (
                            parentNode?.data?.blockConfig
                                ?.startConfiguration === "Scheduler"
                        ) {
                            const slotPrefix = `${CHAIN_PREFIX}${parentConfigId}-sched-slot-`;
                            if (sourceNodeId.startsWith(slotPrefix)) {
                                const tail =
                                    sourceNodeId.slice(slotPrefix.length);
                                const slotRows = [
                                    ...((parentNode.data.blockConfig
                                        .schedulerSlotRows as
                                        | IvrSchedulerSlotRow[]
                                        | undefined) ?? []),
                                ].sort(
                                    (a, b) =>
                                        (a.priority ?? 0) - (b.priority ?? 0)
                                );
                                const slotIdx =
                                    ivrSchedulerSlotRowIndexFromSlotSuffix(
                                        tail,
                                        slotRows
                                    );
                                if (
                                    slotIdx >= 0 &&
                                    slotIdx < slotRows.length
                                ) {
                                    slotRows[slotIdx] = {
                                        ...slotRows[slotIdx],
                                        activeToId: elId,
                                    };
                                    result = result.map((n) =>
                                        n.id === parentConfigId
                                            ? {
                                                  ...n,
                                                  data: {
                                                      ...n.data,
                                                      blockConfig: {
                                                          ...(n.data
                                                              .blockConfig ??
                                                              {}),
                                                          schedulerSlotRows:
                                                              slotRows,
                                                      },
                                                  },
                                              }
                                            : n
                                    );
                                }
                            }
                        }
                    }

                    setTimeout(() => onSelectedNodeChange?.(newNode), 0);
                    return result;
                });
            },
            [canMutateIVRScheme, onSelectedNodeChange]
        );

        const createNewStartConfig = useCallback(() => {
            createNewStartConfigFrom(BASE_NODE_ID);
        }, [createNewStartConfigFrom]);

        const getOffsetForPosition = useCallback((pos: Position) => {
            switch (pos) {
                case Position.Top:
                    return { x: 0, y: -NODE_GAP_VERTICAL };
                case Position.Right:
                    return { x: NODE_GAP_HORIZONTAL, y: 0 };
                case Position.Bottom:
                    return { x: 0, y: NODE_GAP_VERTICAL };
                case Position.Left:
                    return { x: -NODE_GAP_HORIZONTAL, y: 0 };
                default:
                    return { x: NODE_GAP_HORIZONTAL, y: 0 };
            }
        }, []);

        const getStackOffsetForPosition = useCallback((pos: Position) => {
            switch (pos) {
                case Position.Top:
                    return { x: 0, y: -STACK_STEP_VERTICAL };
                case Position.Right:
                    return { x: STACK_STEP_HORIZONTAL, y: 0 };
                case Position.Bottom:
                    return { x: 0, y: STACK_STEP_VERTICAL };
                case Position.Left:
                    return { x: -STACK_STEP_HORIZONTAL, y: 0 };
                default:
                    return { x: STACK_STEP_HORIZONTAL, y: 0 };
            }
        }, []);

        const deleteNode = useCallback((nodeId: string) => {
            setNodes((nds) => nds.filter((n) => n.id !== nodeId));
            setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
        }, []);

        const deleteConfigChain = useCallback((configNodeId: string) => {
            const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
            setNodes((nds) =>
                nds.filter(
                    (n) => n.id !== configNodeId && !n.id.startsWith(chainPrefix)
                )
            );
            setEdges((eds) =>
                eds.filter((e) => {
                    if (e.source === configNodeId || e.target === configNodeId) return false;
                    if (e.source.startsWith(chainPrefix) || e.target.startsWith(chainPrefix)) return false;
                    return true;
                })
            );
            onSelectedNodeChange?.(null);
        }, [onSelectedNodeChange]);

        const nodesRef = useRef(nodes);
        nodesRef.current = nodes;
        const edgesRef = useRef(edges);
        edgesRef.current = edges;
        const schemePlaybackRunRef = useRef(0);
        const schemePlaybackPausedRef = useRef(false);
        const onSchemePlaybackStateChangeRef = useRef(
            onSchemePlaybackStateChange
        );
        onSchemePlaybackStateChangeRef.current = onSchemePlaybackStateChange;

        const notifySchemePlaybackState = useCallback(
            (state: IvrSchemePlaybackState) => {
                onSchemePlaybackStateChangeRef.current?.(state);
            },
            []
        );

        const waitSchemePlaybackDelay = useCallback(
            async (ms: number, runId: number): Promise<boolean> => {
                let remaining = ms;
                while (remaining > 0) {
                    if (schemePlaybackRunRef.current !== runId) {
                        return false;
                    }
                    while (schemePlaybackPausedRef.current) {
                        if (schemePlaybackRunRef.current !== runId) {
                            return false;
                        }
                        await new Promise<void>((resolve) => {
                            window.setTimeout(resolve, 100);
                        });
                    }
                    const chunk = Math.min(50, remaining);
                    await new Promise<void>((resolve) => {
                        window.setTimeout(resolve, chunk);
                    });
                    remaining -= chunk;
                }
                return schemePlaybackRunRef.current === runId;
            },
            []
        );

        const clearSchemePlaybackHighlight = useCallback(() => {
            setNodes((nds) =>
                nds.map((n) =>
                    n.data.playbackActive
                        ? {
                              ...n,
                              data: { ...n.data, playbackActive: false },
                          }
                        : n
                )
            );
        }, []);

        const applySchemePlaybackNodeIds = useCallback(
            (activeNodeIds: ReadonlySet<string>) => {
                setNodes((nds) =>
                    nds.map((n) => {
                        const active = activeNodeIds.has(n.id);
                        if (!!n.data.playbackActive === active) {
                            return n;
                        }
                        return {
                            ...n,
                            data: { ...n.data, playbackActive: active },
                        };
                    })
                );
            },
            []
        );

        const stopSchemePlayback = useCallback(() => {
            schemePlaybackRunRef.current += 1;
            schemePlaybackPausedRef.current = false;
            clearSchemePlaybackHighlight();
            notifySchemePlaybackState({ active: false, paused: false });
        }, [clearSchemePlaybackHighlight, notifySchemePlaybackState]);

        const pauseSchemePlayback = useCallback(() => {
            if (schemePlaybackPausedRef.current) {
                return;
            }
            schemePlaybackPausedRef.current = true;
            notifySchemePlaybackState({ active: true, paused: true });
        }, [notifySchemePlaybackState]);

        const resumeSchemePlayback = useCallback(() => {
            if (!schemePlaybackPausedRef.current) {
                return;
            }
            schemePlaybackPausedRef.current = false;
            notifySchemePlaybackState({ active: true, paused: false });
        }, [notifySchemePlaybackState]);

        const runSchemePlayback = useCallback(
            async (fromNodeId?: string): Promise<boolean> => {
            schemePlaybackRunRef.current += 1;
            const runId = schemePlaybackRunRef.current;
            schemePlaybackPausedRef.current = false;
            clearSchemePlaybackHighlight();
            notifySchemePlaybackState({ active: true, paused: false });

            const data = ivrFullRef.current;
            const elements = (data?.elements ?? []) as IvrApiElement[];
            if (!elements.length) {
                notifySchemePlaybackState({ active: false, paused: false });
                return false;
            }

            const routes = (data?.routes ?? []) as IvrApiRoute[];
            const schedules = resolveIvrSchedulesServerTruth(data);
            const entry =
                (
                    data?.configuration as
                        | { entryElementId?: number | null }
                        | undefined
                )?.entryElementId ?? null;

            const plans = buildIvrGraphPlaybackPlans({
                nodes: nodesRef.current,
                edges: edgesRef.current,
                entryElementId: entry,
                elements,
                routes,
                schedules,
                fromNodeId,
            });
            if (!plans.length) {
                notifySchemePlaybackState({ active: false, paused: false });
                return false;
            }

            const focusNode = (nodeId: string) => {
                const node = nodesRef.current.find((n) => n.id === nodeId);
                const rf = rfInstanceRef.current;
                if (!rf || !node) return;
                const w =
                    (typeof node.width === "number" ? node.width : 120) / 2;
                const h =
                    (typeof node.height === "number" ? node.height : 54) / 2;
                rf.setCenter(node.position.x + w, node.position.y + h, {
                    duration: 380,
                    zoom: Math.max(rf.getZoom(), 0.85),
                });
            };

            for (let p = 0; p < plans.length; p++) {
                if (schemePlaybackRunRef.current !== runId) {
                    return false;
                }
                if (p > 0) {
                    clearSchemePlaybackHighlight();
                    const gapOk = await waitSchemePlaybackDelay(
                        IVR_SCHEME_PLAYBACK_PLAN_GAP_MS,
                        runId
                    );
                    if (!gapOk) {
                        return false;
                    }
                }

                const { nodeIds } = plans[p];
                const highlighted = new Set<string>();

                for (let i = 0; i < nodeIds.length; i++) {
                    if (schemePlaybackRunRef.current !== runId) {
                        return false;
                    }
                    highlighted.add(nodeIds[i]);
                    applySchemePlaybackNodeIds(highlighted);
                    focusNode(nodeIds[i]);
                    const stepOk = await waitSchemePlaybackDelay(
                        IVR_SCHEME_PLAYBACK_STEP_MS,
                        runId
                    );
                    if (!stepOk) {
                        return false;
                    }
                }
            }

            if (schemePlaybackRunRef.current === runId) {
                clearSchemePlaybackHighlight();
                notifySchemePlaybackState({ active: false, paused: false });
            }
            return true;
        },
            [
                clearSchemePlaybackHighlight,
                applySchemePlaybackNodeIds,
                notifySchemePlaybackState,
                waitSchemePlaybackDelay,
            ]
        );

        const playScheme = useCallback(
            () => runSchemePlayback(),
            [runSchemePlayback]
        );

        const playSchemeFromNode = useCallback(
            (nodeId: string) => runSchemePlayback(nodeId),
            [runSchemePlayback]
        );

        useEffect(
            () => () => {
                stopSchemePlayback();
            },
            [stopSchemePlayback]
        );

        const jwtToken = useAppSelector(
            (state) => state.UsersReducer.userData.jwtToken
        );
        const jwtTokenRef = useRef<string | null | undefined>(undefined);
        jwtTokenRef.current = jwtToken?.jwtToken;
        const hasIVRRouteDeletePermissionRef = useRef(hasIVRConfigurationDeletePermission);
        hasIVRRouteDeletePermissionRef.current = hasIVRConfigurationDeletePermission;
        const mayOrchestrateIvrRouteDeleteRef = useRef(false);
        mayOrchestrateIvrRouteDeleteRef.current =
            !!ivrConfigurationPermissions.delete ||
            !!ivrConfigurationPermissions.update;

        const requestDelete = useCallback(
            (nodeId: string) => {
                if (!hasIVRConfigurationDeletePermission) {
                    return;
                }
                const n = nodesRef.current.find((x) => x.id === nodeId);
                const isStartConfig = nodeId.startsWith(START_CONFIG_PREFIX);
                const isPlaceholder =
                    isStartConfig &&
                    !String(n?.data.label ?? "").trim() &&
                    !String(n?.data.icon ?? "").trim();

                const message = isPlaceholder
                    ? "Видалити цей блок?"
                    : isStartConfig
                      ? "Видалити цю конфігурацію?"
                      : "Видалити цей блок?";

                confirmDialog({
                    header: "Підтвердження видалення",
                    message,
                    icon: "pi pi-exclamation-triangle",
                    acceptLabel: "Так",
                    rejectLabel: "Ні",
                    acceptClassName: "p-button-danger",
                    accept: async () => {
                        if (isPlaceholder) {
                            deleteNode(nodeId);
                            onSelectedNodeChange?.(null);
                            return;
                        }

                        if (isStartConfig) {
                            const bc = n?.data?.blockConfig as
                                | Record<string, unknown>
                                | undefined;
                            const rawEl = bc?.elementId;
                            const elId =
                                typeof rawEl === "number"
                                    ? rawEl
                                    : Number(rawEl);
                            const cfg = ivrFullRef.current?.configuration as
                                | { flowId?: number | null }
                                | undefined;
                            const rawFl = cfg?.flowId;
                            const flId =
                                rawFl != null ? Number(rawFl) : NaN;

                            if (
                                Number.isFinite(elId) &&
                                elId >= 0 &&
                                Number.isFinite(flId) &&
                                flId > 0
                            ) {
                                try {
                                    const delRes = await ivrConfigMutationRequest(
                                        dispatch,
                                        deleteIvrConfigElement(
                                            flId,
                                            elId,
                                            jwtToken?.jwtToken ?? undefined
                                        )
                                    );
                                    if (delRes === false) {
                                        return;
                                    }
                                    const delData = (
                                        delRes.data as { data?: unknown }
                                    )?.data ?? delRes.data;
                                    const {
                                        deletedAudioIds = [],
                                        deletedRouteIds = [],
                                        deletedScheduleIds = [],
                                    } = (delData ?? {}) as {
                                        deletedAudioIds?: number[];
                                        deletedRouteIds?: number[];
                                        deletedScheduleIds?: number[];
                                    };
                                    const prev = ivrFullRef.current as
                                        | Record<string, unknown>
                                        | undefined;
                                    const prevElements = (prev?.elements ?? []) as IvrApiElement[];
                                    const prevRoutes = (prev?.routes ?? []) as IvrApiRoute[];
                                    const prevSchedules = (prev?.schedules ?? []) as IvrApiSchedule[];
                                    const prevSchedSnap = (prev?._ivrSchedulesServerSnapshot ??
                                        []) as IvrApiSchedule[];
                                    const delRouteSet = new Set(deletedRouteIds);
                                    const delSchedSet = new Set(deletedScheduleIds);
                                    const delAudioSet = new Set(deletedAudioIds);
                                    const nextElements = prevElements
                                        .filter((e) => e.elementId !== elId)
                                        .map((e) => {
                                            if (!delAudioSet.size || !e.audios?.length) {
                                                return e;
                                            }
                                            const nextAudios = e.audios.filter(
                                                (a) => !delAudioSet.has(a.audioId)
                                            );
                                            if (nextAudios.length === e.audios.length) {
                                                return e;
                                            }
                                            return { ...e, audios: nextAudios };
                                        });
                                    const schedFilter = (s: IvrApiSchedule) =>
                                        !delSchedSet.has(
                                            ivrRawScheduleRowId(s)
                                        ) &&
                                        ivrRawScheduleElementId(s) !== elId;
                                    const nextSchedules =
                                        prevSchedules.filter(schedFilter);
                                    const nextSchedSnap =
                                        prevSchedSnap.filter(schedFilter);
                                    dispatch(
                                        setIvrSchemeGraphApiSlice({
                                            elements: nextElements,
                                            routes: prevRoutes.filter(
                                                (r) =>
                                                    !delRouteSet.has(r.id ?? -1) &&
                                                    r.fromElementId !== elId &&
                                                    r.toElementId !== elId
                                            ),
                                            schedules: nextSchedules,
                                            schedulesServerSnapshotReplace:
                                                nextSchedSnap,
                                            configurationPatch: {
                                                entryElementId:
                                                    (prev?.configuration as { entryElementId?: number | null } | undefined)
                                                        ?.entryElementId ?? null,
                                            },
                                        })
                                    );
                                    deleteConfigChain(nodeId);
                                    onSelectedNodeChange?.(null);
                                } catch {
                                    dispatch(showError());
                                }
                                return;
                            }

                            deleteConfigChain(nodeId);
                            onSelectedNodeChange?.(null);
                            return;
                        }

                        deleteNode(nodeId);
                        onSelectedNodeChange?.(null);
                    },
                });
            },
            [
                deleteNode,
                deleteConfigChain,
                dispatch,
                hasIVRConfigurationDeletePermission,
                jwtToken?.jwtToken,
                onSelectedNodeChange,
            ]
        );

        const addNodeFromBlock = useCallback(
            (sourceNodeId: string, position: Position) => {
                if (!canMutateIVRScheme) {
                    return;
                }
                const baseOffset = getOffsetForPosition(position);
                const stackOffset = getStackOffsetForPosition(position);
                const key = directionKey(sourceNodeId, position);
                setNodes((nds) => {
                    const source = nds.find((n) => n.id === sourceNodeId);
                    if (!source) return nds;
                    const count = directionCountRef.current.get(key) ?? 0;
                    directionCountRef.current.set(key, count + 1);
                    const id = `ivr-${nodeIdRef.current}`;
                    nodeIdRef.current += 1;
                    const delta = {
                        x: baseOffset.x + stackOffset.x * count,
                        y: baseOffset.y + stackOffset.y * count,
                    };
                    const newPosition = {
                        x: source.position.x + delta.x,
                        y: source.position.y + delta.y,
                    };
                    const d = source.data;
                    const nextZ = Math.max(0, ...nds.map((n) => (n.zIndex ?? 0))) + 1;
                    const newNode: Node<IVRBlockData> = {
                        id,
                        type: "ivrBlock",
                        position: newPosition,
                        zIndex: nextZ,
                        data: {
                            label: d.label,
                            shape: d.shape,
                            icon: d.icon,
                            muted: d.muted,
                            accentColor: d.accentColor,
                            blockConfig: d.blockConfig,
                            onAddNode: addNodeFromBlock,
                            onDeleteNode: requestDelete,
                        },
                    };
                    return [...nds, newNode];
                });
            },
            [canMutateIVRScheme, getOffsetForPosition, getStackOffsetForPosition, requestDelete]
        );

        const addNode = useCallback(
            (item: RouterMenuItem) => {
                if (!canMutateIVRScheme) {
                    return;
                }
                if (item.label === START_BLOCK_LABEL) return;

                const id = `ivr-${nodeIdRef.current}`;
                nodeIdRef.current += 1;
                setNodes((nds) => {
                    const last = nds[nds.length - 1];
                    const position = last
                        ? {
                              x: last.position.x + NODE_GAP_HORIZONTAL,
                              y: last.position.y,
                          }
                        : { x: 80, y: 60 };
                    const nextZ = Math.max(0, ...nds.map((n) => (n.zIndex ?? 0))) + 1;
                    const newNode: Node<IVRBlockData> = {
                        id,
                        type: "ivrBlock",
                        position,
                        zIndex: nextZ,
                        data: {
                            label: item.label,
                            shape: item.shape,
                            icon: item.icon,
                            muted: item.muted,
                            accentColor: item.accentColor,
                            onAddNode: addNodeFromBlock,
                            onDeleteNode: requestDelete,
                        },
                    };
                    return [...nds, newNode];
                });
            },
            [addNodeFromBlock, canMutateIVRScheme, requestDelete]
        );

        const generateStartConfigChain = useCallback(
            (configNodeId: string, config: IVRBlockConfig) => {
                if (configNodeId === BASE_NODE_ID) return;
                requestVerticalLayoutRef.current = true;
                const configSourceNodeIdForEdges =
                    (config.configSourceNodeId as string) ?? BASE_NODE_ID;
                setNodes((nds) => {
                    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;
                    const cleaned = nds.filter((n) => !n.id.startsWith(chainPrefix));
                    const startNode = cleaned.find((n) => n.id === BASE_NODE_ID);
                    const baseX = startNode ? startNode.position.x : 80;
                    const baseY = startNode ? startNode.position.y : 60;

                    const configType = (config.startConfiguration as string) || "Play";
                    const meta = (config.playAudioMeta as { audioId: number; audioName: string; groupId: number }[]) ?? [];
                    const silensPre = config.playSilensPre as number | null;
                    const silensPost = config.playSilensPost as number | null;
                    const playTimeout = config.playTimeout as number | null;
                    const menuMeta = (config.menuAudioMeta as { audioId: number; audioName: string; groupId: number }[]) ?? [];
                    const menuSilensPre = config.menuSilensPre as number | null;
                    const menuSilensPost = config.menuSilensPost as number | null;
                    const menuRetries = config.menuRetries as number | null;
                    const menuTimeout = config.menuTimeout as number | null;
                    const menuTimeoutRetries =
                        config.menuTimeoutRetries as number | null;
                    const menuDtmfRetries =
                        (config.menuDtmfRetries as
                            | Record<string, number | null | undefined>
                            | undefined) ?? {};
                    const menuDtmfKeys = (config.menuDtmfKeys as string[] | undefined) ?? [];
                    const allDtmfSelected = menuDtmfKeys.length >= ALL_DTMF_KEYS.length;
                    const hunterQueueIds = (config.hunterQueueIds as number[] | undefined) ?? [];

                    const configIcon =
                        configType === "End"
                            ? "pi pi-stop"
                            : configType === "Menu"
                              ? "pi pi-list"
                              : configType === "AgentHunter"
                                ? "pi pi-database"
                                : configType === "Scheduler"
                                  ? "pi pi-calendar"
                                  : "pi pi-play";

                    const configTitle = (config.startConfigTitle as string | undefined) ?? "";
                    const configTitleTrimmed = configTitle.trim();
                    const configLabelFallback = configType;
                    const configLabel =
                        configType === "End"
                            ? configTitleTrimmed ||
                              ((config.name as string | undefined) ?? "").trim() ||
                              "END"
                            : configTitleTrimmed
                              ? `${configLabelFallback}\n${configTitleTrimmed}`
                              : configLabelFallback;
                    const configAccentColor =
                        configType === "Play"
                            ? "rgb(15, 139, 253)"
                            : configType === "AgentHunter"
                              ? "rgb(255, 152, 0)"
                              : configType === "Scheduler"
                                ? "rgb(76, 175, 80)"
                                : configType === "End"
                                  ? "rgb(76, 175, 80)"
                                  : "#9c27b0";

                    const chainNodes: Node<IVRBlockData>[] = [];
                    const chainEdges: Edge[] = [];
                    const configNode = cleaned.find((n) => n.id === configNodeId);
                    const configX = configNode ? configNode.position.x : baseX;
                    const configY = configNode ? configNode.position.y : baseY + NODE_GAP_VERTICAL;

                    const updatedNodes = cleaned.map((n) =>
                        n.id === configNodeId
                            ? {
                                  ...n,
                                  data: {
                                      ...n.data,
                                      label: configLabel,
                                      blockConfig: {
                                          ...(n.data.blockConfig ?? {}),
                                          ...config,
                                      },
                                      icon: configIcon,
                                      accentColor: configAccentColor,
                                      isGenerated: true,
                                      configParentId: configNodeId,
                                  },
                              }
                            : n
                    );

                    if (configType !== "End") {
                        const srcIsConfigWithChain =
                            configSourceNodeIdForEdges.startsWith(
                                START_CONFIG_PREFIX
                            ) &&
                            cleaned.some((n) =>
                                n.id.startsWith(
                                    `${CHAIN_PREFIX}${configSourceNodeIdForEdges}-`
                                )
                            );
                        if (!srcIsConfigWithChain) {
                            chainEdges.push(
                                makeEdge(
                                    configSourceNodeIdForEdges,
                                    configNodeId
                                )
                            );
                        }
                    }

                    if (configType === "Play") {
                        let lastSourceId = configNodeId;
                        let y = configY;

                        if (silensPre != null) {
                            const preId = `${chainPrefix}silens-pre`;
                            y += IVR_PLAY_CHAIN_VERTICAL_GAP;
                            chainNodes.push({
                                id: preId,
                                type: "ivrBlock",
                                position: { x: configX, y },
                                data: {
                                    label: `Silens Pre\n${silensPre}`,
                                    shape: "square",
                                    icon: "pi pi-clock",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                            chainEdges.push(makeEdge(lastSourceId, preId));
                            lastSourceId = preId;
                        }

                        for (let i = 0; i < meta.length; i++) {
                            const audioId = `${chainPrefix}audio-${i}`;
                            y += IVR_PLAY_CHAIN_VERTICAL_GAP;
                            chainNodes.push({
                                id: audioId,
                                type: "ivrBlock",
                                position: { x: configX, y },
                                data: {
                                    label: wrapLabelToLines(meta[i].audioName, 18, 3),
                                    shape: "triangle",
                                    icon: "pi pi-play",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                            chainEdges.push(makeEdge(lastSourceId, audioId));
                            lastSourceId = audioId;
                        }

                        if (silensPost != null) {
                            const postId = `${chainPrefix}silens-post`;
                            y += IVR_PLAY_CHAIN_VERTICAL_GAP;
                            chainNodes.push({
                                id: postId,
                                type: "ivrBlock",
                                position: { x: configX, y },
                                data: {
                                    label: `Silens Post\n${silensPost}`,
                                    shape: "square",
                                    icon: "pi pi-clock",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                            chainEdges.push(makeEdge(lastSourceId, postId));
                            lastSourceId = postId;
                        }

                        if (playTimeout != null) {
                            const timeoutId = `${chainPrefix}timeout`;
                            y += IVR_PLAY_CHAIN_VERTICAL_GAP;
                            chainNodes.push({
                                id: timeoutId,
                                type: "ivrBlock",
                                position: { x: configX, y },
                                data: {
                                    label: `Timeout\n${playTimeout}`,
                                    shape: "square",
                                    icon: "pi pi-clock",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                            chainEdges.push(makeEdge(lastSourceId, timeoutId));
                            lastSourceId = timeoutId;
                        }

                    }

                    if (configType === "Menu") {
                        let lastSourceId = configNodeId;
                        let y = configY;

                        if (menuSilensPre != null) {
                            const preId = `${chainPrefix}silens-pre`;
                            y += IVR_PLAY_CHAIN_VERTICAL_GAP;
                            chainNodes.push({
                                id: preId,
                                type: "ivrBlock",
                                position: { x: configX, y },
                                data: {
                                    label: `Silens Pre\n${menuSilensPre}`,
                                    shape: "square",
                                    icon: "pi pi-clock",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                            chainEdges.push(makeEdge(lastSourceId, preId));
                            lastSourceId = preId;
                        }

                        for (let i = 0; i < menuMeta.length; i++) {
                            const audioId = `${chainPrefix}audio-${i}`;
                            y += IVR_PLAY_CHAIN_VERTICAL_GAP;
                            chainNodes.push({
                                id: audioId,
                                type: "ivrBlock",
                                position: { x: configX, y },
                                data: {
                                    label: wrapLabelToLines(menuMeta[i].audioName, 18, 3),
                                    shape: "triangle",
                                    icon: "pi pi-play",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                            chainEdges.push(makeEdge(lastSourceId, audioId));
                            lastSourceId = audioId;
                        }

                        if (menuSilensPost != null) {
                            const postId = `${chainPrefix}silens-post`;
                            y += IVR_PLAY_CHAIN_VERTICAL_GAP;
                            chainNodes.push({
                                id: postId,
                                type: "ivrBlock",
                                position: { x: configX, y },
                                data: {
                                    label: `Silens Post\n${menuSilensPost}`,
                                    shape: "square",
                                    icon: "pi pi-clock",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                            chainEdges.push(makeEdge(lastSourceId, postId));
                            lastSourceId = postId;
                        }

                        const dtmfParentId = `${chainPrefix}dtmf`;
                        const dtmfY = y + IVR_MENU_DTMF_VERTICAL_GAP;
                        chainNodes.push({
                            id: dtmfParentId,
                            type: "ivrBlock",
                            position: { x: configX, y: dtmfY },
                            data: {
                                label: "DTMF введення",
                                shape: "square",
                                icon: "pi pi-th-large",
                                accentColor: configAccentColor,
                                isGenerated: true,
                                configParentId: configNodeId,
                            },
                        });

                        const dtmfChildrenLabels: string[] = [
                            ...menuDtmfKeys,
                        ];
                        if (!allDtmfSelected) {
                            dtmfChildrenLabels.push("Others");
                        }
                        if (menuTimeout != null) {
                            dtmfChildrenLabels.push("TimeOut");
                        }
                        const dtmfGap = IVR_MENU_DTMF_GAP_PX;
                        const dtmfTotalWidth = (dtmfChildrenLabels.length - 1) * dtmfGap;
                        const dtmfStartX = configX - dtmfTotalWidth / 2;
                        const dtmfChildY = dtmfY + IVR_MENU_DTMF_VERTICAL_GAP;
                        const dtmfChildIds: string[] = [];
                        for (let i = 0; i < dtmfChildrenLabels.length; i++) {
                            const label = dtmfChildrenLabels[i];
                            const isSelectedKey = menuDtmfKeys.includes(label);
                            const childId = `${chainPrefix}dtmf-${label === "TimeOut" ? "timeout" : label === "Others" ? "others" : label}`;
                            dtmfChildIds.push(childId);
                            chainNodes.push({
                                id: childId,
                                type: "ivrBlock",
                                position: {
                                    x: dtmfStartX + i * dtmfGap,
                                    y: dtmfChildY,
                                },
                                data: {
                                    label,
                                    shape: "square",
                                    icon: isSelectedKey
                                        ? "pi pi-th-large"
                                        : "pi pi-asterisk",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });

                            const x = dtmfStartX + i * dtmfGap;
                            if (isSelectedKey) {
                                const retriesVal = menuDtmfRetries[label];
                                if (retriesVal != null) {
                                    const retriesId = `${chainPrefix}retries-${label}`;
                                    const retriesTrueId = `${chainPrefix}retries-${label}-true`;
                                    const retriesFalseId = `${chainPrefix}retries-${label}-false`;
                                    const branch = ivrMenuRetriesBranchLayout(
                                        x,
                                        dtmfChildY
                                    );
                                    chainNodes.push({
                                        id: retriesId,
                                        type: "ivrBlock",
                                        position: branch.retries,
                                        data: {
                                            label: `Retries\n${retriesVal}`,
                                            shape: "square",
                                            icon: "pi pi-refresh",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainNodes.push({
                                        id: retriesTrueId,
                                        type: "ivrBlock",
                                        position: branch.trueNode,
                                        data: {
                                            label: "true",
                                            shape: "square",
                                            icon: "pi pi-check",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainNodes.push({
                                        id: retriesFalseId,
                                        type: "ivrBlock",
                                        position: branch.falseNode,
                                        data: {
                                            label: "false",
                                            shape: "square",
                                            icon: "pi pi-times",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                }
                            } else if (label === "TimeOut") {
                                if (menuTimeoutRetries != null) {
                                    const retriesId = `${chainPrefix}timeout-retries`;
                                    const retriesTrueId = `${chainPrefix}timeout-retries-true`;
                                    const retriesFalseId = `${chainPrefix}timeout-retries-false`;
                                    const branch = ivrMenuRetriesBranchLayout(
                                        x,
                                        dtmfChildY
                                    );
                                    chainNodes.push({
                                        id: retriesId,
                                        type: "ivrBlock",
                                        position: branch.retries,
                                        data: {
                                            label: `Retries\n${menuTimeoutRetries}`,
                                            shape: "square",
                                            icon: "pi pi-refresh",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainNodes.push({
                                        id: retriesTrueId,
                                        type: "ivrBlock",
                                        position: branch.trueNode,
                                        data: {
                                            label: "true",
                                            shape: "square",
                                            icon: "pi pi-check",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainNodes.push({
                                        id: retriesFalseId,
                                        type: "ivrBlock",
                                        position: branch.falseNode,
                                        data: {
                                            label: "false",
                                            shape: "square",
                                            icon: "pi pi-times",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                }
                            } else if (label === "Others") {
                                if (menuRetries != null) {
                                    const retriesId = `${chainPrefix}others-retries`;
                                    const retriesTrueId = `${chainPrefix}others-retries-true`;
                                    const retriesFalseId = `${chainPrefix}others-retries-false`;
                                    const branch = ivrMenuRetriesBranchLayout(
                                        x,
                                        dtmfChildY
                                    );
                                    chainNodes.push({
                                        id: retriesId,
                                        type: "ivrBlock",
                                        position: branch.retries,
                                        data: {
                                            label: `Retries\n${menuRetries}`,
                                            shape: "square",
                                            icon: "pi pi-refresh",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainNodes.push({
                                        id: retriesTrueId,
                                        type: "ivrBlock",
                                        position: branch.trueNode,
                                        data: {
                                            label: "true",
                                            shape: "square",
                                            icon: "pi pi-check",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainNodes.push({
                                        id: retriesFalseId,
                                        type: "ivrBlock",
                                        position: branch.falseNode,
                                        data: {
                                            label: "false",
                                            shape: "square",
                                            icon: "pi pi-times",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                }
                            }
                        }

                    }

                    if (configType === "Scheduler") {
                        const slotRows = [
                            ...((config.schedulerSlotRows as
                                | IvrSchedulerSlotRow[]
                                | undefined) ?? []),
                        ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
                        const cfgRecord = config as Record<string, unknown>;
                        const schedKind =
                            normalizeIvrSchedulerScheduleKind(
                                cfgRecord.schedulerScheduleKind
                            );
                        const presentationKind =
                            resolveIvrSchedulerPresentationKind(cfgRecord);
                        const schedulerRootH = configNode
                            ? estimateNodeHeight(configNode)
                            : IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT;
                        const y = ivrSchedulerSlotRowY(configY, schedulerRootH);
                        const gap = IVR_SCHEDULER_SIBLING_GAP_PX;

                        if (
                            schedKind === "once" ||
                            presentationKind === "daily"
                        ) {
                            const w = resolveSchedulerOnceExtraWindow(
                                cfgRecord
                            );
                            if (w?.start?.trim() && w?.end?.trim()) {
                                const primaryId = `${chainPrefix}daily-primary`;
                                const primaryLabel = `${trimTimeToHHMM(w.start)} - ${trimTimeToHHMM(w.end)}`;
                                const fullDay = schedulerOnceDailyIsFullDay(
                                    w.start,
                                    w.end
                                );
                                const childCount = fullDay ? 1 : 2;
                                const totalWidth = (childCount - 1) * gap;
                                const startX = configX - totalWidth / 2;

                                chainNodes.push({
                                    id: primaryId,
                                    type: "ivrBlock",
                                    position: { x: startX, y },
                                    data: {
                                        label: primaryLabel,
                                        shape: "square",
                                        icon: "pi pi-calendar",
                                        accentColor: configAccentColor,
                                        isGenerated: true,
                                        outlineDashed: false,
                                        configParentId: configNodeId,
                                    },
                                });
                                chainEdges.push(
                                    makeEdge(configNodeId, primaryId)
                                );

                                if (!fullDay) {
                                    const restId = `${chainPrefix}daily-rest`;
                                    const restLabel = dailyRestRangeLabel(
                                        w.start,
                                        w.end,
                                        w.start2,
                                        w.end2
                                    );
                                    chainNodes.push({
                                        id: restId,
                                        type: "ivrBlock",
                                        position: {
                                            x: startX + gap,
                                            y,
                                        },
                                        data: {
                                            label: restLabel,
                                            shape: "square",
                                            icon: "pi pi-calendar",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            outlineDashed: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainEdges.push(
                                        makeEdge(configNodeId, restId)
                                    );
                                }
                            }
                        } else if (schedKind === "daysweek") {
                            const { nodes: dwNodes, edges: dwEdges } =
                                expandSchedulerDaysweekSimpleChain(
                                    configNodeId,
                                    cfgRecord,
                                    configX,
                                    configY,
                                    configAccentColor
                                );
                            chainNodes.push(...dwNodes);
                            chainEdges.push(...dwEdges);
                        } else if (
                            schedKind === "once_advanced" &&
                            slotRows.length > 0
                        ) {
                            const totalWidth =
                                Math.max(0, slotRows.length - 1) * gap;
                            const startX = configX - totalWidth / 2;
                            for (let si = 0; si < slotRows.length; si++) {
                                const row = slotRows[si];
                                const slotId = `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(slotRows, si)}`;
                                const label = formatSchedulerSlotNodeLabel(
                                    row.daysMask,
                                    row.start,
                                    row.end
                                );
                                chainNodes.push({
                                    id: slotId,
                                    type: "ivrBlock",
                                    position: { x: startX + si * gap, y },
                                    data: {
                                        label: wrapLabelToLines(label, 22, 4),
                                        shape: "square",
                                        icon: "pi pi-calendar",
                                        accentColor: configAccentColor,
                                        isGenerated: true,
                                        configParentId: configNodeId,
                                    },
                                });
                                chainEdges.push(
                                    makeEdge(configNodeId, slotId)
                                );
                            }
                        } else if (slotRows.length > 0) {
                            const useAdvancedTree =
                                schedKind === "daysweek_advanced" &&
                                !schedulerPreferFlatSlotRowChain(slotRows);
                            if (useAdvancedTree) {
                                const { nodes: dwNodes, edges: dwEdges } =
                                    expandSchedulerDaysweekAdvancedChain(
                                        configNodeId,
                                        cfgRecord,
                                        configX,
                                        configY,
                                        configAccentColor
                                    );
                                chainNodes.push(...dwNodes);
                                chainEdges.push(...dwEdges);
                            } else {
                                const needOthers =
                                    schedKind !== "once_advanced" &&
                                    schedulerSlotRowsNeedOthers(slotRows);
                                const childCount =
                                    slotRows.length + (needOthers ? 1 : 0);
                                const totalWidth =
                                    Math.max(0, childCount - 1) * gap;
                                const startX = configX - totalWidth / 2;
                                for (let si = 0; si < slotRows.length; si++) {
                                    const row = slotRows[si];
                                    const slotId = `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(slotRows, si)}`;
                                    const label = formatSchedulerSlotNodeLabel(
                                        row.daysMask,
                                        row.start,
                                        row.end
                                    );
                                    chainNodes.push({
                                        id: slotId,
                                        type: "ivrBlock",
                                        position: { x: startX + si * gap, y },
                                        data: {
                                            label: wrapLabelToLines(label, 22, 4),
                                            shape: "square",
                                            icon: "pi pi-calendar",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainEdges.push(
                                        makeEdge(configNodeId, slotId)
                                    );
                                }
                                if (needOthers) {
                                    const othersId = `${chainPrefix}sched-others`;
                                    chainNodes.push({
                                        id: othersId,
                                        type: "ivrBlock",
                                        position: {
                                            x: startX + slotRows.length * gap,
                                            y,
                                        },
                                        data: {
                                            label: "Others",
                                            shape: "square",
                                            icon: "pi pi-asterisk",
                                            accentColor: configAccentColor,
                                            isGenerated: true,
                                            configParentId: configNodeId,
                                        },
                                    });
                                    chainEdges.push(
                                        makeEdge(configNodeId, othersId)
                                    );
                                }
                            }
                        } else {
                            const days =
                                (config.schedulerDays as
                                    | {
                                          dayOfWeek: number;
                                          enabled: boolean;
                                          start: string;
                                          end: string;
                                      }[]
                                    | undefined) ?? [];
                            const enabledDays = days.filter(
                                (d) => d.enabled && !!d.start && !!d.end
                            );
                            const daySortOrder: Record<number, number> = {
                                1: 0,
                                2: 1,
                                3: 2,
                                4: 3,
                                5: 4,
                                6: 5,
                                0: 6,
                            };
                            const enabledDaysSorted = [...enabledDays].sort(
                                (a, b) =>
                                    (daySortOrder[a.dayOfWeek] ?? 999) -
                                    (daySortOrder[b.dayOfWeek] ?? 999)
                            );
                            const needOthers = schedulerNeedsOthers(enabledDays);
                            const childCount =
                                enabledDaysSorted.length + (needOthers ? 1 : 0);
                            const totalWidth = (childCount - 1) * gap;
                            const startX = configX - totalWidth / 2;
                            for (let i = 0; i < enabledDaysSorted.length; i++) {
                                const d = enabledDaysSorted[i];
                                const id = `${chainPrefix}day-${d.dayOfWeek}`;
                                const label = `${WEEKDAY_ABBREVS[d.dayOfWeek] ?? d.dayOfWeek}\n${d.start} - ${d.end}`;
                                chainNodes.push({
                                    id,
                                    type: "ivrBlock",
                                    position: { x: startX + i * gap, y },
                                    data: {
                                        label,
                                        shape: "square",
                                        icon: "pi pi-calendar",
                                        accentColor: configAccentColor,
                                        isGenerated: true,
                                        configParentId: configNodeId,
                                    },
                                });
                                chainEdges.push(makeEdge(configNodeId, id));
                            }

                            if (needOthers) {
                                const othersId = `${chainPrefix}sched-others`;
                                chainNodes.push({
                                    id: othersId,
                                    type: "ivrBlock",
                                    position: {
                                        x: startX + enabledDaysSorted.length * gap,
                                        y,
                                    },
                                    data: {
                                        label: "Others",
                                        shape: "square",
                                        icon: "pi pi-asterisk",
                                        accentColor: configAccentColor,
                                        isGenerated: true,
                                        configParentId: configNodeId,
                                    },
                                });
                                chainEdges.push(makeEdge(configNodeId, othersId));
                            }
                        }
                    }

                    if (configType === "AgentHunter") {
                        const enabled = hunterQueueIds.filter(
                            (q) => Number.isFinite(q) && q > 0
                        );
                        const queueChildY = ivrAgentHunterQueueChildY(
                            configY,
                            configNode
                                ? estimateNodeHeight(configNode)
                                : IVR_SCHEDULER_ROOT_DEFAULT_HEIGHT
                        );
                        const queueMeta =
                            (config.hunterQueueMeta as
                                | { queueId: number; queueName: string }[]
                                | undefined) ?? [];
                        if (enabled.length > 0) {
                            const qId = enabled[0]!;
                            const queueName = queueMeta.find(
                                (m) => m.queueId === qId
                            )?.queueName;
                            const label =
                                queueName != null &&
                                String(queueName).trim() !== ""
                                    ? `ID ${qId}\n${String(queueName).trim()}\nPlayBack`
                                    : `ID ${qId}\nQueue\nPlayBack`;
                            const id = ivrOperatorQueueChainNodeId(configNodeId);
                            chainNodes.push({
                                id,
                                type: "ivrBlock",
                                position: { x: configX, y: queueChildY },
                                data: {
                                    label,
                                    shape: "square",
                                    icon: "pi pi-database",
                                    accentColor: configAccentColor,
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                        } else {
                            const endId = `${chainPrefix}end`;
                            chainNodes.push({
                                id: endId,
                                type: "ivrBlock",
                                position: { x: configX, y: queueChildY + NODE_GAP_VERTICAL },
                                data: {
                                    label: "end",
                                    shape: "circle",
                                    icon: "pi pi-stop",
                                    accentColor: "rgb(76, 175, 80)",
                                    isGenerated: true,
                                    configParentId: configNodeId,
                                },
                            });
                        }
                    }

                    return [...updatedNodes, ...chainNodes];
                });

                setEdges((eds) => {
                    const chainPrefix = `${CHAIN_PREFIX}${configNodeId}-`;

                    const endId = `${chainPrefix}end`;

                    const configType = (config.startConfiguration as string) || "Play";
                    const meta = (config.playAudioMeta as { audioId: number; audioName: string; groupId: number }[]) ?? [];
                    const silensPre = config.playSilensPre as number | null;
                    const silensPost = config.playSilensPost as number | null;
                    const playTimeout = config.playTimeout as number | null;
                    const menuMeta = (config.menuAudioMeta as { audioId: number; audioName: string; groupId: number }[]) ?? [];
                    const menuSilensPre = config.menuSilensPre as number | null;
                    const menuSilensPost = config.menuSilensPost as number | null;
                    const menuRetries = config.menuRetries as number | null;
                    const menuTimeout = config.menuTimeout as number | null;
                    const menuTimeoutRetries =
                        config.menuTimeoutRetries as number | null;
                    const menuDtmfRetries =
                        (config.menuDtmfRetries as
                            | Record<string, number | null | undefined>
                            | undefined) ?? {};
                    const menuDtmfKeys = (config.menuDtmfKeys as string[] | undefined) ?? [];
                    const allDtmfSelected = menuDtmfKeys.length >= ALL_DTMF_KEYS.length;
                    const hunterQueueIds = (config.hunterQueueIds as number[] | undefined) ?? [];

                    const configHasChainSubBlocks = (() => {
                        if (configType === "Play") {
                            return meta.length > 0 || silensPre != null || silensPost != null || playTimeout != null;
                        }
                        if (configType === "Menu" || configType === "Scheduler") return true;
                        if (configType === "AgentHunter") {
                            return hunterQueueIds.some((q) => Number.isFinite(q) && q > 0);
                        }
                        return false;
                    })();

                    const detachedPlayChainOutEdges: Edge[] = [];
                    const detachedMenuChainOutEdges: Edge[] = [];
                    const schedulerOutboundSources =
                        configType === "Scheduler"
                            ? collectIvrSchedulerOutboundLinkSourceIds(
                                  configNodeId,
                                  config as Record<string, unknown>
                              )
                            : null;

                    const cleaned = eds.filter((e) => {
                        if (
                            e.source === configNodeId &&
                            e.target !== configNodeId &&
                            !e.target.startsWith(chainPrefix)
                        ) {
                            return false;
                        }

                        const srcChain = e.source.startsWith(chainPrefix);
                        const tgtChain = e.target.startsWith(chainPrefix);
                        const isStartToConfig =
                            e.source === configSourceNodeIdForEdges &&
                            e.target === configNodeId;

                        if (
                            srcChain &&
                            e.target.startsWith(START_CONFIG_PREFIX) &&
                            e.target !== configNodeId
                        ) {
                            if (
                                configType === "Scheduler" &&
                                e.source.includes(`${chainPrefix}sched-slot-`)
                            ) {
                                if (
                                    !(
                                        schedulerOutboundSources?.has(
                                            e.source
                                        ) ?? false
                                    )
                                ) {
                                    return false;
                                }
                                const elIdRaw = config.elementId;
                                const elId =
                                    typeof elIdRaw === "number"
                                        ? elIdRaw
                                        : Number(elIdRaw);
                                const slotRows = [
                                    ...((config.schedulerSlotRows as
                                        | IvrSchedulerSlotRow[]
                                        | undefined) ?? []),
                                ].sort(
                                    (a, b) =>
                                        (a.priority ?? 0) - (b.priority ?? 0)
                                );
                                const slotTail = e.source.match(
                                    /sched-slot-(.+)$/
                                )?.[1];
                                const si = slotTail
                                    ? ivrSchedulerSlotRowIndexFromSlotSuffix(
                                          slotTail,
                                          slotRows
                                      )
                                    : -1;
                                const row =
                                    si >= 0 && si < slotRows.length
                                        ? slotRows[si]!
                                        : null;
                                const scheds = ivrFullRef.current
                                    ?.schedules as IvrApiSchedule[] | undefined;
                                if (
                                    row == null ||
                                    !Number.isFinite(elId) ||
                                    elId <= 0 ||
                                    !schedulerSlotRowIsPersistedInApi(
                                        scheds,
                                        elId,
                                        row
                                    )
                                ) {
                                    return false;
                                }
                            }
                            if (configType === "Play") {
                                if (
                                    isIvrPlayChainAudioLaneEdgeSource(
                                        chainPrefix,
                                        e.source
                                    )
                                ) {
                                    detachedPlayChainOutEdges.push(e);
                                }
                                return false;
                            }
                            if (configType === "Menu") {
                                if (
                                    isIvrMenuChainAudioLaneEdgeSource(
                                        chainPrefix,
                                        e.source
                                    )
                                ) {
                                    detachedMenuChainOutEdges.push(e);
                                    return false;
                                }
                                if (
                                    isIvrMenuDtmfSubtreeOutboundSource(
                                        chainPrefix,
                                        e.source
                                    )
                                ) {
                                    return true;
                                }
                                return false;
                            }
                            if (configType === "Scheduler") {
                                return (
                                    schedulerOutboundSources?.has(e.source) ??
                                    false
                                );
                            }
                            if (configType === "AgentHunter") {
                                return isIvrAgentHunterChainLeafSource(
                                    chainPrefix,
                                    e.source
                                );
                            }
                            if (configType === "End") {
                                return false;
                            }
                            return true;
                        }

                        if (srcChain || tgtChain) return false;

                        if (isStartToConfig) return false;

                        if (
                            configHasChainSubBlocks &&
                            e.source === configNodeId &&
                            e.target.startsWith(START_CONFIG_PREFIX) &&
                            e.target !== configNodeId
                        ) {
                            return false;
                        }

                        return true;
                    });

                    const chainEdges: Edge[] = [];

                    const sourceIsConfigWithChain =
                        configSourceNodeIdForEdges.startsWith(START_CONFIG_PREFIX) &&
                        nodesRef.current.some((n) =>
                            n.id.startsWith(
                                `${CHAIN_PREFIX}${configSourceNodeIdForEdges}-`
                            )
                        );
                    if (!sourceIsConfigWithChain) {
                        chainEdges.push(
                            makeEdge(configSourceNodeIdForEdges, configNodeId)
                        );
                    }

                    if (configType === "Play") {
                        let lastSourceId = configNodeId;

                        if (silensPre != null) {
                            const preId = `${chainPrefix}silens-pre`;
                            chainEdges.push(makeEdge(lastSourceId, preId));
                            lastSourceId = preId;
                        }

                        for (let i = 0; i < meta.length; i++) {
                            const audioId = `${chainPrefix}audio-${i}`;
                            chainEdges.push(makeEdge(lastSourceId, audioId));
                            lastSourceId = audioId;
                        }

                        if (silensPost != null) {
                            const postId = `${chainPrefix}silens-post`;
                            chainEdges.push(makeEdge(lastSourceId, postId));
                            lastSourceId = postId;
                        }

                        if (playTimeout != null) {
                            const timeoutId = `${chainPrefix}timeout`;
                            chainEdges.push(makeEdge(lastSourceId, timeoutId));
                            lastSourceId = timeoutId;
                        }

                        const reattachSeen = new Set<string>();
                        for (const out of detachedPlayChainOutEdges) {
                            if (reattachSeen.has(out.target)) continue;
                            reattachSeen.add(out.target);
                            chainEdges.push(
                                makeEdge(
                                    lastSourceId,
                                    out.target,
                                    out.sourceHandle ?? "bottom-source",
                                    out.targetHandle ?? "top",
                                    (out.data as Record<string, unknown> | undefined) ??
                                        null
                                )
                            );
                        }

                    }

                    if (configType === "Menu") {
                        let lastSourceId = configNodeId;

                        if (menuSilensPre != null) {
                            const preId = `${chainPrefix}silens-pre`;
                            chainEdges.push(makeEdge(lastSourceId, preId));
                            lastSourceId = preId;
                        }

                        for (let i = 0; i < menuMeta.length; i++) {
                            const audioId = `${chainPrefix}audio-${i}`;
                            chainEdges.push(makeEdge(lastSourceId, audioId));
                            lastSourceId = audioId;
                        }

                        if (menuSilensPost != null) {
                            const postId = `${chainPrefix}silens-post`;
                            chainEdges.push(makeEdge(lastSourceId, postId));
                            lastSourceId = postId;
                        }

                        const tailBeforeDtmf = lastSourceId;

                        const dtmfParentId = `${chainPrefix}dtmf`;
                        const dtmfChildrenLabels: string[] = [...menuDtmfKeys];
                        if (!allDtmfSelected) dtmfChildrenLabels.push("Others");
                        if (menuTimeout != null) dtmfChildrenLabels.push("TimeOut");
                        const dtmfChildIds = dtmfChildrenLabels.map((label) =>
                            `${chainPrefix}dtmf-${
                                label === "TimeOut"
                                    ? "timeout"
                                    : label === "Others"
                                      ? "others"
                                      : label
                            }`
                        );

                        const timeoutId =
                            menuTimeout != null
                                ? `${chainPrefix}dtmf-timeout`
                                : null;
                        const othersId = !allDtmfSelected
                            ? `${chainPrefix}dtmf-others`
                            : null;

                        chainEdges.push(makeEdge(lastSourceId, dtmfParentId));

                        for (const cId of dtmfChildIds) {
                            chainEdges.push(makeEdge(dtmfParentId, cId));
                        }

                        // Connect each dtmf key node to its own retries decision
                        for (const key of menuDtmfKeys) {
                            const retriesVal = menuDtmfRetries[key];
                            if (retriesVal == null) continue;
                            const dtmfId = `${chainPrefix}dtmf-${key}`;
                            const retriesId = `${chainPrefix}retries-${key}`;
                            const retriesTrueId = `${chainPrefix}retries-${key}-true`;
                            const retriesFalseId = `${chainPrefix}retries-${key}-false`;
                            chainEdges.push(makeEdge(dtmfId, retriesId));
                            chainEdges.push(makeEdge(retriesId, retriesTrueId));
                            chainEdges.push(makeEdge(retriesId, retriesFalseId));
                        }

                        if (timeoutId && menuTimeoutRetries != null) {
                            const retriesId = `${chainPrefix}timeout-retries`;
                            const retriesTrueId = `${chainPrefix}timeout-retries-true`;
                            const retriesFalseId = `${chainPrefix}timeout-retries-false`;
                            chainEdges.push(makeEdge(timeoutId, retriesId));
                            chainEdges.push(makeEdge(retriesId, retriesTrueId));
                            chainEdges.push(makeEdge(retriesId, retriesFalseId));
                        }

                        if (othersId && menuRetries != null) {
                            const retriesId = `${chainPrefix}others-retries`;
                            const retriesTrueId = `${chainPrefix}others-retries-true`;
                            const retriesFalseId = `${chainPrefix}others-retries-false`;
                            chainEdges.push(makeEdge(othersId, retriesId));
                            chainEdges.push(makeEdge(retriesId, retriesTrueId));
                            chainEdges.push(makeEdge(retriesId, retriesFalseId));
                        }

                        const menuReattachSeen = new Set<string>();
                        for (const out of detachedMenuChainOutEdges) {
                            if (menuReattachSeen.has(out.target)) continue;
                            menuReattachSeen.add(out.target);
                            chainEdges.push(
                                makeEdge(
                                    tailBeforeDtmf,
                                    out.target,
                                    out.sourceHandle ?? "bottom-source",
                                    out.targetHandle ?? "top",
                                    (out.data as Record<string, unknown> | undefined) ??
                                        null
                                )
                            );
                        }
                    }

                    if (configType === "Scheduler") {
                        const slotRows = [
                            ...((config.schedulerSlotRows as
                                | IvrSchedulerSlotRow[]
                                | undefined) ?? []),
                        ].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
                        const cfgRecordEdges = config as Record<string, unknown>;
                        const schedKindEdges =
                            normalizeIvrSchedulerScheduleKind(
                                cfgRecordEdges.schedulerScheduleKind
                            );
                        const presentationKind =
                            resolveIvrSchedulerPresentationKind(cfgRecordEdges);
                        if (
                            schedKindEdges === "once" ||
                            presentationKind === "daily"
                        ) {
                            const w = resolveSchedulerOnceExtraWindow(
                                cfgRecordEdges
                            );
                            if (w?.start?.trim() && w?.end?.trim()) {
                                chainEdges.push(
                                    makeEdge(
                                        configNodeId,
                                        `${chainPrefix}daily-primary`
                                    )
                                );
                                if (
                                    !schedulerOnceDailyIsFullDay(w.start, w.end)
                                ) {
                                    chainEdges.push(
                                        makeEdge(
                                            configNodeId,
                                            `${chainPrefix}daily-rest`
                                        )
                                    );
                                }
                            }
                        } else if (schedKindEdges === "daysweek") {
                            const { edges: dwEdges } =
                                expandSchedulerDaysweekSimpleChain(
                                    configNodeId,
                                    cfgRecordEdges,
                                    0,
                                    0,
                                    ""
                                );
                            chainEdges.push(...dwEdges);
                        } else if (
                            schedKindEdges === "once_advanced" &&
                            slotRows.length > 0
                        ) {
                            for (let si = 0; si < slotRows.length; si++) {
                                const slotId = `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(slotRows, si)}`;
                                chainEdges.push(
                                    makeEdge(configNodeId, slotId)
                                );
                            }
                        } else if (slotRows.length > 0) {
                            if (schedKindEdges === "daysweek_advanced") {
                                const { edges: dwEdges } =
                                    expandSchedulerDaysweekAdvancedChain(
                                        configNodeId,
                                        cfgRecordEdges,
                                        0,
                                        0,
                                        ""
                                    );
                                chainEdges.push(...dwEdges);
                            } else {
                                for (let si = 0; si < slotRows.length; si++) {
                                    const slotId = `${chainPrefix}sched-slot-${ivrSchedulerSlotNodeIdSuffix(slotRows, si)}`;
                                    chainEdges.push(
                                        makeEdge(configNodeId, slotId)
                                    );
                                }
                                const needOthersEdges =
                                    schedKindEdges !== "once_advanced" &&
                                    schedulerSlotRowsNeedOthers(slotRows);
                                if (needOthersEdges) {
                                    chainEdges.push(
                                        makeEdge(
                                            configNodeId,
                                            `${chainPrefix}sched-others`
                                        )
                                    );
                                }
                            }
                        } else {
                            const enabledDays = (
                                (config.schedulerDays as any[] | undefined) ?? []
                            ).filter((d) => d?.enabled && d?.start && d?.end);
                            const daySortOrder: Record<number, number> = {
                                1: 0, // Monday
                                2: 1,
                                3: 2,
                                4: 3,
                                5: 4,
                                6: 5,
                                0: 6, // Sunday
                            };
                            const enabledDaysSorted = [...enabledDays].sort(
                                (a, b) =>
                                    (daySortOrder[a.dayOfWeek] ?? 999) -
                                    (daySortOrder[b.dayOfWeek] ?? 999)
                            );
                            for (const d of enabledDaysSorted) {
                                const id = `${chainPrefix}day-${d.dayOfWeek}`;
                                chainEdges.push(makeEdge(configNodeId, id));
                            }

                            if (schedulerNeedsOthers(enabledDaysSorted)) {
                                const othersId = `${chainPrefix}sched-others`;
                                chainEdges.push(makeEdge(configNodeId, othersId));
                            }
                        }
                    }

                    if (configType === "AgentHunter") {
                        const hasQueue = hunterQueueIds.some(
                            (q) => Number.isFinite(q) && q > 0
                        );
                        if (hasQueue) {
                            chainEdges.push(
                                makeEdge(
                                    configNodeId,
                                    ivrOperatorQueueChainNodeId(configNodeId)
                                )
                            );
                        } else {
                            chainEdges.push(makeEdge(configNodeId, endId));
                        }
                    }

                    const nextEdges = [...cleaned, ...chainEdges];
                    const nodeIdsForPrune = new Set(
                        nodesRef.current
                            .filter((n) => !n.id.startsWith(chainPrefix))
                            .map((n) => n.id)
                    );
                    nodeIdsForPrune.add(configNodeId);
                    if (schedulerOutboundSources) {
                        Array.from(schedulerOutboundSources).forEach((id) => {
                            nodeIdsForPrune.add(id);
                        });
                    }
                    for (const ce of chainEdges) {
                        nodeIdsForPrune.add(ce.source);
                        nodeIdsForPrune.add(ce.target);
                    }
                    const prunedNextEdges = nextEdges.filter(
                        (e) =>
                            nodeIdsForPrune.has(e.source) &&
                            nodeIdsForPrune.has(e.target)
                    );
                    const nextEdgeIdSet = new Set(
                        prunedNextEdges.map((e) => e.id)
                    );
                    const droppedEdges = eds.filter(
                        (e) => !nextEdgeIdSet.has(e.id)
                    );

                    const jwtInner = jwtTokenRef.current ?? undefined;
                    const mayDeleteStaleRoutes =
                        mayOrchestrateIvrRouteDeleteRef.current &&
                        jwtInner &&
                        droppedEdges.length > 0;

                    if (mayDeleteStaleRoutes) {
                        const nodesForRouteResolve =
                            nodesRef.current.map((n) =>
                                n.id === configNodeId
                                    ? {
                                          ...n,
                                          data: {
                                              ...n.data,
                                              blockConfig: {
                                                  ...(n.data.blockConfig ??
                                                      {}),
                                                  ...config,
                                              },
                                          },
                                      }
                                    : n
                            );
                        const routesSnap =
                            (ivrFullRef.current?.routes ?? []) as IvrApiRoute[];
                        const entrySnap =
                            (
                                ivrFullRef.current?.configuration as {
                                    entryElementId?: number | null;
                                } | undefined
                            )?.entryElementId ?? null;
                        const elementsSnap =
                            (ivrFullRef.current?.elements ??
                                []) as IvrApiElement[];

                        const nextContinuationKeys = new Set<string>();
                        for (const e of nextEdges) {
                            const k = ivrRouteContinuationKeyForEdge(
                                e,
                                nodesForRouteResolve,
                                entrySnap,
                                elementsSnap
                            );
                            if (k) nextContinuationKeys.add(k);
                        }

                        const routeIdsPending = new Set<number>();
                        for (const edge of droppedEdges) {
                            const k = ivrRouteContinuationKeyForEdge(
                                edge,
                                nodesForRouteResolve,
                                entrySnap,
                                elementsSnap
                            );
                            if (!k || nextContinuationKeys.has(k)) continue;

                            const rid = resolveRouteIdForConfigurableEdgeDelete(
                                edge,
                                nodesForRouteResolve,
                                routesSnap,
                                entrySnap,
                                elementsSnap
                            );
                            if (
                                rid != null &&
                                Number.isFinite(rid) &&
                                rid > 0
                            ) {
                                routeIdsPending.add(rid);
                            }
                        }

                        if (routeIdsPending.size > 0) {
                            void (async () => {
                                const removedOk = new Set<number>();
                                try {
                                    for (const rid of Array.from(routeIdsPending)) {
                                        try {
                                            await deleteIvrConfigRoute(
                                                rid,
                                                jwtInner
                                            );
                                            removedOk.add(rid);
                                        } catch {
                                            dispatch(showError());
                                        }
                                    }
                                    if (removedOk.size > 0) {
                                        const prev = ivrFullRef.current;
                                        const prevRoutes =
                                            (prev?.routes ?? []) as IvrApiRoute[];
                                        const prevElements =
                                            (prev?.elements ?? []) as IvrApiElement[];
                                        const prevSchedulesLive =
                                            (prev?.schedules ??
                                                []) as IvrApiSchedule[];
                                        dispatch(
                                            setIvrSchemeGraphApiSlice({
                                                elements: prevElements,
                                                routes: prevRoutes.filter((r) => {
                                                    const id = ivrRouteRowApiId(r);
                                                    return (
                                                        id == null ||
                                                        !removedOk.has(id)
                                                    );
                                                }),
                                                schedules: prevSchedulesLive,
                                                configurationPatch: {
                                                    entryElementId:
                                                        (
                                                            prev?.configuration as {
                                                                entryElementId?:
                                                                    | number
                                                                    | null;
                                                            } | undefined
                                                        )?.entryElementId ?? null,
                                                },
                                            })
                                        );
                                    }
                                } catch {
                                    dispatch(showError());
                                }
                            })();
                        }
                    }

                    return prunedNextEdges;
                });
            },
            []
        );

        useEffect(() => {
            setEdges((eds) => {
                const nodeIds = new Set(nodes.map((n) => n.id));
                const pruned = eds.filter(
                    (e) => nodeIds.has(e.source) && nodeIds.has(e.target)
                );
                return pruned.length === eds.length ? eds : pruned;
            });
        }, [nodes]);

        useEffect(() => {
            if (!requestVerticalLayoutRef.current) return;
            requestVerticalLayoutRef.current = false;
            const preserveY = ivrLayoutPreserveYRef.current;
            ivrLayoutPreserveYRef.current = false;
            const layouted = getLayoutedElements(nodes, edges, "TB", { preserveY });
            setNodes([...layouted.nodes]);
            setEdges([...layouted.edges]);
            setTimeout(() => {
                rfInstanceRef.current?.fitView({ padding: 0.2, duration: 500 });
            }, 0);
        }, [nodes, edges]);

        useEffect(() => {
            const outgoingNodeIds = new Set<string>(edges.map((e) => e.source));

            setNodes((nds) => {
                let changed = false;

                const next = nds.map((n) => {
                    const isLeaf = !outgoingNodeIds.has(n.id);

                    if (n.id === BASE_NODE_ID) {
                        const expectedHasPlus = canMutateIVRScheme;
                        const currentHasPlus = !!n.data.onAddNode;
                        if (
                            currentHasPlus === expectedHasPlus &&
                            n.data.onDeleteNode === undefined
                        ) {
                            return n;
                        }
                        changed = true;
                        return {
                            ...n,
                            data: {
                                ...n.data,
                                onAddNode: expectedHasPlus ? () => createNewStartConfig() : undefined,
                                onDeleteNode: undefined,
                            },
                        };
                    }

                    const isGenerated = !!n.data.isGenerated;
                    if (isGenerated) {
                        const isEnd = isIvrEndBlockData(n.data);

                        const isStartConfigNode = n.id.startsWith(START_CONFIG_PREFIX);
                        const isUnsavedStartConfigPlaceholder =
                            isStartConfigNode &&
                            !String(n.data.label ?? "").trim() &&
                            !String(n.data.icon ?? "").trim();
                        const isSavedStartConfig =
                            isStartConfigNode && !isUnsavedStartConfigPlaceholder;

                        const alwaysDisabled = isEnd;
                        const expectedHasPlus =
                            canMutateIVRScheme &&
                            !isStartConfigNode &&
                            !isUnsavedStartConfigPlaceholder &&
                            isLeaf &&
                            !alwaysDisabled;
                        const expectedDeleteFn =
                            hasIVRConfigurationDeletePermission &&
                            (isUnsavedStartConfigPlaceholder || isSavedStartConfig)
                                ? requestDelete
                                : undefined;
                        const currentHasPlus = !!n.data.onAddNode;

                        if (
                            currentHasPlus === expectedHasPlus &&
                            n.data.onDeleteNode === expectedDeleteFn
                        ) {
                            return n;
                        }

                        changed = true;
                        return {
                            ...n,
                            data: {
                                ...n.data,
                                onAddNode: expectedHasPlus
                                    ? () => createNewStartConfigFrom(n.id)
                                    : undefined,
                                onDeleteNode: expectedDeleteFn,
                            },
                        };
                    }
                    const isStartCfgLeaf = n.id.startsWith(START_CONFIG_PREFIX);
                    const expectedHasPlus =
                        canMutateIVRScheme &&
                        isLeaf &&
                        !isStartCfgLeaf &&
                        !isIvrEndBlockData(n.data);
                    const currentHasPlus = !!n.data.onAddNode;
                    if (
                        currentHasPlus === expectedHasPlus &&
                        n.data.onDeleteNode === undefined
                    ) {
                        return n;
                    }

                    changed = true;
                    return {
                        ...n,
                        data: {
                            ...n.data,
                            onAddNode: expectedHasPlus ? () => createNewStartConfigFrom(n.id) : undefined,
                            onDeleteNode: undefined,
                        },
                    };
                });

                return changed ? next : nds;
            });
        }, [
            nodes,
            edges,
            addNodeFromBlock,
            canMutateIVRScheme,
            requestDelete,
            createNewStartConfig,
            createNewStartConfigFrom,
            hasIVRConfigurationDeletePermission,
        ]);

        const updateNodeData = useCallback((nodeId: string, blockConfig: IVRBlockConfig) => {
            setNodes((nds) =>
                nds.map((n) =>
                    n.id === nodeId
                        ? {
                              ...n,
                              data: {
                                  ...n.data,
                                  blockConfig: {
                                      ...n.data.blockConfig,
                                      ...blockConfig,
                                  },
                              },
                          }
                        : n
                )
            );
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                addNode,
                updateNodeData,
                generateStartConfigChain,
                getNodeById: (nodeId: string) =>
                    nodesRef.current.find((n) => n.id === nodeId),
                collectPositiveElementIds: () => {
                    const out: number[] = [];
                    for (const n of nodesRef.current) {
                        if (isIvrEndBlockData(n.data)) {
                            continue;
                        }
                        const raw = n.data?.blockConfig?.elementId;
                        const x =
                            typeof raw === "number" ? raw : Number(raw);
                        if (Number.isFinite(x) && x > 0) {
                            out.push(x);
                        }
                    }
                    return out;
                },
                collectEndElementPositiveIds: () => {
                    const out: number[] = [];
                    for (const n of nodesRef.current) {
                        if (!isIvrEndBlockData(n.data)) {
                            continue;
                        }
                        const raw = n.data?.blockConfig?.elementId;
                        const x =
                            typeof raw === "number" ? raw : Number(raw);
                        if (Number.isFinite(x) && x > 0) {
                            out.push(x);
                        }
                    }
                    return out;
                },
                getSchedulerJumpTargetElementId: (configNodeId: string) =>
                    resolveSchedulerJumpTargetElementIdFromGraph(
                        configNodeId,
                        nodesRef.current,
                        edgesRef.current
                    ),
                playScheme,
                playSchemeFromNode,
                pauseSchemePlayback,
                resumeSchemePlayback,
                stopSchemePlayback,
            }),
            [
                addNode,
                updateNodeData,
                generateStartConfigChain,
                playScheme,
                playSchemeFromNode,
                pauseSchemePlayback,
                resumeSchemePlayback,
                stopSchemePlayback,
            ]
        );

        const handleNodeClick = useCallback(
            (_: React.MouseEvent, node: Node<IVRBlockData>) => {
                if (node.data.isGenerated && node.data.configParentId) {
                    setNodes((nds) => {
                        const configNode = nds.find(
                            (n) => n.id === node.data.configParentId
                        );
                        if (configNode) {
                            setTimeout(() => onSelectedNodeChange?.(configNode), 0);
                        }
                        return nds;
                    });
                } else {
                    onSelectedNodeChange?.(node);
                }
            },
            [onSelectedNodeChange]
        );

        const onConnect = useCallback(
            async (params: Connection) => {
                if (!canMutateIVRScheme) {
                    return;
                }
                const data = ivrFullRef.current;
                const cfg = data?.configuration as
                    | { flowId?: number | null; entryElementId?: number | null }
                    | undefined;
                const flowId = cfg?.flowId != null ? Number(cfg.flowId) : NaN;
                const jwt = jwtToken?.jwtToken;
                if (!Number.isFinite(flowId) || flowId <= 0 || !jwt) {
                    dispatch(showError());
                    return;
                }
                const nds = nodesRef.current;
                const elements = data?.elements as IvrApiElement[] | undefined;
                const toEl = resolveRouteTargetElementId(params.target, nds);
                if (toEl == null || toEl <= 0) {
                    dispatch(showError());
                    return;
                }
                if (
                    edges.some(
                        (e) =>
                            e.source === params.source &&
                            e.target === params.target
                    )
                ) {
                    return;
                }

                if (params.source === BASE_NODE_ID) {
                    if (!Number.isFinite(toEl) || toEl < 0) {
                        dispatch(showError());
                        return;
                    }
                    const conf = cfg as
                        | {
                              name?: string | null;
                              description?: string | null;
                              branchId?: number | null;
                          }
                        | undefined;
                    try {
                        await putIvrConfigConfiguration(
                            {
                                flowId,
                                entryElementId: toEl,
                                name: conf?.name ?? null,
                                description: conf?.description ?? null,
                                branchId: conf?.branchId ?? null,
                            },
                            jwt
                        );
                        dispatch(getIVRConfigurationFullById(flowId, jwt));
                    } catch {
                        dispatch(showError());
                    }
                    requestVerticalLayoutRef.current = true;
                    return;
                }

                const fromEl = resolveRouteSourceElementId(
                    params.source,
                    nds,
                    cfg?.entryElementId,
                    elements
                );
                const fromOk = fromEl != null && fromEl > 0;
                if (!fromOk) {
                    dispatch(showError());
                    return;
                }
                const srcNode = nds.find((n) => n.id === params.source);
                const parentId = srcNode?.data?.configParentId;
                const parentCfgNode = parentId
                    ? nds.find((n) => n.id === parentId)
                    : null;
                const isScheduler =
                    parentCfgNode?.data?.blockConfig
                        ?.startConfiguration === "Scheduler" ||
                    parentCfgNode?.data?.blockConfig?.elementType ===
                        "SCHEDULER";

                const isSchedulerOthersChainLeaf =
                    isScheduler &&
                    params.source != null &&
                    params.source.endsWith("-sched-others");

                if (isScheduler && parentCfgNode && !isSchedulerOthersChainLeaf) {
                    try {
                        const schedCfg = parentCfgNode.data
                            .blockConfig as Record<string, unknown>;
                        const allRows =
                            buildSchedulerSlotRowsFromBlockConfig(
                                schedCfg,
                                toEl
                            );

                        const slotPrefix = `${CHAIN_PREFIX}${parentId}-sched-slot-`;
                        let matchedRow: typeof allRows[number] | null =
                            null;
                        const dayDow =
                            params.source != null
                                ? ivrSchedulerWeekdayChainDayOfWeek(
                                      params.source
                                  )
                                : null;
                        if (dayDow != null) {
                            const mask =
                                schedulerDayOfWeekToDaysMask(dayDow);
                            const hit = allRows.find(
                                (r) =>
                                    (Number(r.daysMask) || 0) === mask
                            );
                            if (hit) {
                                matchedRow = {
                                    ...hit,
                                    activeToId: toEl,
                                };
                            }
                        } else if (
                            params.source &&
                            params.source.startsWith(slotPrefix)
                        ) {
                            const tail =
                                params.source.slice(slotPrefix.length);
                            const si = ivrSchedulerSlotRowIndexFromSlotSuffix(
                                tail,
                                allRows
                            );
                            if (si >= 0 && si < allRows.length) {
                                matchedRow = {
                                    ...allRows[si]!,
                                    activeToId: toEl,
                                };
                            }
                        } else if (
                            params.source.endsWith("-daily-primary") &&
                            allRows.length >= 1
                        ) {
                            matchedRow = {
                                ...allRows[0]!,
                                activeToId: toEl,
                            };
                        } else if (
                            params.source.endsWith("-daily-rest") &&
                            allRows.length >= 2
                        ) {
                            matchedRow = {
                                ...allRows[1]!,
                                activeToId: toEl,
                            };
                        }

                        const schedKind = normalizeIvrSchedulerScheduleKind(
                            schedCfg.schedulerScheduleKind
                        );
                        const deferScheduleUntilBlockLink =
                            schedKind === "daysweek" ||
                            schedKind === "once_advanced" ||
                            schedKind === "daysweek_advanced";
                        const schedRows = matchedRow
                            ? [matchedRow]
                            : deferScheduleUntilBlockLink
                              ? []
                              : allRows.length === 1
                                ? allRows.map((r) => ({
                                      ...r,
                                      activeToId: toEl,
                                  }))
                                : [];
                        const schedRowsForApi = filterIvrSchedulerSlotRowsForScheduleApi(
                            schedKind,
                            schedRows
                        );

                        const updatedSchedules = [
                            ...((data?.schedules ?? []) as IvrApiSchedule[]),
                        ];
                        const serverSchedSnap = (
                            (data as Record<string, unknown> | undefined)
                                ?._ivrSchedulesServerSnapshot ?? []
                        ) as IvrApiSchedule[];
                        const apiSchedules = slotRowsToSchedules(
                            fromEl,
                            flowId,
                            schedRowsForApi
                        );
                        let nextPrio = nextIvrSchedulePostPriorityFromSources(
                            fromEl,
                            updatedSchedules,
                            serverSchedSnap
                        );
                        for (const s of apiSchedules) {
                            const inactiveHit =
                                findInactiveSchedulerSlotMatchingPostCandidate(
                                    fromEl,
                                    s,
                                    [updatedSchedules, serverSchedSnap]
                                );
                            const hitId = inactiveHit
                                ? ivrRawScheduleRowId(inactiveHit)
                                : -1;
                            if (inactiveHit && hitId > 0) {
                                const schedPut = await ivrConfigMutationRequest(
                                    dispatch,
                                    putIvrConfigSchedule(
                                        {
                                            id: hitId,
                                            daysMask: s.daysMask,
                                            fromTime: s.fromTime,
                                            toTime: s.toTime,
                                            activeToId: s.activeToId,
                                            isActive: true,
                                        },
                                        jwt
                                    )
                                );
                                if (schedPut === false) {
                                    return;
                                }
                                const putRow: IvrApiSchedule = {
                                    ...inactiveHit,
                                    id: hitId,
                                    flowId: s.flowId ?? flowId,
                                    elementId: s.elementId,
                                    priority:
                                        inactiveHit.priority ?? s.priority,
                                    daysMask: s.daysMask,
                                    fromTime: s.fromTime,
                                    toTime: s.toTime,
                                    activeToId: s.activeToId,
                                    isActive: true,
                                };
                                const ix = updatedSchedules.findIndex(
                                    (x) =>
                                        ivrRawScheduleRowId(x) === hitId
                                );
                                if (ix >= 0)
                                    updatedSchedules[ix] = putRow;
                                else updatedSchedules.push(putRow);
                                continue;
                            }
                            const prio = nextPrio++;
                            const schedPost = await ivrConfigMutationRequest(
                                dispatch,
                                postIvrConfigSchedule(
                                    {
                                        flowId: s.flowId ?? flowId,
                                        elementId: s.elementId,
                                        priority: prio,
                                        daysMask: s.daysMask,
                                        fromTime: s.fromTime,
                                        toTime: s.toTime,
                                        activeToId: s.activeToId,
                                        isActive: true,
                                    },
                                    jwt
                                )
                            );
                            if (schedPost === false) {
                                return;
                            }
                            const resRoot = schedPost.data as {
                                data?: Partial<IvrApiSchedule>;
                            };
                            const created = resRoot?.data;
                            updatedSchedules.push({
                                id: created?.id,
                                flowId: created?.flowId ?? s.flowId ?? flowId,
                                elementId: created?.elementId ?? s.elementId,
                                priority: created?.priority ?? prio,
                                daysMask: created?.daysMask ?? s.daysMask,
                                fromTime: created?.fromTime ?? s.fromTime,
                                toTime: created?.toTime ?? s.toTime,
                                activeToId:
                                    created?.activeToId ?? s.activeToId,
                                isActive: created?.isActive ?? true,
                            });
                        }

                        const nextSchedulesSnapshot = [...updatedSchedules];

                        dispatch(
                            setIvrSchemeGraphApiSlice({
                                elements:
                                    (data?.elements ?? []) as IvrApiElement[],
                                routes: (data?.routes ?? []) as IvrApiRoute[],
                                schedules: nextSchedulesSnapshot,
                                schedulesServerSnapshotReplace:
                                    nextSchedulesSnapshot,
                                configurationPatch: {
                                    entryElementId:
                                        cfg?.entryElementId ?? null,
                                },
                            })
                        );

                        if (parentId) {
                            const routesForPatch = (data?.routes ??
                                []) as IvrApiRoute[];
                            const apiElRaw = elements?.find(
                                (e) => Number(e.elementId) === fromEl
                            );
                            const apiEl =
                                apiElRaw != null && parentCfgNode
                                    ? augmentSchedulerApiElementWithClientBlockConfig(
                                          apiElRaw,
                                          parentCfgNode.data
                                              .blockConfig as Record<
                                              string,
                                              unknown
                                          >
                                      )
                                    : apiElRaw;
                            const schedPatch = apiEl
                                ? (() => {
                                      const mergedCfg = elementToBlockConfig(
                                          apiEl,
                                          routesForPatch,
                                          nextSchedulesSnapshot
                                      );
                                      return {
                                          schedulerSlotRows:
                                              mergedCfg.schedulerSlotRows,
                                          schedulerDays:
                                              mergedCfg.schedulerDays,
                                          schedulerActiveToId:
                                              mergedCfg.schedulerActiveToId,
                                          schedulerShowOthers:
                                              mergedCfg.schedulerShowOthers,
                                          schedulerScheduleKind:
                                              mergedCfg.schedulerScheduleKind,
                                      };
                                  })()
                                : schedulerBlockConfigPatchFromSchedules(
                                      fromEl,
                                      nextSchedulesSnapshot,
                                      routesForPatch
                                  );
                            setNodes((nds) =>
                                nds.map((n) =>
                                    n.id === parentId
                                        ? {
                                              ...n,
                                              data: {
                                                  ...n.data,
                                                  blockConfig: {
                                                      ...n.data.blockConfig,
                                                      ...schedPatch,
                                                  },
                                              },
                                          }
                                        : n
                                )
                            );
                        }

                        setEdges((eds) => {
                            const edgeId = `e-${params.source}-${params.target}`;
                            if (eds.some((e) => e.id === edgeId)) return eds;
                            return [
                                ...eds,
                                makeEdge(params.source, params.target),
                            ];
                        });
                    } catch {
                        dispatch(showError());
                    }
                    requestVerticalLayoutRef.current = true;
                    return;
                }

                const routes = (data?.routes ?? []) as IvrApiRoute[];
                const priority = nextIvrRoutePriority(fromEl, routes);
                const { routeType, routeValue } =
                    resolveIvrPostRouteTypeValue(params.source);
                try {
                    const routeRes = await postIvrConfigRoute(
                        {
                            flowId,
                            fromElementId: fromEl,
                            routeType,
                            routeValue,
                            toElementId: toEl,
                            priority,
                            isActive: true,
                        },
                        jwt
                    );

                    const rd = routeRes?.data?.data;
                    const newRoute: IvrApiRoute = {
                        id: rd?.id,
                        flowId: rd?.flowId ?? flowId,
                        fromElementId: rd?.fromElementId ?? fromEl,
                        routeType: rd?.routeType ?? routeType,
                        routeValue: rd?.routeValue ?? routeValue,
                        toElementId: rd?.toElementId ?? toEl,
                        priority: rd?.priority ?? priority,
                        isActive: rd?.isActive ?? true,
                        tries: rd?.tries ?? null,
                    };

                    dispatch(
                        setIvrSchemeGraphApiSlice({
                            elements:
                                (data?.elements ?? []) as IvrApiElement[],
                            routes: [...routes, newRoute],
                            schedules:
                                (data?.schedules ?? []) as IvrApiSchedule[],
                            configurationPatch: {
                                entryElementId:
                                    cfg?.entryElementId ?? null,
                            },
                        })
                    );

                    setEdges((eds) => {
                        const edgeId = `e-${params.source}-${params.target}`;
                        if (eds.some((e) => e.id === edgeId)) return eds;
                        return [
                            ...eds,
                            makeEdge(
                                params.source,
                                params.target,
                                "bottom-source",
                                "top",
                                { ivrRoute: newRoute }
                            ),
                        ];
                    });
                } catch {
                    dispatch(showError());
                }
                requestVerticalLayoutRef.current = true;
            },
            [canMutateIVRScheme, dispatch, edges, jwtToken?.jwtToken]
        );

        const onNodesChange = useCallback((changes: NodeChange<Node<IVRBlockData>>[]) => {
            const safe = changes.filter((c) => c.type !== "remove");
            setNodes((nds) => applyNodeChanges(safe, nds));
        }, []);

        const onEdgesChange = useCallback(
            (changes: EdgeChange[]) => {
                const flowRaw = ivrFullRef.current?.configuration?.flowId;
                const flowId = flowRaw != null ? Number(flowRaw) : NaN;

                const immediate: EdgeChange[] = [];

                for (const c of changes) {
                    if (c.type === "remove" && !hasIVRConfigurationDeletePermission) {
                        continue;
                    }
                    if (c.type !== "remove") {
                        immediate.push(c);
                        continue;
                    }
                    const edge = edgesRef.current.find((e) => e.id === c.id);
                    if (
                        !edge ||
                        !isPlusZoneToConfigRootEdge(
                            edge,
                            nodesRef.current
                        )
                    ) {
                        continue;
                    }
                    if (edge.source === BASE_NODE_ID) {
                        immediate.push(c);
                        continue;
                    }
                    const routesSnap = (ivrFullRef.current?.routes ??
                        []) as IvrApiRoute[];
                    const elementsSnap = (ivrFullRef.current?.elements ??
                        []) as IvrApiElement[];
                    const entrySnap = (
                        ivrFullRef.current?.configuration as
                            | { entryElementId?: number | null }
                            | undefined
                    )?.entryElementId;
                    const idNumResolved = resolveRouteIdForConfigurableEdgeDelete(
                        edge,
                        nodesRef.current,
                        routesSnap,
                        entrySnap ?? null,
                        elementsSnap
                    );
                    if (idNumResolved != null) {
                        const edgeIdToRemove = c.id;
                        void (async () => {
                            try {
                                const jwtInner = jwtTokenRef.current;
                                if (!jwtInner) {
                                    dispatch(showError());
                                    return;
                                }
                                const idNum = idNumResolved;
                                const prevBefore = ivrFullRef.current;
                                const prevRoutesBefore =
                                    (prevBefore?.routes ?? []) as IvrApiRoute[];
                                const removedRoute = prevRoutesBefore.find(
                                    (r) => Number(r.id ?? (r as Record<string, unknown>).Id) === idNum
                                );

                                await deleteIvrConfigRoute(idNum, jwtInner);
                                setEdges((eds) =>
                                    eds.filter((e) => e.id !== edgeIdToRemove)
                                );
                                if (Number.isFinite(flowId) && flowId > 0) {
                                    const prev = ivrFullRef.current;
                                    const prevRoutes = (prev?.routes ?? []) as IvrApiRoute[];
                                    const prevElements = (prev?.elements ??
                                        []) as IvrApiElement[];
                                    const prevSchedulesLive = (prev?.schedules ??
                                        []) as IvrApiSchedule[];
                                    const prevServerSnap = (prev?._ivrSchedulesServerSnapshot ??
                                        []) as IvrApiSchedule[];
                                    const schedulesTruth =
                                        resolveIvrSchedulesServerTruth(prev);

                                    const nextRoutes = prevRoutes.filter(
                                        (r) => r.id !== idNum
                                    );

                                    let nextSchedules = prevSchedulesLive;
                                    let nextElements = prevElements;
                                    const delSchedIds = new Set<number>();
                                    const parentIdsToRefresh = new Set<number>();

                                    if (removedRoute) {
                                        const fromEl =
                                            ivrRouteEndpointElementId(
                                                removedRoute,
                                                "from"
                                            );
                                        const toEl = ivrRouteEndpointElementId(
                                            removedRoute,
                                            "to"
                                        );
                                        let scheduleDeletes: IvrApiSchedule[] =
                                            [];

                                        const dow =
                                            ivrSchedulerWeekdayChainDayOfWeek(
                                                edge.source
                                            );
                                        if (
                                            fromEl != null &&
                                            fromEl > 0 &&
                                            toEl != null &&
                                            Number.isFinite(toEl) &&
                                            dow != null
                                        ) {
                                            const srcN =
                                                nodesRef.current.find(
                                                    (n) => n.id === edge.source
                                                );
                                            const parentCfgNodeId =
                                                srcN?.data?.configParentId;
                                            const pNode =
                                                parentCfgNodeId
                                                    ? nodesRef.current.find(
                                                          (n) =>
                                                              n.id ===
                                                              parentCfgNodeId
                                                      )
                                                    : undefined;
                                            const pCfg = pNode?.data
                                                ?.blockConfig as
                                                | Record<string, unknown>
                                                | undefined;
                                            const isSchedCfg =
                                                (pCfg
                                                    ?.startConfiguration as string) ===
                                                    "Scheduler" ||
                                                String(
                                                    pCfg?.elementType ?? ""
                                                ).toUpperCase() ===
                                                    "SCHEDULER" ||
                                                String(
                                                    pCfg?.elementType ?? ""
                                                ).toUpperCase() === "SCHEDULE";
                                            if (isSchedCfg) {
                                                const rows = [
                                                    ...buildSchedulerSlotRowsFromBlockConfig(
                                                        pCfg ?? {},
                                                        toEl
                                                    ),
                                                ].sort(
                                                    (a, b) =>
                                                        (a.priority ?? 0) -
                                                        (b.priority ?? 0)
                                                );
                                                const dayMask =
                                                    schedulerDayOfWeekToDaysMask(
                                                        dow
                                                    );
                                                const matchedRow =
                                                    rows.find(
                                                        (r) =>
                                                            (Number(r.daysMask) ||
                                                                0) === dayMask
                                                    ) ?? null;
                                                scheduleDeletes =
                                                    matchedRow != null
                                                        ? schedulesToDeleteForSchedulerJumpEdge(
                                                              {
                                                                  schedules:
                                                                      schedulesTruth,
                                                                  schedulerElementId:
                                                                      fromEl,
                                                                  mode: {
                                                                      kind: "slotRows",
                                                                      rows: [
                                                                          matchedRow,
                                                                      ],
                                                                  },
                                                              }
                                                          )
                                                        : schedulesTruth.filter(
                                                              (s) => {
                                                                  const id =
                                                                      ivrRawScheduleRowId(
                                                                          s
                                                                      );
                                                                  if (
                                                                      !(
                                                                          Number.isFinite(
                                                                              id
                                                                          ) &&
                                                                          id > 0
                                                                      )
                                                                  ) {
                                                                      return false;
                                                                  }
                                                                  if (
                                                                      ivrRawScheduleElementId(
                                                                          s
                                                                      ) !==
                                                                      fromEl
                                                                  ) {
                                                                      return false;
                                                                  }
                                                                  if (
                                                                      ivrRawScheduleActiveToId(
                                                                          s
                                                                      ) !==
                                                                      toEl
                                                                  ) {
                                                                      return false;
                                                                  }
                                                                  const sr =
                                                                      s as Record<
                                                                          string,
                                                                          unknown
                                                                      >;
                                                                  const mask =
                                                                      Number(
                                                                          sr.daysMask ??
                                                                              sr.DaysMask
                                                                      );
                                                                  return (
                                                                      mask ===
                                                                      dayMask
                                                                  );
                                                              }
                                                          );
                                            }
                                        }

                                        if (scheduleDeletes.length === 0) {
                                            const fromApiEl =
                                                prevElements.find(
                                                    (e) =>
                                                        Number(e.elementId) ===
                                                        fromEl
                                                );
                                            const fromType = String(
                                                fromApiEl?.elementType ?? ""
                                            ).toUpperCase();
                                            if (fromType === "SCHEDULE") {
                                                scheduleDeletes =
                                                    schedulesTruth.filter(
                                                        (s) =>
                                                            ivrRawScheduleActiveToId(
                                                                s
                                                            ) === fromEl &&
                                                            ivrRawScheduleRowId(
                                                                s
                                                            ) > 0
                                                    );
                                            }
                                        }

                                        if (
                                            scheduleDeletes.length === 0 &&
                                            fromEl != null &&
                                            fromEl > 0 &&
                                            toEl != null &&
                                            Number.isFinite(toEl)
                                        ) {
                                            const jumpPlan =
                                                resolveSchedulerJumpEdgeDeletionPlan(
                                                    edge,
                                                    nodesRef.current,
                                                    prevBefore as
                                                        | Record<
                                                              string,
                                                              unknown
                                                          >
                                                        | undefined
                                                );

                                            if (
                                                jumpPlan &&
                                                jumpPlan.mode.kind !== "none" &&
                                                jumpPlan.mode.kind !==
                                                    "allWithActiveTarget"
                                            ) {
                                                scheduleDeletes =
                                                    schedulesToDeleteForSchedulerJumpEdge(
                                                        {
                                                            schedules:
                                                                schedulesTruth,
                                                            schedulerElementId:
                                                                jumpPlan.fromEl,
                                                            mode: jumpPlan.mode,
                                                        }
                                                    );
                                            }
                                        }

                                        for (const s of scheduleDeletes) {
                                            const sid =
                                                ivrRawScheduleRowId(s);
                                            if (sid <= 0) continue;
                                            await deleteIvrConfigSchedule(
                                                sid,
                                                jwtInner
                                            );
                                            delSchedIds.add(sid);
                                        }
                                        if (delSchedIds.size > 0) {
                                            nextSchedules =
                                                prevSchedulesLive.filter(
                                                    (s) =>
                                                        !delSchedIds.has(
                                                            ivrRawScheduleRowId(
                                                                s
                                                            )
                                                        )
                                                );
                                            const byParent = new Map<
                                                number,
                                                IvrApiSchedule[]
                                            >();
                                            for (const s of scheduleDeletes) {
                                                const pid =
                                                    ivrRawScheduleElementId(
                                                        s
                                                    );
                                                if (
                                                    !Number.isFinite(pid) ||
                                                    pid <= 0
                                                ) {
                                                    continue;
                                                }
                                                const arr =
                                                    byParent.get(pid) ?? [];
                                                arr.push(s);
                                                byParent.set(pid, arr);
                                            }
                                            nextElements = [...prevElements];
                                            for (const [
                                                parentId,
                                                removedSched,
                                            ] of Array.from(byParent.entries())) {
                                                parentIdsToRefresh.add(
                                                    parentId
                                                );
                                                const pIdx =
                                                    nextElements.findIndex(
                                                        (e) =>
                                                            Number(
                                                                e.elementId
                                                            ) === parentId
                                                    );
                                                if (pIdx < 0) continue;
                                                const parentEl =
                                                    nextElements[pIdx]!;
                                                const pt = String(
                                                    parentEl.elementType ?? ""
                                                ).toUpperCase();
                                                if (
                                                    pt !== "SCHEDULER" &&
                                                    pt !== "SCHEDULE"
                                                ) {
                                                    continue;
                                                }
                                                const mergedDraft =
                                                    mergeRemovedSchedulerApiSchedulesIntoDraftJson(
                                                        parentEl,
                                                        removedSched
                                                    );
                                                if (mergedDraft != null) {
                                                    nextElements[pIdx] = {
                                                        ...parentEl,
                                                        draftData: mergedDraft,
                                                    };
                                                }
                                            }
                                        }
                                    }

                                    const nextServerSnap =
                                        delSchedIds.size > 0
                                            ? prevServerSnap.filter(
                                                  (s) =>
                                                      !delSchedIds.has(
                                                          ivrRawScheduleRowId(s)
                                                      )
                                              )
                                            : prevServerSnap;

                                    const schedulesForConfig =
                                        resolveIvrSchedulesServerTruth({
                                            schedules: nextSchedules,
                                            _ivrSchedulesServerSnapshot:
                                                nextServerSnap,
                                        });

                                    dispatch(
                                        setIvrSchemeGraphApiSlice({
                                            elements: nextElements,
                                            routes: nextRoutes,
                                            schedules: nextSchedules,
                                            ...(delSchedIds.size > 0
                                                ? {
                                                      schedulesServerSnapshotReplace:
                                                          nextServerSnap,
                                                  }
                                                : {}),
                                            configurationPatch: {
                                                entryElementId:
                                                    (
                                                        prev?.configuration as {
                                                            entryElementId?: number | null;
                                                        } | undefined
                                                    )?.entryElementId ?? null,
                                            },
                                        })
                                    );

                                    if (
                                        delSchedIds.size > 0 &&
                                        parentIdsToRefresh.size > 0
                                    ) {
                                        for (const parentId of Array.from(
                                            parentIdsToRefresh
                                        )) {
                                            const pe =
                                                nextElements.find(
                                                    (e) =>
                                                        Number(e.elementId) ===
                                                        parentId
                                                ) ?? null;
                                            if (!pe) continue;
                                            const putOk =
                                                await persistSchedulerDraftAfterScheduleRemoval(
                                                    dispatch,
                                                    jwtInner,
                                                    flowId,
                                                    pe,
                                                    nextRoutes,
                                                    schedulesForConfig
                                                );
                                            if (!putOk) {
                                                dispatch(showError());
                                            }
                                        }
                                    }

                                    if (parentIdsToRefresh.size > 0) {
                                        const blockPatchByParentId = new Map<
                                            number,
                                            Record<string, unknown>
                                        >();
                                        for (const parentId of Array.from(
                                            parentIdsToRefresh
                                        )) {
                                            const parentEl =
                                                nextElements.find(
                                                    (e) =>
                                                        Number(e.elementId) ===
                                                        parentId
                                                ) ?? null;
                                            if (!parentEl) continue;
                                            blockPatchByParentId.set(
                                                parentId,
                                                elementToBlockConfig(
                                                    parentEl,
                                                    nextRoutes,
                                                    schedulesForConfig
                                                ) as Record<string, unknown>
                                            );
                                        }
                                        setNodes((nds) =>
                                            nds.map((n) => {
                                                const elRaw =
                                                    n.data?.blockConfig
                                                        ?.elementId;
                                                const elNum =
                                                    typeof elRaw === "number"
                                                        ? elRaw
                                                        : Number(elRaw);
                                                if (
                                                    !n.id.startsWith(
                                                        START_CONFIG_PREFIX
                                                    ) ||
                                                    !Number.isFinite(elNum)
                                                ) {
                                                    return n;
                                                }
                                                const schedBlockPatch =
                                                    blockPatchByParentId.get(
                                                        elNum
                                                    );
                                                if (!schedBlockPatch) {
                                                    return n;
                                                }
                                                return {
                                                    ...n,
                                                    data: {
                                                        ...n.data,
                                                        blockConfig: {
                                                            ...(n.data
                                                                .blockConfig ??
                                                                {}),
                                                            schedulerSlotRows:
                                                                schedBlockPatch.schedulerSlotRows,
                                                            schedulerDays:
                                                                schedBlockPatch.schedulerDays,
                                                            schedulerActiveToId:
                                                                schedBlockPatch.schedulerActiveToId,
                                                            schedulerShowOthers:
                                                                schedBlockPatch.schedulerShowOthers,
                                                            schedulerScheduleKind:
                                                                schedBlockPatch.schedulerScheduleKind,
                                                            ...(schedBlockPatch.schedulerExtraWindow !==
                                                            undefined
                                                                ? {
                                                                      schedulerExtraWindow:
                                                                          schedBlockPatch.schedulerExtraWindow,
                                                                  }
                                                                : {}),
                                                        },
                                                    },
                                                };
                                            })
                                        );
                                    }
                                }
                            } catch {
                                dispatch(showError());
                            }
                        })();
                    } else {
                        const edgeIdToRemove = c.id;
                        const plan = resolveSchedulerJumpEdgeDeletionPlan(
                            edge,
                            nodesRef.current,
                            ivrFullRef.current as
                                | Record<string, unknown>
                                | undefined
                        );
                        const jwtSched = jwtTokenRef.current;
                        if (
                            plan &&
                            jwtSched &&
                            Number.isFinite(flowId) &&
                            flowId > 0
                        ) {
                            const prevSchedules = resolveIvrSchedulesServerTruth(
                                ivrFullRef.current
                            );
                            let toDelete = schedulesToDeleteForSchedulerJumpEdge({
                                schedules: prevSchedules,
                                schedulerElementId: plan.fromEl,
                                mode: plan.mode,
                            });
                            if (
                                !toDelete.length &&
                                plan.mode.kind === "none"
                            ) {
                                toDelete = schedulesToDeleteForSchedulerJumpEdge(
                                    {
                                        schedules: prevSchedules,
                                        schedulerElementId: plan.fromEl,
                                        mode: {
                                            kind: "allWithActiveTarget",
                                            toElementId: plan.toEl,
                                        },
                                    }
                                );
                            }
                            if (!toDelete.length) {
                                immediate.push(c);
                                continue;
                            }
                            void (async () => {
                                try {
                                    const jwtInnerSched = jwtTokenRef.current;
                                    if (!jwtInnerSched) {
                                        dispatch(showError());
                                        return;
                                    }
                                    for (const s of toDelete) {
                                        const sid = ivrRawScheduleRowId(s);
                                        if (sid <= 0) continue;
                                        await deleteIvrConfigSchedule(
                                            sid,
                                            jwtInnerSched
                                        );
                                    }
                                    const prev = ivrFullRef.current as
                                        | Record<string, unknown>
                                        | undefined;
                                    const prevElements = (prev?.elements ??
                                        []) as IvrApiElement[];
                                    const prevSchedulesLive = (prev?.schedules ??
                                        []) as IvrApiSchedule[];
                                    const prevServerSnap = (prev?._ivrSchedulesServerSnapshot ??
                                        []) as IvrApiSchedule[];
                                    const delIds = new Set(
                                        toDelete.map((x) => ivrRawScheduleRowId(x))
                                    );
                                    const nextSchedules = prevSchedulesLive.filter(
                                        (s) => !delIds.has(ivrRawScheduleRowId(s))
                                    );
                                    const nextServerSnap = prevServerSnap.filter(
                                        (s) => !delIds.has(ivrRawScheduleRowId(s))
                                    );
                                    const schedulesForConfig =
                                        resolveIvrSchedulesServerTruth({
                                            schedules: nextSchedules,
                                            _ivrSchedulesServerSnapshot:
                                                nextServerSnap,
                                        });
                                    const idx = prevElements.findIndex(
                                        (e) =>
                                            Number(e.elementId) === plan.fromEl
                                    );
                                    let nextElements = prevElements;
                                    let nextBc: Record<string, unknown> | null =
                                        null;
                                    if (idx >= 0) {
                                        const schedEl = prevElements[idx]!;
                                        const mergedDraft =
                                            mergeRemovedSchedulerApiSchedulesIntoDraftJson(
                                                schedEl,
                                                toDelete
                                            );
                                        const nextSchedEl: IvrApiElement =
                                            mergedDraft != null
                                                ? {
                                                      ...schedEl,
                                                      draftData: mergedDraft,
                                                  }
                                                : { ...schedEl };
                                        nextElements = [...prevElements];
                                        nextElements[idx] = nextSchedEl;
                                        nextBc = elementToBlockConfig(
                                            nextSchedEl,
                                            (prev?.routes ??
                                                []) as IvrApiRoute[],
                                            schedulesForConfig
                                        ) as Record<string, unknown>;
                                    }
                                    dispatch(
                                        setIvrSchemeGraphApiSlice({
                                            elements: nextElements,
                                            routes: (prev?.routes ??
                                                []) as IvrApiRoute[],
                                            schedules: nextSchedules,
                                            schedulesServerSnapshotReplace:
                                                nextServerSnap,
                                            configurationPatch: {
                                                entryElementId:
                                                    (
                                                        prev?.configuration as {
                                                            entryElementId?: number | null;
                                                        } | undefined
                                                    )?.entryElementId ?? null,
                                            },
                                        })
                                    );
                                    if (idx >= 0 && jwtInnerSched) {
                                        const putOk =
                                            await persistSchedulerDraftAfterScheduleRemoval(
                                                dispatch,
                                                jwtInnerSched,
                                                flowId,
                                                nextElements[idx]!,
                                                (prev?.routes ??
                                                    []) as IvrApiRoute[],
                                                schedulesForConfig
                                            );
                                        if (!putOk) {
                                            dispatch(showError());
                                        }
                                    }
                                    const schedBlockPatch = nextBc;
                                    if (schedBlockPatch) {
                                        setNodes((nds) =>
                                            nds.map((n) =>
                                                n.id === plan.configNodeId
                                                    ? {
                                                          ...n,
                                                          data: {
                                                              ...n.data,
                                                              blockConfig: {
                                                                  ...(n.data
                                                                      .blockConfig ?? {}),
                                                                  schedulerSlotRows:
                                                                      schedBlockPatch.schedulerSlotRows,
                                                                  schedulerDays:
                                                                      schedBlockPatch.schedulerDays,
                                                                  schedulerActiveToId:
                                                                      schedBlockPatch.schedulerActiveToId,
                                                                  schedulerShowOthers:
                                                                      schedBlockPatch.schedulerShowOthers,
                                                                  schedulerScheduleKind:
                                                                      schedBlockPatch.schedulerScheduleKind,
                                                                  ...(schedBlockPatch.schedulerExtraWindow !==
                                                                  undefined
                                                                      ? {
                                                                            schedulerExtraWindow:
                                                                                schedBlockPatch.schedulerExtraWindow,
                                                                        }
                                                                      : {}),
                                                              },
                                                          },
                                                      }
                                                    : n
                                            )
                                        );
                                    }
                                    setEdges((eds) =>
                                        eds.filter((e) => e.id !== edgeIdToRemove)
                                    );
                                } catch {
                                    dispatch(showError());
                                }
                            })();
                        } else {
                            immediate.push(c);
                        }
                    }
                }

                setEdges((eds) => applyEdgeChanges(immediate, eds));
            },
            [dispatch, hasIVRConfigurationDeletePermission, jwtToken?.jwtToken, setNodes]
        );


        const isValidConnection = useCallback(
            (c: Connection | Edge) => {
                if (!canMutateIVRScheme) {
                    return false;
                }
                const src = c.source;
                const tgt = c.target;
                if (!src || !tgt) return false;
                if (src === tgt) return false;
                if (edges.some((e) => e.source === src && e.target === tgt)) return false;
                if (!isPlusZoneToConfigRootEdge({ source: src, target: tgt }, nodes)) {
                    return false;
                }
                const data = ivrFullRef.current;
                const entry = (
                    data?.configuration as
                        | { entryElementId?: number | null }
                        | undefined
                )?.entryElementId;
                const elements = data?.elements as IvrApiElement[] | undefined;
                const toEl = resolveRouteTargetElementId(tgt, nodes);
                if (toEl == null || toEl < 0) return false;

                if (src === BASE_NODE_ID) {
                    const dup = edges.some(
                        (e) =>
                            e.source === BASE_NODE_ID &&
                            e.target === tgt &&
                            e.target.startsWith(START_CONFIG_PREFIX)
                    );
                    return !dup && Number.isFinite(toEl) && toEl >= 0;
                }

                const fromEl = resolveRouteSourceElementId(
                    src,
                    nodes,
                    entry,
                    elements
                );
                return fromEl != null && fromEl > 0;
            },
            [canMutateIVRScheme, edges, nodes]
        );

        const onLayout = useCallback(
            (direction: "TB" | "LR") => {
                const layouted = getLayoutedElements(nodes, edges, direction);
                setNodes([...layouted.nodes]);
                setEdges([...layouted.edges]);
            },
            [nodes, edges]
        );

        const AlignButtons = useCallback(
            function AlignButtons() {
                const { fitView } = useReactFlow();
                const handleLayout = (direction: "TB" | "LR") => {
                    onLayout(direction);
                    setTimeout(() => {
                        fitView({ padding: 0.2, duration: 500 });
                    }, 100);
                };
                return (
                    <Panel position="top-right">
                        {/*<div className="ivr-layout-buttons flex gap-2">*/}
                        {/*    <Button*/}
                        {/*        label="Вертикально"*/}
                        {/*        icon="pi pi-arrows-v"*/}
                        {/*        className="p-button-sm p-button-outlined"*/}
                        {/*        onClick={() => handleLayout("TB")}*/}
                        {/*        title="Вертикальне вирівнювання"*/}
                        {/*    />*/}
                        {/*    <Button*/}
                        {/*        label="Горизонтально"*/}
                        {/*        icon="pi pi-arrows-h"*/}
                        {/*        className="p-button-sm p-button-outlined"*/}
                        {/*        onClick={() => handleLayout("LR")}*/}
                        {/*        title="Горизонтальне вирівнювання"*/}
                        {/*    />*/}
                        {/*</div>*/}
                    </Panel>
                );
            },
            [onLayout]
        );

        return (
            <div
                className="ivr-scheme-builder"
                data-color-scheme={ivrDiagramColorScheme}
                style={{ height: "80vh" }}
            >
                <ConfirmDialog />
                <ReactFlow
                    className={ivrDiagramColorScheme === "dark" ? "darkIVR" : undefined}
                    colorMode="light"
                    nodes={nodes}
                    edges={edges}
                    onConnect={onConnect}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={handleNodeClick}
                    onPaneClick={() => onSelectedNodeChange?.(null)}
                    isValidConnection={isValidConnection}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    deleteKeyCode={hasIVRConfigurationDeletePermission ? ["Delete", "Backspace"] : []}
                    nodesDraggable={canMutateIVRScheme}
                    nodesConnectable={canMutateIVRScheme}
                    nodesFocusable={false}
                    edgesFocusable
                    elevateNodesOnSelect={true}
                    elevateEdgesOnSelect={true}
                    fitView
                    fitViewOptions={{ padding: 0.2 }}
                    onInit={(instance) => {
                        rfInstanceRef.current = instance;
                    }}
                >
                    <Background
                        gap={16}
                        size={1}
                        color={ivrDiagramColorScheme === "light" ? "#b0bdd4" : "#444"}
                    />
                    <Controls />
                    <AlignButtons />
                </ReactFlow>
            </div>
        );
    }
);

export default IVRSchemeBuilder;
